import React, { createContext, useState, useContext, useEffect } from 'react';

export const SUPPORTED_LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'hi', label: 'हिन्दी' },
    { code: 'zh', label: '中文' },
    { code: 'ta', label: 'தமிழ்' },
    { code: 'ml', label: 'മലയാളം' },
    { code: 'te', label: 'తెలుగు' }
];

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

const translations = {
    en: {
        activityLog: "Activity Log",
        activity_log: "Activity Log",
        viewActivityLog: "View Activity Log",
        present: "Present",
        noBranch: "No branch",
        price: "Price",
        currency: "Currency",
        absent: "Absent",
        totalHours: "Total Hours",
        avgHours: "Avg Hours",
        clickToViewList: "Click to view list",
        // General
        loading: "Loading...",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        edit: "Edit",
        close: "Close",
        add: "Add",
        submit: "Submit",
        confirmBulkDelete: "Are you sure you want to delete these ingredients?",
        deleteSelected: "Delete Selected",
        ingredientsDeletedSuccess: "Ingredients deleted successfully",
        errorDeletingIngredients: "Error deleting ingredients",

        // Analytics - Discounts & Cancellations
        discountsPromos: "Discounts & Promos",
        cancellationsLoss: "Cancellations & Loss",
        totalDiscountAmount: "Total Discount Given",
        ordersWithDiscount: "Orders with Discount",
        avgDiscountPercentage: "Avg Discount Percentage",
        discountChartTitle: "Top Discounted Items by Loss",
        discountItemsTable: "Discount Details",
        itemName: "Item",
        discountPercentage: "Discount %",
        totalDiscount: "Total Discount",
        totalRevenueLost: "Total Revenue Lost",
        cancelledOrdersCount: "Cancelled Orders",
        cancelledItemsCount: "Cancelled Items Count",
        topCancelledItems: "Most Cancelled Menu Items",
        estimatedLoss: "Loss Amount",
        orderStatusOverview: "Order Status Overview",
        orderStatus: "Status",

        // Dashboard / Home
        home: "Home",
        features: "Features",
        pricing: "Pricing",
        contact: "Contact",
        login: "Log In",
        admin: "Admin",
        kitchen: "Kitchen",
        customer: "Customer",
        analytics: "Analytics",
        ingredients: "Ingredients",
        getStarted: "Get Started",
        heroTitle: "Scan Order Serve Enjoy",
        heroSubtitle: "Built for Restaurants, Dhabas, Cafes, Hotels and QSR Chains",
        heroTagline: "No Waiter. No Waiting. Just Scan & Order",
        branchMgmtDesc: "Comprehensive multi-location management system. Control menus, inventory, staff, and operations from one dashboard.",

        // Admin
        adminPanel: "Admin Panel",
        manageMenu: "Manage Menu & Tables",
        menu: "Menu",
        tables: "Tables",
        orders: "Orders",
        menuItems: "Menu Items",
        addItem: "Add Item",
        allCategories: "All Categories",
        tableGroups: "Table Groups",
        addGroup: "Add Group",
        addTable: "Add Table",
        filterByGroup: "Filter by Group",
        allTables: "All Tables",
        nonAc: "Non AC",
        showOrders: "Show Orders",

        // Customer
        menuTitle: "Menu",
        table: "Table",
        placeOrder: "Place Order",
        addToCart: "Add",
        orderPlaced: "Order Placed Successfully!",
        selfServiceNotice: "Self-Service Item: Please collect it at the counter.",
        orderId: "Order ID",
        yourOrders: "Your Orders",
        status: "Status",
        pending: "Pending",
        noOrders: "No orders found",
        todaySpecialItems: "Today Special items",
        topRatedItems: "Top Rated items",
        noSpecialItems: "No special items today. Check back later!",
        noTopRatedItems: "No top-rated items available right now.",
        veg: "Veg",
        nonVeg: "Non-Veg",
        vegOnly: "Veg",
        nonVegOnly: "Non-Veg",
        foodType: "Food Type",
        itemDetails: "Details & Ingredients",
        ingredientsPlaceholder: "Ingredients (e.g., Tomato, Cheese, Basil, Olive Oil)",

        // Nutritional
        calories: "Calories",
        protein: "Protein",
        carbs: "Carbs",
        fats: "Fats",
        aiRecommendation: "AI Recommendations",
        aiDisclaimer: "AI Generated. Verify Info.",

        // Availability
        outOfStock: "Out of Stock",
        selectRole: "Select Role",
        phoneNumber: "Phone Number",
        newPassword: "New Password",
        passwordLabel: "Password (leave blank to keep current)",
        leaveBlankToKeepCurrent: "Leave blank to keep current",
        addBranch: "ADD BRANCH",
        addRole: "ADD ROLE",
        leaveType: "Leave Type",
        fullDay: "Full Day",
        halfDay: "Half Day",
        fullDayInfo: "Full day leave counts as 1.0 day",
        halfDayInfo: "Half day leave counts as 0.5 day",
        saveUser: "Save Management",
        supportChat: "Support Chat",
        chatWithUs: "Chat with us",
        noOrdersForTable: "No orders found for this table.",
        groupNamePlaceholder: "Group Name (e.g., AC, Garden)",
        tableNumber: "Table Number",
        nameOptional: "Name (Optional)",
        totalAmount: "Total Amount",

        // Auth
        signInTitle: "Sign in to your account",
        createAccountTitle: "Create your account",
        or: "Or",
        email: "Email address",
        password: "Password",
        confirmPassword: "Confirm Password",
        fullName: "Full Name",
        signIn: "Sign in",
        signUp: "Sign up",
        enterFullName: "Enter Full Name",
        enterPhone: "Enter Phone Number",
        enterEmail: "Enter Email",
        enterPassword: "Enter Password",
        alreadyHaveAccount: "Already have an account?",
        needAccount: "Need an account?",
        createAccountLink: "create a new account",
        signInLink: "sign in to your existing account",
        passwordsDoNotMatch: "Passwords do not match",
        passwordLength: "Password must be at least 6 characters",

        // Dashboard Extra
        faqs: "FAQs",
        startFreeTrial: "Start Free Trial",
        watchDemo: "Watch Demo",
        fasterService: "Faster Service",
        higherRevenue: "Higher Revenue",
        completeEcosystem: "The Complete Ecosystem",
        stunningSuite: "A stunning suite of tools designed for modern dining.",
        investmentInExcellence: "Investment in Excellence",
        simpleTransparentPricing: "Simple, transparent pricing for unlimited growth.",
        completeSuite: "Complete Suite",
        unlimitedTables: "Unlimited Tables & Staff",
        advancedKDS: "Advanced KDS & Analytics",
        instantMenuUpdates: "Instant Menu Updates",
        prioritySupport: "24/7 Priority Support",
        start14DayTrial: "Start 20-Day Free Trial",
        stayAhead: "Stay Ahead",
        getLatestFeatures: "Get the latest features, tips, and industry trends delivered to your inbox.",
        joinRevolution: "Join the dining revolution and scale your business today.",
        product: "Product",
        company: "Company",
        privacyPolicy: "Privacy Policy",
        termsOfService: "Terms of Service",
        cookiePolicy: "Cookie Policy",

        // Admin Extra
        adminDashboard: "Admin Dashboard",
        logout: "Logout",
        groups: "Groups",
        staff: "Staff",
        users: "Management",
        roles: "Roles",
        branches: "Branches",
        branchComparison: "Branch Comparison",
        menuEmpty: "Your menu is empty",
        menuEmptyDesc: "Start by adding delicious items to your menu. You can organize them into categories and even use AI to generate nutritional info!",
        noTables: "No tables found",
        noTablesDesc: "Set up your restaurant layout by adding tables. You can assign them to groups (like AC, Garden) later.",
        noGroups: "No table groups yet",
        noGroupsDesc: "Groups help you organize your tables (e.g., 'AC Section', 'Outdoor', 'First Floor').",
        saveChanges: "Save Changes",
        role: "Role",
        pin: "PIN",
        salary: "Salary",
        salaryInr: "Salary (₹)",
        pinPlaceholder: "PIN (6 digits)",
        enterRoleName: "Enter Role Name",
        enterRole: "Enter Role",
        enterName: "Enter Name",
        enterBranchName: "Enter Branch Name",
        enterAddress: "Enter Address",
        managerName: "Manager Name",
        managerNamePlaceholder: "Enter Manager Name",
        phoneNumberPlaceholder: "Enter Phone Number",
        enterPhoneNumber: "Enter Phone Number",
        create: "Create",
        update: "Update",
        customerPage: "Customer Page",
        selectAll: "Select All",
        passwordPlaceholder: "Enter Password",
        otherRole: "Other Role",
        actions: "Actions",
        addUser: "Add Management",
        editUser: "Edit Management",
        deleteUser: "Delete Management",
        loadingUsers: "Loading management...",
        noUsersFound: "No Management Found",
        roleName: "Role Name",
        roleNameExample: "e.g., Senior Chef, Night Manager",
        permissions: "Permissions",
        loadingRoles: "Loading roles...",
        noRolesFound: "No roles found",
        editRole: "Edit Role",
        deleteRole: "Delete Role",
        createRole: "Create Role",
        updateRole: "Update Role",
        markAsSpecial: "Mark as Special Item",
        specialItemDesc: "When marked as special, this item appears in the premium top section of the customer page.",
        specialDuration: "Special item duration",
        expireIn1Day: "Expire in 1 Day",
        expireIn2Days: "Expire in 2 Days",
        expireIn3Days: "Expire in 3 Days",
        expireIn7Days: "Expire in 7 Days",
        expireIn30Days: "Expire in 30 Days",
        branchManagement: "Branch Management",
        editBranch: "Edit Branch",
        address: "Address",
        ingredientUpdated: "Ingredient updated",
        ingredientAdded: "Ingredient added",
        failedLoadIngredients: "Failed to load ingredients",
        errorSavingIngredient: "Error saving ingredient",
        errorDeletingIngredient: "Error deleting ingredient",
        paymentMethods: "Payment Methods",
        paymentReconciliation: "Orders and Payments Comparison",
        discrepancy: "Discrepancy",
        busiestTimeDesc: "{hour}:00 is your busiest time with {orders} orders.",
        topPerformerDesc: "{name} is your best-selling item with {orders} orders.",
        preferredPaymentDesc: "Customers prefer {method} payments ({orders} orders).",
        fewestOrdersDesc: "{category} has the fewest orders. Consider promotions to boost sales.",
        loyaltyProgramDesc: "Consider loyalty programs.",
        phonePlaceholder: "+1-555-0100",
        itemPricePlaceholder: "Price (₹)",
        ingredientNamePlaceholder: "Ingredient Name (e.g., Tomato)",
        quantityPlaceholder: "Quantity",
        thresholdPlaceholder: "Low Stock Alert at...",
        refreshData: "Refresh Data",
        export: "Export",
        viewList: "View List",
        exportExcel: "Export Excel Report",
        exportPDF: "Export PDF Report",
        qrTable: "Table QR",
        print: "Print",
        ordersForTable: "Orders for Table",
        order: "Order",
        total: "Total",
        payment: "Payment",
        noItemsSold: "No items sold in this period",
        noTableData: "No table data available",
        noDataAvailable: "No data available for this period",
        tryAgain: "Try Again",
        // Common Categories
        "Dessert": "Dessert",
        "Desserts": "Desserts",
        "Main Course": "Main Course",
        "Starters": "Starters",
        "Beverages": "Beverages",
        "Drinks": "Drinks",
        "Appetizers": "Appetizers",
        // Common Menu Items
        "Grilled Chicken": "Grilled Chicken",
        "Burger": "Burger",
        "Pizza": "Pizza",
        "Pasta": "Pasta",
        "Noodles": "Noodles",
        "Salad": "Salad",
        "Ice Cream": "Ice Cream",
        "Coffee": "Coffee",
        "Tea": "Tea",
        "Juice": "Juice",
        "Soda": "Soda",
        "Beer": "Beer",
        "Wine": "Wine",
        "Spirits": "Spirits",
        "Cocktails": "Cocktails",
        "Mocktails": "Mocktails",
        "Milkshake": "Milkshake",
        "Smoothie": "Smoothie",
        "Snacks": "Snacks",
        "Sides": "Sides",
        "Extras": "Extras",
        "Vegetarian": "Vegetarian",
        "Non-Vegetarian": "Non-Vegetarian",
        "Vegan": "Vegan",
        "Gluten-Free": "Gluten-Free",
        "Spicy": "Spicy",
        "Mild": "Mild",
        "Hot": "Hot",
        "Cold": "Cold",
        "Sweet": "Sweet",
        "Sour": "Sour",
        "Salty": "Salty",
        "Bitter": "Bitter",
        "Umami": "Umami",
        "Crunchy": "Crunchy",
        "Crispy": "Crispy",
        "Juicy": "Juicy",
        "Tender": "Tender",
        "Fresh": "Fresh",
        "Healthy": "Healthy",
        "Homemade": "Homemade",
        "Traditional": "Traditional",
        "Seasonal": "Seasonal",
        "Daily Special": "Daily Special",
        "Chef's Choice": "Chef's Choice",
        "House Special": "House Special",
        "Signature Dish": "Signature Dish",
        "Bestseller": "Bestseller",
        "Popular": "Popular",
        "Recommended": "Recommended",
        "New Arrival": "New Arrival",
        "Limited Time": "Limited Time",
        "Discounted": "Discounted",
        "Combo": "Combo",
        "Platter": "Platter",
        "Bigger Portion": "Bigger Portion",
        "Small Portion": "Small Portion",
        // Specific user items
        "Biryani": "Biryani",
        "Chicken Biryani": "Chicken Biryani",
        "Mutton Biryani": "Mutton Biryani",
        "Akaveedu": "Akaveedu",
        "Kanigiri": "Kanigiri",
        "Podili": "Podili",
        "Ongole": "Ongole",
        "Nellore": "Nellore",
        "Vijayawada": "Vijayawada",
        "Guntur": "Guntur",
        "Hyderabad": "Hyderabad",
        // Common Ingredients
        "Tomato": "Tomato",
        "Onion": "Onion",
        "Chicken": "Chicken",
        "Mutton": "Mutton",
        "Oil": "Oil",
        "Sugar": "Sugar",
        "Rice": "Rice",
        "Egg": "Egg",
        "Milk": "Milk",
        "Paneer": "Paneer",
        "Fish": "Fish",
        "Prawns": "Prawns",
        "Garlic": "Garlic",
        "Ginger": "Ginger",
        "Chilli": "Chilli",
        "Masala": "Masala",
        "Pulav": "Pulav",
        "Roti": "Roti",
        "Chapathi": "Chapathi",
        "Curry": "Curry",
        "Fry": "Fry",
        "Snack": "Snack",
        "Cool Drink": "Cool Drink",
        "Mineral Water": "Mineral Water",
        "Table 1": "Table 1",
        "Table 2": "Table 2",
        "Table 3": "Table 3",
        "Table 4": "Table 4",
        "Table 5": "Table 5",
        "Table 6": "Table 6",
        "Table 7": "Table 7",
        "Table 8": "Table 8",
        "Table 9": "Table 9",
        "Table 10": "Table 10",
        "Table 11": "Table 11",
        "Table 12": "Table 12",
        "Table 13": "Table 13",
        "Table 14": "Table 14",
        "Table 15": "Table 15",
        "Table 16": "Table 16",
        "Table 17": "Table 17",
        "Table 18": "Table 18",
        "Table 19": "Table 19",
        "Table 20": "Table 20",
        "General Area": "General Area",
        "Balcony": "Balcony",
        "AC Room": "AC Room",
        "Non AC Room": "Non AC Room",
        "Reception": "Reception",
        "Parking": "Parking",
        "Waiting Area": "Waiting Area",
        "Regular": "Regular",
        "Junior": "Junior",
        "Senior": "Senior",
        "Family Size": "Family Size",
        "Party Size": "Party Size",
        "Extra Large": "Extra Large",
        "Extra Small": "Extra Small",
        "Quarter": "Quarter",
        "Single": "Single",
        "Quadruple": "Quadruple",
        "N/A": "N/A",

        noStaffFound: "No staff found",
        dailyRate: "Daily Rate",
        "Semi-Automatic": "Semi-Automatic",
        "Real-time": "Real-time",
        liveDashboard: "Live Dashboard",
        activeOrders: "Active Orders",
        completedToday: "Completed Today",
        ordersInQueue: "Orders in Queue",
        preparingOrders: "Preparing Orders",
        readyToServe: "Ready to Serve",
        deliveredOrders: "Delivered Orders",
        "Sign Up": "Sign Up",
        "Sign In": "Sign In",
        "In Progress": "In Progress",
        "To Do": "To Do",
        "Up": "Up",
        "Down": "Down",
        "Left": "Left",
        "Right": "Right",
        "Top": "Top",
        "Bottom": "Bottom",
        "Center": "Center",
        "Middle": "Middle",
        "Side": "Side",
        "Inside": "Inside",
        "Outside": "Outside",
        "Above": "Above",
        "Below": "Below",
        "Behind": "Behind",
        "Front": "Front",
        "Back": "Back",
        "First": "First",
        "Last": "Last",
        "Before": "Before",
        "After": "After",
        "Between": "Between",
        "Among": "Among",
        "Over": "Over",
        "Under": "Under",
        "Around": "Around",
        "Across": "Across",
        "Through": "Through",
        "Along": "Along",
        "Near": "Near",
        "Far": "Far",
        "Close": "Close",
        "Wide": "Wide",
        "Narrow": "Narrow",
        "Deep": "Deep",
        "Shallow": "Shallow",
        "Thick": "Thick",
        "Thin": "Thin",
        "Heavy": "Heavy",
        "Hard": "Hard",
        "Soft": "Soft",
        "Strong": "Strong",
        "Weak": "Weak",
        "Large": "Large",
        "Small": "Small",
        "Big": "Big",
        "Little": "Little",
        "Huge": "Huge",
        "Tiny": "Tiny",
        "Massive": "Massive",
        "Gigantic": "Gigantic",
        "Microscopic": "Microscopic",
        "Finite": "Finite",
        "Absolute": "Absolute",
        "Relative": "Relative",
        "Direct": "Direct",
        "Indirect": "Indirect",
        "Main": "Main",
        "Sub": "Sub",
        "Slave": "Slave",
        "Parent": "Parent",
        "Child": "Child",
        "Sibling": "Sibling",
        "Root": "Root",
        "Branch": "Branch",
        "Leaf": "Leaf",
        "Link": "Link",
        "Transaction": "Transaction",
        "Event": "Event",
        "Task": "Task",
        "Process": "Process",
        "Job": "Job",
        "Activity": "Activity",
        "Action": "Action",
        "Operation": "Operation",
        "Command": "Command",
        "Script": "Script",
        "Code": "Code",
        "Data": "Data",
        "Information": "Information",
        "Knowledge": "Knowledge",
        "Idea": "Idea",
        "Concept": "Concept",
        "Design": "Design",
        "Style": "Style",
        "Type": "Type",
        "Category": "Category",
        "Group": "Group",
        "Set": "Set",
        "List": "List",
        "Array": "Array",
        "Map": "Map",
        "Dictionary": "Dictionary",
        "Object": "Object",
        "Property": "Property",
        "Name": "Name",
        "Id": "Id",
        "Uuid": "Uuid",
        "Tag": "Tag",
        "Label": "Label",
        "Metadata": "Metadata",
        "Attribute": "Attribute",
        "Field": "Field",
        "Column": "Column",
        "Row": "Row",
        "Cell": "Cell",
        "Table": "Table",
        "Database": "Database",
        "Storage": "Storage",
        "Bucket": "Bucket",
        "Folder": "Folder",
        "File": "File",
        "Path": "Path",
        "Url": "Url",
        "Uri": "Uri",
        "Email": "Email",
        "Phone": "Phone",
        "Address": "Address",
        "Location": "Location",
        "Coordinate": "Coordinate",
        "Latitude": "Latitude",
        "Longitude": "Longitude",
        "Altitude": "Altitude",
        "Distance": "Distance",
        "Area": "Area",
        "Volume": "Volume",
        "Mass": "Mass",
        "Weight": "Weight",
        "Temperature": "Temperature",
        "Pressure": "Pressure",
        "Duration": "Duration",
        "Interval": "Interval",
        "Frequency": "Frequency",
        "Rate": "Rate",
        "Ratio": "Ratio",
        "Percentage": "Percentage",
        "Count": "Count",
        "Total": "Total",
        "Sum": "Sum",
        "Average": "Average",
        "Mean": "Mean",
        "Median": "Median",
        "Mode": "Mode",
        "Min": "Min",
        "Max": "Max",
        "Range": "Range",
        "Variance": "Variance",
        "Deviation": "Deviation",
        "Correlation": "Correlation",
        "Regression": "Regression",
        "Distribution": "Distribution",
        "Probability": "Probability",
        "Statistic": "Statistic",
        "Metric": "Metric",
        "Log": "Log",
        "State": "State",
        "Status": "Status",
        "Flag": "Flag",
        "Setting": "Setting",
        "Option": "Option",
        "Preference": "Preference",
        "Configuration": "Configuration",
        "Policy": "Policy",
        "Rule": "Rule",
        "Permission": "Permission",
        "Role": "Role",
        "Identity": "Identity",
        "Session": "Session",
        "Acl": "Acl",
        "Auth": "Auth",
        "Cred": "Cred",
        "Key": "Key",
        "Cert": "Cert",
        "Token": "Token",
        "Nonce": "Nonce",
        "Salt": "Salt",
        "Hash": "Hash",
        "Cipher": "Cipher",
        "Iv": "Iv",
        "Algorithm": "Algorithm",
        "Protocol": "Protocol",
        "Format": "Format",
        "Encoding": "Encoding",
        "Scheme": "Scheme",
        "Template": "Template",
        "Asset": "Asset",
        "Resource": "Resource",
        "Component": "Component",
        "Module": "Module",
        "Package": "Package",
        "Library": "Library",
        "Framework": "Framework",
        "Platform": "Platform",
        "Stack": "Stack",
        "Cluster": "Cluster",
        "Node": "Node",
        "Service": "Service",
        "Api": "Api",
        "Web": "Web",
        "Mobile": "Mobile",
        "Desktop": "Desktop",
        "Backend": "Backend",
        "Frontend": "Frontend",
        "Fullstack": "Fullstack",
        "Cloud": "Cloud",
        "Edge": "Edge",
        "Serverless": "Serverless",
        "Container": "Container",
        "Vm": "Vm",
        "Os": "Os",
        "Kernel": "Kernel",
        "Driver": "Driver",
        "Hardware": "Hardware",
        "Firmware": "Firmware",
        "Cpu": "Cpu",
        "Gpu": "Gpu",
        "Ram": "Ram",
        "Disk": "Disk",
        "Network": "Network",
        "Interface": "Interface",
        "Port": "Port",
        "Socket": "Socket",
        "Stream": "Stream",
        "Queue": "Queue",
        "Bus": "Bus",
        "Pipe": "Pipe",
        "Signal": "Signal",
        "Interrupt": "Interrupt",
        "Trap": "Trap",
        "Exception": "Exception",
        "Fault": "Fault",
        "Bug": "Bug",
        "Vulnerability": "Vulnerability",
        "Exploit": "Exploit",
        "Malware": "Malware",
        "Virus": "Virus",
        "Worm": "Worm",
        "Trojan": "Trojan",
        "Ransomware": "Ransomware",
        "Spyware": "Spyware",
        "Adware": "Adware",
        "Phishing": "Phishing",
        "Spam": "Spam",
        "Bot": "Bot",
        "Agent": "Agent",
        "Assistant": "Assistant",
        "Ai": "Ai",
        "Ml": "Ml",
        "Dl": "Dl",
        "Nlp": "Nlp",
        "Cv": "Cv",
        "Rl": "Rl",
        "Gan": "Gan",
        "Transformer": "Transformer",
        "Bert": "Bert",
        "Gpt": "Gpt",
        "Llm": "Llm",
        "BigData": "Big Data",
        "Blockchain": "Blockchain",
        "Crypto": "Crypto",
        "Nft": "Nft",
        "Web3": "Web3",
        "Metaverse": "Metaverse",
        "Vr": "Vr",
        "Ar": "Ar",
        "Mr": "Mr",
        "Xr": "Xr",
        "Iot": "Iot",
        "EdgeAi": "Edge Ai",
        "Robotics": "Robotics",
        "Cybernetics": "Cybernetics",
        "Bioinformatics": "Bioinformatics",
        "Aero": "Aero",
        "Auto": "Auto",
        "Fin": "Fin",
        "Ed": "Ed",
        "Med": "Med",
        "Gov": "Gov",
        "Media": "Media",
        "E-commerce": "E-commerce",
        "Retail": "Retail",
        "Banking": "Banking",
        "Insurance": "Insurance",
        "RealEstate": "Real Estate",
        "Tourism": "Tourism",
        "Hospitality": "Hospitality",
        "Logistics": "Logistics",
        "SupplyChain": "Supply Chain",
        "Manufacturing": "Manufacturing",
        "Utilities": "Utilities",
        "Charity": "Charity",
        "Non-Profit": "Non-Profit",
        "Religion": "Religion",
        "Culture": "Culture",
        "Art": "Art",
        "Music": "Music",
        "Sports": "Sports",
        "Entertainment": "Entertainment",
        "Fashion": "Fashion",
        "Food": "Food",
        "Drink": "Drink",
        "Cooking": "Cooking",
        "Nutrition": "Nutrition",
        "Fitness": "Fitness",
        "Wellness": "Wellness",
        "Lifestyle": "Lifestyle",
        "Garden": "Garden",
        "Pets": "Pets",
        "Hobby": "Hobby",
        "Gaming": "Gaming",
        "Networking": "Networking",
        "Dating": "Dating",
        "Travel": "Travel",
        "Adventure": "Adventure",
        "Science": "Science",
        "Present": "Present",
        "Morning": "Morning",
        "Afternoon": "Afternoon",
        "Evening": "Evening",
        "Night": "Night",
        "Week": "Week",
        "Month": "Month",
        "Year": "Year",
        "Decade": "Decade",
        "Century": "Century",
        "Millennium": "Millennium",
        "Always": "Always",
        "Never": "Never",
        "Sometimes": "Sometimes",
        "Often": "Often",
        "Rarely": "Rarely",
        "Usually": "Usually",
        "Frequently": "Frequently",
        "Occasionally": "Occasionally",
        "Constantly": "Constantly",
        "Periodically": "Periodically",
        "Randomly": "Randomly",
        "Sequentially": "Sequentially",
        "Simultaneously": "Simultaneously",
        "Concurrently": "Concurrently",
        "Parallelly": "Parallelly",
        "Directly": "Directly",
        "Indirectly": "Indirectly",
        "Explicitly": "Explicitly",
        "Implicitly": "Implicitly",
        "Formally": "Formally",
        "Informally": "Informally",
        "Strictly": "Strictly",
        "Loosely": "Loosely",
        "Precisely": "Precisely",
        "Approximately": "Approximately",
        "Basically": "Basically",
        "Fundamental": "Fundamental",
        "Essential": "Essential",
        "Crucial": "Crucial",
        "Vital": "Vital",
        "Important": "Important",
        "Significant": "Significant",
        "Minor": "Minor",
        "Major": "Major",
        "Primary": "Primary",
        "Secondary": "Secondary",
        "Tertiary": "Tertiary",
        "Ultimate": "Ultimate",
        "Final": "Final",
        "Original": "Original",
        "Initial": "Initial",
        "Basic": "Basic",
        "Advanced": "Advanced",
        "Expert": "Expert",
        "Professional": "Professional",
        "Master": "Master",
        "Grandmaster": "Grandmaster",
        "Legendary": "Legendary",
        "Epic": "Epic",
        "Mythic": "Mythic",
        "Divine": "Divine",
        "Holy": "Holy",
        "Sacred": "Sacred",
        "Profane": "Profane",
        "Secular": "Secular",
        "Common": "Common",
        "Rare": "Rare",
        "Unique": "Unique",
        "Standard": "Standard",
        "Custom": "Custom",
        "Bespoke": "Bespoke",
        "Tailored": "Tailored",
        "Generic": "Generic",
        "Specific": "Specific",
        "General": "General",
        "Universal": "Universal",
        "Global": "Global",
        "Regional": "Regional",
        "National": "National",
        "International": "International",
        "Cosmic": "Cosmic",
        "Galactic": "Galactic",
        "Solar": "Solar",
        "Planetary": "Planetary",
        "Earthly": "Earthly",
        "Atmospheric": "Atmospheric",
        "Oceanic": "Oceanic",
        "Terrestrial": "Terrestrial",
        "Chemical": "Chemical",
        "Physical": "Physical",
        "Mathematical": "Mathematical",
        "Logical": "Logical",
        "Psychological": "Psychological",
        "Social": "Social",
        "Economic": "Economic",
        "Political": "Political",
        "Historical": "Historical",
        "Geographical": "Geographical",
        "Geological": "Geological",
        "Astronomical": "Astronomical",
        "Biological": "Biological",
        "Evolutionary": "Evolutionary",
        "Genetic": "Genetic",
        "Neurological": "Neurological",
        "Cellular": "Cellular",
        "Molecular": "Molecular",
        "Atomic": "Atomic",
        "Subatomic": "Subatomic",
        "Quantum": "Quantum",
        "Relativistic": "Relativistic",
        "Newtonian": "Newtonian",
        "Modern": "Modern",
        "Postmodern": "Postmodern",
        "Contemporary": "Contemporary",
        "Ancient": "Ancient",
        "Medieval": "Medieval",
        "Renaissance": "Renaissance",
        "Baroque": "Baroque",
        "Classical": "Classical",
        "Romantic": "Romantic",
        "Industrial": "Industrial",
        "Electronic": "Electronic",
        "Digital": "Digital",
        "Analog": "Analog",
        "Virtual": "Virtual",
        "Augmented": "Augmented",
        "Mixed": "Mixed",
        "Extended": "Extended",
        "Artificial": "Artificial",
        "Synthetic": "Synthetic",
        "Natural": "Natural",
        "Organic": "Organic",
        "Inorganic": "Inorganic",
        "Dead": "Dead",
        "Living": "Living",
        "Non-living": "Non-living",
        "Sentient": "Sentient",
        "Conscious": "Conscious",
        "Sapient": "Sapient",
        "Humanoid": "Humanoid",
        "Robotic": "Robotic",
        "Mechanical": "Mechanical",
        "Hydraulic": "Hydraulic",
        "Pneumatic": "Pneumatic",
        "Electric": "Electric",
        "Magnetic": "Magnetic",
        "Optical": "Optical",
        "Acoustic": "Acoustic",
        "Thermal": "Thermal",
        "Nuclear": "Nuclear",
        "Radiant": "Radiant",
        "Gravitational": "Gravitational",
        "Centrifugal": "Centrifugal",
        "Centripetal": "Centripetal",
        "Inertial": "Inertial",
        "Kinetic": "Kinetic",
        "Potential": "Potential",
        "Elastic": "Elastic",
        "Stable": "Stable",
        "Unstable": "Unstable",
        "Equilibrium": "Equilibrium",
        "Harmony": "Harmony",
        "Chaos": "Chaos",
        "Order": "Order",
        "Entropy": "Entropy",
        "Complexity": "Complexity",
        "Symmetry": "Symmetry",
        "Asymmetry": "Asymmetry",
        "Pattern": "Pattern",
        "Structure": "Structure",
        "Shape": "Shape",
        "Size": "Size",
        "Scale": "Scale",
        "Proportion": "Proportion",
        "Dimension": "Dimension",
        "Geometry": "Geometry",
        "Topology": "Topology",
        "Algebra": "Algebra",
        "Calculus": "Calculus",
        "Set Theory": "Set Theory",
        "Number Theory": "Number Theory",
        "Graph Theory": "Graph Theory",
        "Probability Theory": "Probability Theory",
        "Statistics": "Statistics",
        "Information Theory": "Information Theory",
        "Game Theory": "Game Theory",
        "Control Theory": "Control Theory",
        "Systems Theory": "Systems Theory",
        "Chaos Theory": "Chaos Theory",
        "Quantum Theory": "Quantum Theory",
        "Relativity": "Relativity",
        "Genetics": "Genetics",
        "Neuroscience": "Neuroscience",
        "Psychology": "Psychology",
        "Sociology": "Sociology",
        "Economics": "Economics",
        "Politics": "Politics",
        "Geography": "Geography",
        "Anthropology": "Anthropology",
        "Archaeology": "Archaeology",
        "Linguistics": "Linguistics",
        "Philology": "Philology",
        "Philosophy": "Philosophy",
        "Theology": "Theology",
        "Aesthetics": "Aesthetics",
        "Metaphysics": "Metaphysics",
        "Epistemology": "Epistemology",
        "Ontology": "Ontology",
        "Logic": "Logic",
        "Mathematics": "Mathematics",
        "Physics": "Physics",
        "Chemistry": "Chemistry",
        "Biology": "Biology",
        "Medicine": "Medicine",
        "Engineering": "Engineering",
        "Architecture": "Architecture",
        "Military": "Military",
        "Business": "Business",
        "Journalism": "Journalism",
        "Library Science": "Library Science",
        "Informatics": "Informatics",
        "Communication": "Communication",
        "Marketing": "Marketing",
        "Finance": "Finance",
        "Accounting": "Accounting",
        "Human Resources": "Human Resources",
        "Operations": "Operations",
        "Strategy": "Strategy",
        "Leadership": "Leadership",
        "Innovation": "Innovation",
        "Entrepreneurship": "Entrepreneurship",
        "Governance": "Governance",
        "Diplomacy": "Diplomacy",
        "Sustainability": "Sustainability",
        "Ethics": "Ethics",
        "Law": "Law",
        "Human Rights": "Human Rights",
        "Equality": "Equality",
        "Justice": "Justice",
        "Democracy": "Democracy",
        "Health": "Health",
        "Education": "Education",
        "Poverty": "Poverty",
        "Hunger": "Hunger",
        "Environment": "Environment",
        "Climate Change": "Climate Change",
        "Water": "Water",
        "Sanitation": "Sanitation",
        "Infrastructure": "Infrastructure",
        "Industry": "Industry",
        "Technology": "Technology",
        "Inequality": "Inequality",
        "Cities": "Cities",
        "Consumption": "Consumption",
        "Life Below Water": "Life Below Water",
        "Life On Land": "Life On Land",
        "Partnership": "Partnership",
        "Growth": "Growth",
        "Progress": "Progress",
        "Prosperity": "Prosperity",
        "Happiness": "Happiness",
        "Well-being": "Well-being",
        "Wisdom": "Wisdom",
        "Truth": "Truth",
        "Beauty": "Beauty",
        "Love": "Love",
        "Diversity": "Diversity",
        "Inclusion": "Inclusion",
        "Empathy": "Empathy",
        "Compassion": "Compassion",
        "Kindness": "Kindness",
        "Respect": "Respect",
        "Integrity": "Integrity",
        "Courage": "Courage",
        "Perseverance": "Perseverance",
        "Resilience": "Resilience",
        "Optimism": "Optimism",
        "Gratitude": "Gratitude",
        "Forgiveness": "Forgiveness",
        "Patience": "Patience",
        "Humility": "Humility",
        "Simplicity": "Simplicity",
        "Moderation": "Moderation",
        "Balance": "Balance",
        "Peace": "Peace",
        "Silence": "Silence",
        "Solitude": "Solitude",
        "Contemplation": "Contemplation",
        "Meditation": "Meditation",
        "Spirituality": "Spirituality",
        "Mysticism": "Mysticism",
        "Enlightenment": "Enlightenment",
        "Nirvana": "Nirvana",
        "Zen": "Zen",
        "Tao": "Tao",
        "Dharma": "Dharma",
        "Karma": "Karma",
        "Yoga": "Yoga",
        "Mandala": "Mandala",
        "Fractal": "Fractal",
        "Infinity": "Infinity",
        "Eternity": "Eternity",
        "Universe": "Universe",
        "Multiverse": "Multiverse",
        "Cosmos": "Cosmos",
        "Creation": "Creation",
        "Existence": "Existence",
        "Being": "Being",
        "Non-being": "Non-being",
        "Nothingness": "Nothingness",
        "Void": "Void",
        "Emptiness": "Emptiness",
        "Presence": "Presence",
        "Absence": "Absence",
        "Change": "Change",
        "Motion": "Motion",
        "Stillness": "Stillness",
        "Flow": "Flow",
        "Vibration": "Vibration",
        "Energy": "Energy",
        "Light": "Light",
        "Darkness": "Darkness",
        "Shadow": "Shadow",
        "Color": "Color",
        "Sound": "Sound",
        "Form": "Form",
        "Space": "Space",
        "Time": "Time",
        "History": "History",
        "Destiny": "Destiny",
        "Fate": "Fate",
        "Will": "Will",
        "Choice": "Choice",
        "Freedom": "Freedom",
        "Responsibility": "Responsibility",
        "Meaning": "Meaning",
        "Purpose": "Purpose",
        "Value": "Value",
        "Goal": "Goal",
        "Mission": "Mission",
        "Vision": "Vision",
        "Dream": "Dream",
        "Hope": "Hope",
        "Faith": "Faith",
        "Trust": "Trust",
        "Safety": "Safety",
        "Home": "Home",
        "Belonging": "Belonging",
        "Connection": "Connection",
        "Relationship": "Relationship",
        "Family": "Family",
        "Friendship": "Friendship",
        "Community": "Community",
        "Society": "Society",
        "Humanity": "Humanity",
        "Nature": "Nature",
        "Life": "Life",
        "Death": "Death",
        "Birth": "Birth",
        "Rebirth": "Rebirth",
        "Evolution": "Evolution",
        "Awakening": "Awakening",
        "Transcendence": "Transcendence",
        "Wholeness": "Wholeness",
        "Perfection": "Perfection",
        "Imperfection": "Imperfection",
        "Duality": "Duality",
        "Non-duality": "Non-duality",
        "Oneness": "Oneness",
        "Unity": "Unity",
        "Zero": "Zero",
        "One": "One",
        "Two": "Two",
        "Three": "Three",
        "Four": "Four",
        "Five": "Five",
        "Six": "Six",
        "Seven": "Seven",
        "Eight": "Eight",
        "Nine": "Nine",
        "Ten": "Ten",
        "Eleven": "Eleven",
        "Twelve": "Twelve",
        "Thirteen": "Thirteen",
        "Fourteen": "Fourteen",
        "Fifteen": "Fifteen",
        "Sixteen": "Sixteen",
        "Seventeen": "Seventeen",
        "Eighteen": "Eighteen",
        "Nineteen": "Nineteen",
        "Twenty": "Twenty",
        "Thirty": "Thirty",
        "Forty": "Forty",
        "Fifty": "Fifty",
        "Sixty": "Sixty",
        "Seventy": "Seventy",
        "Eighty": "Eighty",
        "Ninety": "Ninety",
        "Hundred": "Hundred",
        "Thousand": "Thousand",
        "Million": "Million",
        "Billion": "Billion",
        "Trillion": "Trillion",
        "Infinite": "Infinite",
        "Unknown": "Unknown",
        "None": "None",
        "All": "All",
        "Many": "Many",
        "Few": "Few",
        "Several": "Several",
        "Half": "Half",
        "Double": "Double",
        "Triple": "Triple",
        "Multiple": "Multiple",
        "Variable": "Variable",
        "Fixed": "Fixed",
        "Dynamic": "Dynamic",
        "Static": "Static",
        "Automatic": "Automatic",
        "Manual": "Manual",
        "Smart": "Smart",
        "Intelligent": "Intelligent",
        "Fast": "Fast",
        "Slow": "Slow",
        "Quick": "Quick",
        "Rapid": "Rapid",
        "Delayed": "Delayed",
        "Scheduled": "Scheduled",
        "Planned": "Planned",
        "Live": "Live",
        "Online": "Online",
        "Offline": "Offline",
        "Connected": "Connected",
        "Disconnected": "Disconnected",
        "Syncing": "Syncing",
        "Synced": "Synced",
        "Error": "Error",
        "Success": "Success",
        "Failure": "Failure",
        "Info": "Info",
        "Debug": "Debug",
        "Trace": "Trace",
        "Critical": "Critical",
        "Alert": "Alert",
        "Notification": "Notification",
        "Message": "Message",
        "System": "System",
        "User": "User",
        "Admin": "Admin",
        "Guest": "Guest",
        "Client": "Client",
        "Server": "Server",
        "Host": "Host",
        "Remote": "Remote",
        "Local": "Local",
        "External": "External",
        "Internal": "Internal",
        "Shared": "Shared",
        "Private": "Private",
        "Public": "Public",
        "Secret": "Secret",
        "Hidden": "Hidden",
        "Visible": "Visible",
        "Enabled": "Enabled",
        "Disabled": "Disabled",
        "Active": "Active",
        "Inactive": "Inactive",
        "Locked": "Locked",
        "Unlocked": "Unlocked",
        "Protected": "Protected",
        "Unprotected": "Unprotected",
        "Secure": "Secure",
        "Insecure": "Insecure",
        "Safe": "Safe",
        "Unsafe": "Unsafe",
        "Danger": "Danger",
        "Risk": "Risk",
        "Hazard": "Hazard",
        "Caution": "Caution",
        "Attention": "Attention",
        "Careful": "Careful",
        "Warning": "Warning",
        "Notice": "Notice",
        "Report": "Report",
        "Analytics": "Analytics",
        "Dashboard": "Dashboard",
        "Management": "Management",
        "Settings": "Settings",
        "Profile": "Profile",
        "Account": "Account",
        "Logout": "Logout",
        "Login": "Login",
        "Register": "Register",
        "ForgotPassword": "Forgot Password",
        "ResetPassword": "Reset Password",
        "UpdatePassword": "Update Password",
        "VerifyEmail": "Verify Email",
        "ConfirmEmail": "Confirm Email",
        "Support": "Support",
        "Help": "Help",
        "Documentation": "Documentation",
        "About": "About",
        "Contact": "Contact",
        "Terms": "Terms",
        "Privacy": "Privacy",
        "Cookies": "Cookies",
        "Legal": "Legal",
        "Licenses": "Licenses",
        "Copyright": "Copyright",
        "Trademark": "Trademark",
        "Credits": "Credits",
        "Version": "Version",
        "Build": "Build",
        "Release": "Release",
        "Alpha": "Alpha",
        "Beta": "Beta",
        "Staging": "Staging",
        "Production": "Production",
        "Development": "Development",
        "Testing": "Testing",
        "Quality": "Quality",
        "Performance": "Performance",
        "Security": "Security",
        "Reliability": "Reliability",
        "Availability": "Availability",
        "Scalability": "Scalability",
        "Usability": "Usability",
        "Accessibility": "Accessibility",
        "Internationalization": "Internationalization",
        "Localization": "Localization",
        "Customization": "Customization",
        "Personalization": "Personalization",
        "Integration": "Integration",
        "Automation": "Automation",
        "Optimization": "Optimization",
        "Transformation": "Transformation",
        "Modernization": "Modernization",
        "Migration": "Migration",
        "Upgrade": "Upgrade",
        "Update": "Update",
        "Fix": "Fix",
        "Patch": "Patch",
        "Hotfix": "Hotfix",
        "Emergency": "Emergency",
        "Priority": "Priority",
        "High": "High",
        "Medium": "Medium",
        "Low": "Low",
        "Lowest": "Lowest",
        "Highest": "Highest",
        "Normal": "Normal",
        "Urgent": "Urgent",
        "Immediate": "Immediate",
        "ASAP": "ASAP",
        "Today": "Today",
        "Tomorrow": "Tomorrow",
        "Yesterday": "Yesterday",
        "Now": "Now",
        "Next": "Next",
        "Previous": "Previous",
        "Current": "Current",
        "Past": "Past",
        "Future": "Future",
        "Upcoming": "Upcoming",
        "Ongoing": "Ongoing",
        "Completed": "Completed",
        "Pending": "Pending",
        "Cancelled": "Cancelled",
        "Failed": "Failed",
        "Succeeded": "Succeeded",
        "Doing": "Doing",
        "Done": "Done",
        "Backlog": "Backlog",
        "Ready": "Ready",
        "Review": "Review",
        "Draft": "Draft",
        "Archived": "Archived",
        "Deleted": "Deleted",
        "Restored": "Restored"
        ,
        socialMediaLinks: "Social Media Links",
        twitter: "Twitter",
        youtube: "YouTube",
        instagram: "Instagram",

        // Kitchen Page

        // Analytics Page
        revenue: "Revenue",
        items: "Items",

        // HomePage Extra
        welcomeTo: "Welcome to",
        restaurant: "Restaurant",
        heroDescription: "A modern QR-based restaurant ordering system that enhances customer experience and streamlines operations",
        customerExperience: "Customer Experience",
        customerDesc: "Seamless QR-based ordering, real-time tracking, and instant payments.",
        kitchenDisplayFeature: "Kitchen Display",
        kitchenDesc: "Efficient order management, status updates, and preparation tracking.",
        adminDashboardFeature: "Admin Dashboard",
        adminDesc: "Comprehensive management of menu, tables, and restaurant settings.",
        analyticsReports: "Analytics & Reports",
        analyticsDesc: "Deep insights into sales, popular items, and peak hours.",
        ingredientsDesc: "Track inventory, manage stock levels, and get low stock alerts.",
        staffDesc: "Staff login, attendance tracking, and leave management.",
        viewCustomerApp: "View Customer App",
        viewKitchenDisplay: "Kitchen Display",
        viewAdminPanel: "View Admin Panel",
        viewAnalytics: "View Analytics",
        manageIngredients: "Manage Ingredients",
        staffLogin: "Staff Login",
        whyChooseTitle: "Why Choose RedSorm?",
        mobileFirstTitle: "Mobile First",
        mobileFirstDesc: "Optimized for all devices with responsive design and touch-friendly interface",
        lightningFastTitle: "Lightning Fast",
        lightningFastDesc: "Quick order placement and real-time updates for efficient restaurant operations",
        secureReliableTitle: "Secure & Reliable",
        secureReliableDesc: "Enterprise-grade security with encrypted payments and data protection",
        testimonialQuote: "RedSorm transformed our restaurant operations. Orders are processed 40% faster, and our customers love the convenience of ordering from their tables.",
        testimonialAuthor: "Sarah Johnson",
        testimonialRole: "Owner, The Garden Bistro",
        footerTagline: "Modern QR-based restaurant ordering system for enhanced dining experiences.",
        copyright: "All rights reserved.",
        switchBranch: "Switch Branch",
        about: "About",
        blog: "Blog",
        careers: "Careers",

        // Dashboard Features
        qrScanTitle: "Scan & Order",
        qrScanDesc: "Frictionless ordering experience. Customers scan, order, and pay without waiting.",
        liveKDS: "Live KDS",
        kdsDesc: "Replace printers with smart screens. Real-time updates for your kitchen team.",
        analyticsInsights: "Analytics & Insights",
        analyticsInsightsDesc: "Track sales, top items, and peak hours. Data-driven growth.",
        tableMgmt: "Table Management",
        tableMgmtDesc: "Digital floor plan. Track occupancy status in real-time.",
        dynamicMenu: "Dynamic Menu",
        dynamicMenuDesc: "Update items, prices, and images instantly everywhere.",
        smartInventory: "Smart Inventory",
        inventoryDesc: "Automated stock deduction. Low-stock alerts.",
        roleMgmt: "Role Management",
        roleMgmtDesc: "Secure logins for Staff, Chefs, and Managers.",
        loyaltyRewardsTitle: "Loyalty & Rewards",
        loyaltyRewardsDesc: "Points rewards and birthday discount settings.",
        manualOrderingTitle: "Manual Ordering",
        manualOrderingDesc: "Log Swiggy, Zomato & phone orders directly.",

        // Testimonials
        sarahJohnsonPosition: "Owner, The Garden Bistro",
        sarahJohnsonQuote: "RedSorm transformed our operations. Orders are 40% faster.",
        michaelChenPosition: "Manager, Urban Eats",
        michaelChenQuote: "The analytics insight increased our profits by 25% in two months.",
        emilyRodriguezPosition: "Chef, Coastal Kitchen",
        emilyRodriguezQuote: "The KDS is a lifesaver. The kitchen is calm, organized, and efficient.",

        // Toasts & Alerts
        serverError: "Server error",
        networkError: "Network error",
        sessionExpired: "Session expired. Please login again.",
        uploadFailed: "Upload failed",
        profileUpdated: "Profile updated!",
        roleSaved: "Role saved successfully",
        roleDeleted: "Role deleted",
        userSaved: "User saved successfully",
        userDeleted: "User deleted",
        staffAdded: "Staff Added",
        staffUpdated: "Staff Updated",
        itemDeleted: "Item deleted!",
        itemUpdated: "Item updated!",
        itemAdded: "Item added!",
        tableUpdated: "Table updated!",
        tableAdded: "Table added!",
        categoryExists: "Category already exists",
        groupUpdated: "Group updated!",
        groupAdded: "Group added!",
        actionFailed: "Action failed",
        generating: "Generating...",
        aiSuccess: "AI generation successful!",

        // Customer & Orders
        chooseTable: "Choose your table...",
        paymentMethod: "Payment Method",
        paymentStatus: "Payment Status",
        cash: "Cash (Pay at table)",
        online: "Online (Digital payment)",
        ordered: "Ordered",
        cooking: "Cooking",
        enjoy: "Enjoy",
        totalWaitTime: "Total Wait Time",
        rateFood: "Rate Food",
        reorder: "Reorder",
        cancelOrder: "Cancel Full Order",
        cancelItem: "Cancel Item",
        confirmCancel: "Confirm Cancel",
        reasonForCancel: "Reason for cancellation...",
        yourCart: "Your Cart",
        cartEmpty: "Your cart is empty",
        orderSuccess: "Order Placed!",
        orderPrep: "Your order is being prepared.",
        vitamin: "Vitamin",
        vitamins: "Vitamins",
        nutritionalInfo: "Nutritional Info",
        available: "Available",

        // Admin Specific Labels
        clickToEditProfile: "Click to edit profile",
        staffDirectory: "Staff Directory",
        portalQr: "Portal QR",
        editStaff: "Edit Staff",
        addStaff: "Add Staff",
        updateStaff: "Update Staff",
        companySettings: "Company Settings",
        companyName: "Company Name",
        logo: "Logo",
        banner: "Banner",
        trialExpiredBanner: "Your free trial has expired. Some features may be limited.",
        trialExpiresToday: "⚠️ Your free trial expires today! Upgrade now to keep access.",
        trialDaysLeft: "🎉 You have {days} days left in your 20-day free access period.",
        subscribeNow: "Subscribe Now",
        upgradePlan: "Upgrade Plan",
        activeSubscription: "Active Subscription",
        validUntil: "Valid until",
        unlimitedOrders: "Unlimited Orders",
        detailedAnalytics: "Detailed Analytics",
        support247: "24/7 Support",
        upgradeNowToContinue: "Upgrade Now to Continue",
        securePaymentNote: "Secure payment via Cashfree • Cancel anytime",
        trialExpiredTitle: "Trial Period Expired",
        trialExpiredDesc: "Your 20-day free access to {name} has ended. To continue managing your restaurant, please upgrade. ",

        // Misc
        search: "Search...",
        noItemsFound: "No items found. Try adjusting filters.",
        kitchenDisplay: "Kitchen Display",
        preparing: "Preparing",
        ready: "Ready",
        delivered: "Delivered",
        yesterday: "Yesterday",
        date: "Date",
        name: "Name",
        connected: "Connected",
        disconnected: "Disconnected",
        completed: "Completed",
        time: "Time",
        allOrdersCleared: "All orders cleared!",
        greatJob: "Great job keeping up with the pace.",
        startPreparation: "Start Preparation",
        markReady: "Mark Ready",
        complete: "Complete",
        analyticsDashboard: "Analytics Dashboard",
        analyticsDescription: "Real-time insights into your restaurant performance",
        totalOrders: "Total Orders",
        totalRevenue: "Total Revenue",
        tablesServed: "Tables Served",
        avgOrderValue: "Avg Order Value",
        businessInsights: "Business Insights",
        topPerformer: "Top Performer",
        peakHour: "Peak Hour",
        paymentPreference: "Payment Preference",
        categoryOpportunity: "Category Opportunity",
        newCustomers: "New Customers",
        returningCustomers: "Returning Customers",
        downloadExcel: "Download Excel",
        downloadPDF: "Download PDF",
        daily: "Daily",
        weekly: "Weekly",
        monthly: "Monthly",
        yearly: "Yearly",
        today: "Today",
        overview: "Overview",
        revenueByBranch: "Revenue by Branch",
        ordersByBranch: "Orders by Branch",
        topItemsByBranch: "Top Items by Branch",
        last7: "Last 7 Days",
        operations: "Operations",
        customers: "Customers",
        ingredientsManagement: "Ingredients Management",
        manageInventory: "Manage your inventory and stock levels",
        backToAdmin: "Back to Admin",
        addIngredient: "Add Ingredient",
        totalIngredients: "Total Ingredients",
        lowStockItems: "Low Stock Items",
        searchIngredients: "Search ingredients...",
        quantity: "Quantity",
        unit: "Unit",
        threshold: "Threshold",
        costPerUnit: "Cost / Unit",
        totalCost: "Total Cost",
        lowStock: "Low Stock",
        inStock: "In Stock",
        editIngredient: "Edit Ingredient",
        deleteIngredientConfirm: "Are you sure you want to delete this ingredient?",
        saveIngredient: "Save Ingredient",
        lowStockThreshold: "Low Stock Threshold",
        staffPortal: "Staff Portal",
        enterPin: "Enter 6-digit PIN",
        verifying: "Verifying...",
        staffDashboard: "Staff Dashboard",
        attendance: "Attendance",
        leaves: "Leaves",
        punchIn: "Punch In",
        punchOut: "Punch Out",
        attendanceHistory: "Attendance History",
        allStaffAttendance: "All Staff Attendance",
        last7Days: "Last 7 Days",
        thisMonth: "This Month",
        customRange: "Custom Range",
        staffName: "Staff Name",
        checkIn: "In",
        checkOut: "Out",
        hours: "Hours",
        newLeaveRequest: "New Leave Request",
        fromDate: "From",
        toDate: "To",

        // Additional missing translations

        // Branch Management
        reason: "Reason",
        submitRequest: "Submit Request",
        allLeaveRequests: "All Leave Requests",
        myLeaveHistory: "My Leave History",
        noAttendanceRecords: "No attendance records found",
        noLeaveRequestsFound: "No leave requests found",
        approve: "Approve",
        reject: "Reject",
        todaySummary: "Today's Summary",
        inTime: "Start Time",
        outTime: "End Time",
        totalWorkingHours: "Total Working Hours",
        startTime: "Start Time",
        endTime: "End Time",
        workingHours: "Working Hours",
        workSessionDetails: "Work Session Details",
        duration: "Duration",
        closeDetails: "Close Details",
        sessions: "Sessions",
        sessionTimeline: "Session Timeline",
        session: "Session",
        selfService: "Self Service",
        selfServiceItem: "Self Service Item",
        pickupRequired: "Pickup Required",
        selfServiceInfo: "Please collect this item from the counter after placing your order.",

        // Additional missing translations
        attendanceManagement: "Attendance Management",
        allBranches: "All Branches",
        noName: "No Name",
        allEnabled: "All Enabled",
        enableAll: "Enable All",
        selfServe: "Self Serve",
        served: "Served",
        selfServeCategoryInfo: "Toggle \"Self Serve\" to notify customers to pick up orders from this category.",
        festivalOffer: "Festival Offer",
        festivalName: "Festival Name",
        festivalDiscount: "Festival Discount (%)",
        festivalDesc: "This discount will be applied to the overall bill",
        festivalPlaceholder: "e.g., Diwali, Christmas, New Year",
        billingSettings: "Billing & Payment Settings",
        unlockToEdit: "Unlock to Edit",
        unlocked: "Unlocked",
        paymentSettingsLocked: "Payment Settings Locked",
        lockMessage: "For security reasons, your QR codes and UPI settings and billing structure are locked. Please unlock using your admin password.",
        unlockNow: "Unlock Now",
        sgst: "SGST (%)",
        cgst: "CGST (%)",
        upiIdLabel: "UPI ID for Click-to-Pay",
        upiIdPlaceholder: "e.g., yourname@okaxis",
        paymentQRCodes: "Payment QR Codes",
        addQR: "Add QR",
        renamedSuccess: "Renamed successfully",
        renameFailed: "Rename failed",
        allBranchesFloating: "All Branches (Floating)",
        manageAttendanceTitle: "Manage Attendance & Leaves",
        manageAttendanceDesc: "Track staff attendance, punch-in/out times, and manage leave requests in our dedicated portal.",
        openAttendanceManager: "Open Attendance Manager",
        staffPortalQr: "Staff Portal QR",
        scanToAccessStaff: "Scan to check-in/out",
        itemNamePlaceholder: "Item Name (e.g., Grilled Chicken)",
        itemDescriptionPlaceholder: "Short description of the item",
        tableNumberPlaceholder: "Table Number (Eg: 1)",
        tableNamePlaceholder: "Table Name (Eg: Garden 1)",
        roleTypeAdd: "Role Type Add",
        newCategoryName: "New Category Name",
        globalSelfService: "Global Self Service",
        globalSelfServiceDesc: "Turn on/off self-service for all categories at once",
        upgradeForAllLanguages: "Upgrade to Premium for all 8 languages",
        subscribeForLanguages: "Subscribe to access language support",
        tablesUsed: "{current}/{total} tables used",
        itemsUsed: "{current}/{total} items used",
        noMenuItemsToExport: "No menu items to export",
        noDataInFile: "No data found in the file",
        itemsImportedSuccessfully: "{count} items imported successfully!",
        categoryNamePlaceholder: "New Category Name",
        upgradeForBulk: "Upgrade for Bulk Upload/Download",
        unlockSettings: "Unlock Settings",
        enterPasswordToProceed: "Please enter your admin password to continue",
        settingsUnlocked: "Settings unlocked!",
        invalidPassword: "Invalid password",
        verificationFailed: "Verification failed",
        adminEmailNotFound: "Admin email not found",
        sendingResetLink: "Sending reset link...",
        allBranchesCombined: "All Branches",
        unknownBranch: "Unknown Branch",
        refreshAll: "Refresh All Data",
        addNew: "Add New",
        manageCategories: "Manage Categories",
        uploadExcel: "Upload Excel",
        preview: "Preview",
        editName: "Edit Name",
        deleteCategory: "Delete Category",
        allGroups: "All Groups",
        firstIn: "First In",
        lastOut: "Last Out",
        download: "Download",
        refresh: "Refresh",
        selectBranch: "Select Branch",
        viewingDataFor: "Viewing data for",
        filteredByBranchInfo: "All menu items, orders, ingredients, and analytics are filtered to this branch.",
        revenueTrend: "Revenue Trend",
        ordersOverview: "Orders Overview",
        revenueBreakdown: "Revenue Breakdown",
        revenueByCategory: "Revenue by Category",
        itemPerformance: "Item Performance",
        topSellingItems: "Top Selling Items",
        categoryPerformance: "Category Performance",
        topItemsDetails: "Top Items Details",
        customerAnalysis: "Customer Analysis",
        customerRetention: "Customer Retention",
        customerMetrics: "Customer Metrics",
        retentionRate: "Retention Rate",
        operationsAnalysis: "Operations Analysis",
        hourlyOrderDistribution: "Hourly Order Distribution",
        tablePerformance: "Table Performance",
        tablePerformanceDetails: "Table Performance Details",
        from: "From",
        to: "To",
        tryAdjustingFilters: "Try adjusting the date filters",
        noAttendanceToExport: "No attendance records to export.",
        refreshing: "Refreshing all data...",
        refreshed: "All data refreshed!",
        refreshFailed: "Refresh failed",
        statusUpdated: "Status updated!",
        updateFailed: "Update failed",
        failedLoadMenu: "Failed to load menu",
        categoryAdded: "Category added!",
        updatedSuccessfully: "Updated successfully",
        enabledForAll: "Enabled for all",
        disabledForAll: "Disabled for all",
        bulkUpdateFailed: "Bulk update failed",
        enterGroupName: "Enter group name",
        deleted: "Deleted",
        error: "Error",
        printedPopupBlocked: "Printed (popup blocked)",
        qrSentToPrinter: "QR sent to printer",
        printFailed: "Print failed",
        uploadingLogo: "Uploading logo...",
        logoUploaded: "Logo uploaded!",
        uploadingBanner: "Uploading banner...",
        bannerUploaded: "Banner uploaded!",
        generationFailed: "Generation failed",
        uploadingQR: "Uploading QR...",
        qrAdded: "QR Code added!",
        deleteStaffConfirm: "Are you sure you want to delete this staff member?",
        deleteGroupConfirm: "Delete group? Tables in this group will be set to Non AC.",
        deleteRoleConfirm: "Are you sure you want to delete this role?",
        deleteUserConfirm: "Are you sure you want to delete this user?",
        deleteCategoryConfirm: "Delete category \"{name}\"? Items in it will lose their category.",
        upiIdDesc: "Entering a UPI ID will enable GPay, PhonePe, and Paytm buttons on the bill.",
        optionalDiscountDesc: "Optional: Add a discount percentage (e.g., 10 for 10% off)",
        paymentSettings: "Payment Settings",
        festivalOfferDesc: "This discount will be applied to the overall bill",
        yes: "Yes",
        no: "No",
        confirmAction: "Confirm Action",
        failedResetEmail: "Failed to send reset email",
        saving: "Saving...",
        qr: "QR",

        // Branch Management
        branchMgmt: "Branch Management",
        branchMgmtSub: "Manage your restaurant locations and branches",
        addNewBranch: "Add New Branch",
        loadingBranches: "Loading branches...",
        noBranchesYet: "No branches yet",
        noBranchesDesc: "Expand your business by adding your first branch or location.",
        createFirstBranch: "Create First Branch",
        branchActiveDesc: "Branch is active and accepting orders",
        failedLoadBranches: "Failed to load branches",
        branchSavedSuccess: "Branch saved successfully",
        failedSaveBranch: "Failed to save branch",
        errorSavingBranch: "Error saving branch",
        deleteBranchConfirm: "Are you sure you want to delete this branch? This will also affect associated data.",
        branchDeletedSuccess: "Branch deleted successfully",
        failedDeleteBranch: "Failed to delete branch",
        errorDeletingBranch: "Error deleting branch",
        createBranch: "Create Branch",
        updateBranch: "Update Branch",
        confirmDeletion: "Confirm Deletion",
        branchName: "Branch Name",
        unavailable: "Unavailable",
        leaveEmptyForNoBranch: "Leave empty for no branch",
        allowedBranches: "Allowed Branches",
        active: "Active",
        inactive: "Inactive",
        all: "All",
        branch: "Branch",
        nonACDefault: "Non-AC (Default)",
        unlock: "Unlock",
        lock: "Lock",

    },
    es: {
        present: "Presente",
        noBranch: "Sin sucursal",
        price: "Precio",
        currency: "Moneda",
        absent: "Ausente",
        totalHours: "Horas Totales",
        avgHours: "Horas Promedio",
        clickToViewList: "Haz clic para ver la lista",
        loading: "Cargando...",
        search: "Buscar...",
        save: "Guardar",
        cancel: "Cancelar",
        delete: "Eliminar",
        edit: "Editar",
        close: "Cerrar",
        add: "Añadir",
        submit: "Enviar",
        home: "Inicio",
        features: "Características",
        pricing: "Precios",
        contact: "Contacto",
        login: "Iniciar Sesión",
        admin: "Administrador",
        kitchen: "Cocina",
        customer: "Cliente",
        analytics: "Analítica",
        ingredients: "Ingredientes",
        staff: "Personal",
        getStarted: "Empezar",
        heroTitle: "Acaba con el Hambre, Empieza a Pedir",
        heroSubtitle: "El sistema de pedidos QR más avanzado.",
        adminPanel: "Panel de Administración",
        manageMenu: "Gestionar Menú y Mesas",
        menu: "Menú",
        tables: "Mesas",
        orders: "Pedidos",
        menuItems: "Elementos del Menú",
        addItem: "Añadir Elemento",
        allCategories: "Todas las Categorías",
        noItemsFound: "No se encontraron elementos",
        tableGroups: "Grupos de Mesas",
        addGroup: "Añadir Grupo",
        addTable: "Añadir Mesa",
        filterByGroup: "Filtrar por Grupo",
        allTables: "Todas las Mesas",
        nonAc: "Sin AC",
        showOrders: "Mostrar Pedidos",
        qr: "QR",
        menuTitle: "Menú",
        table: "Mesa",
        yourCart: "Tu Carrito",
        placeOrder: "Realizar Pedido",
        paymentMethod: "Método de Pago",
        paymentStatus: "Estado de Pago",
        cash: "Efectivo",
        online: "En línea",
        total: "Total",
        addToCart: "Añadir",
        itemAdded: "¡Elemento añadido al carrito!",
        orderPlaced: "¡Pedido realizado con éxito!",
        orderId: "ID del Pedido",
        yourOrders: "Tus Pedidos",
        status: "Estado",
        pending: "Pendiente",
        preparing: "Preparando",
        ready: "Listo",
        delivered: "Entregado",
        todaySpecialItems: "Especiales de hoy",
        topRatedItems: "Mejor valorados",
        noSpecialItems: "No hay artículos especiales hoy. ¡Vuelve más tarde!",
        noTopRatedItems: "No hay artículos mejor valorados disponibles ahora mismo.",
        nutritionalInfo: "Información Nutricional",
        calories: "Calorías",
        protein: "Proteína",
        carbs: "Carbohidratos",
        fats: "Grasas",
        vitamins: "Vitaminas",
        aiRecommendation: "Autocompletar con IA",
        aiDisclaimer: "Generado por IA. Por favor verifique.",
        generating: "Generando...",
        aiSuccess: "¡Generado!",
        available: "Disponible",
        unavailable: "No disponible",
        outOfStock: "Agotado",
        selectRole: "Seleccionar Rol",
        fullName: "Nombre Completo",
        email: "Correo electrónico",
        phoneNumber: "Número de teléfono",
        newPassword: "Nueva Contraseña",
        passwordLabel: "Contraseña (dejar en blanco para mantener la actual)",
        saveUser: "Guardar Usuario",
        confirmAction: "Confirmar Acción",
        yes: "Sí",
        no: "No",
        supportChat: "Chat de Soporte",
        chatWithUs: "Chatea con nosotros",
        scanToAccessStaff: "Escanea para acceder al panel de personal",
        noOrdersForTable: "No se encontraron pedidos para esta mesa.",
        groupNamePlaceholder: "Nombre del grupo (ej. AC, Jardín)",
        tableNumber: "Número de mesa",
        nameOptional: "Nombre (Opcional)",
        totalAmount: "Monto Total",
        trialExpiredBanner: "Tu prueba gratuita ha expirado. Algunas funciones pueden estar limitadas.",
        trialDaysLeft: "🎉 Te quedan {days} días en tu período de acceso gratuito de 20 días.",
        trialExpired: "Acceso gratuito expirado ⏳",
        trialExpiredDesc: "Tu acceso gratuito de 20 días a <b>{name}</b> ha terminado.<br/>Para seguir gestionando tu restaurante, por favor mejora a un plan premium.",
        subscribeNow: "Suscribirse Ahora",
        upgradePlan: "Mejorar Plan",
        unlimitedOrders: "Pedidos Ilimitados",
        detailedAnalytics: "Analítica Detallada",
        support247: "Soporte 24/7",
        upgradeNowToContinue: "Actualizar Ahora para Continuar",
        switchBranch: "Cambiar Sucursal",
        whyChooseTitle: "¿Por qué elegir RedSorm?",
        mobileFirstTitle: "Móvil Primero",
        mobileFirstDesc: "Optimizado para todos los dispositivos con diseño responsivo e interfaz amigable para el tacto",
        lightningFastTitle: "Rápido como el Rayo",
        lightningFastDesc: "Colocación rápida de pedidos y actualizaciones en tiempo real para operaciones eficientes del restaurante",
        secureReliableTitle: "Seguro y Confiable",
        secureReliableDesc: "Seguridad de grado empresarial con pagos encriptados y protección de datos",
        testimonialQuote: "RedSorm transformó las operaciones de nuestro restaurante. Los pedidos se procesan un 40% más rápido y a nuestros clientes les encanta la comodidad de pedir desde sus mesas.",
        testimonialAuthor: "Sarah Johnson",
        testimonialRole: "Propietaria, The Garden Bistro",
        footerTagline: "Sistema moderno de pedidos de restaurante basado en QR para experiencias gastronómicas mejoradas.",
        copyright: "Todos los derechos reservados.",
        product: "Producto",
        company: "Compañía",
        connect: "Conectar",
        about: "Acerca de",
        blog: "Blog",
        careers: "Carreras",
        groups: "Grupos",
        users: "Usuarios",
        roles: "Roles",
        branches: "Sucursales",
        branchComparison: "Comparación de sucursales",
        settings: "Ajustes",
        welcomeTo: "Bienvenido a",
        restaurant: "Restaurante",
        heroDescription: "Un sistema moderno de pedidos de restaurante basado en QR que mejora la experiencia del cliente y optimiza las operaciones",
        customerExperience: "Experiencia del Cliente",
        customerDesc: "Pedidos sin interrupciones basados en QR, seguimiento en tiempo real y pagos instantáneos.",
        kitchenDisplayFeature: "Pantalla de Cocina",
        kitchenDesc: "Gestión eficiente de pedidos, actualizaciones de estado y seguimiento de preparación.",
        adminDashboardFeature: "Panel de Administración",
        adminDesc: "Gestión integral de menú, mesas y ajustes del restaurante.",
        analyticsReports: "Analítica e Informes",
        analyticsDesc: "Información profunda sobre ventas, artículos populares y horas pico.",
        ingredientsManagement: "Gestión de Ingredientes",
        ingredientsDesc: "Rastrea el inventario, gestiona los niveles de stock y obtén alertas de stock bajo.",
        staffPortal: "Portal del Personal",
        staffDesc: "Inicio de sesión de personal, seguimiento de asistencia y gestión de licencias.",
        viewCustomerApp: "Ver App del Cliente",
        viewKitchenDisplay: "Ver Pantalla de Cocina",
        viewAdminPanel: "Ver Panel de Admin",
        viewAnalytics: "Ver Analítica",
        manageIngredients: "Gestionar Ingredientes",
        staffLogin: "Login de Personal",
        menuEmpty: "Tu menú está vacío",
        menuEmptyDesc: "Comienza añadiendo artículos deliciosos a tu menú.",
        noTables: "No se encontraron mesas",
        noTablesDesc: "Configura el diseño de tu restaurante añadiendo mesas.",
        noGroups: "Aún no hay grupos de mesas",
        noGroupsDesc: "Los grupos te ayudan a organizar tus mesas.",
        addFirstItem: "Añade tu primer artículo",
        addFirstTable: "Añade tu primer mesa",
        allGroups: "Todos los grupos",
        adminDashboard: "Panel de Administración",
        clickToEditProfile: "Clic para editar perfil",
        logout: "Cerrar sesión",
        branch: "Sucursal",
        companySettings: "Configuración de la Empresa",
        companyName: "Nombre de la Empresa",
        logo: "Logo",
        banner: "Banner",
        saveChanges: "Guardar Cambios",
        staffDirectory: "Directorio de Personal",
        portalQr: "QR del Portal",
        editStaff: "Editar Personal",
        addStaff: "Añadir Personal",
        updateStaff: "Actualizar Personal",
        activeSubscription: "Suscripción Activa",
        plan: "Plan",
        validUntil: "Válido hasta",
        securePaymentNote: "Pago seguro vía Cashfree • Cancela en cualquier momento",
        waiter: "Mesero",
        kitchenStaff: "Personal de Cocina",
        manager: "Gerente",
        chef: "Chef",
        cashier: "Cajero",
        cleaner: "Personal de Limpieza",
        otherRole: "Otro Rol",
        noStaff: "No se ha añadido personal",
        noStaffDesc: "Comienza añadiendo personal.",
        manageAttendanceTitle: "Gestionar Asistencia y Licencias",
        manageAttendanceDesc: "Ver registros detallados de asistencia, horas de trabajo y solicitudes de licencia en el panel de gestión de personal.",
        openAttendanceManager: "Abrir Gestor de Asistencia",
        selectBranch: "Seleccionar Sucursal",
        viewingDataFor: "Viendo datos de",
        filteredByBranchInfo: "Todos los artículos del menú, pedidos, ingredientes y análisis están filtrados para esta sucursal.",
        revenueTrend: "Tendencia de Ingresos",
        ordersOverview: "Resumen de Pedidos",
        revenueBreakdown: "Desglose de Ingresos",
        revenueByCategory: "Ingresos por Categoría",
        itemPerformance: "Rendimiento de Artículos",
        topSellingItems: "Artículos Más Vendidos",
        categoryPerformance: "Rendimiento por Categoría",
        topItemsDetails: "Detalles de Artículos Principale",
        customerAnalysis: "Análisis de Clientes",
        customerRetention: "Retención de Clientes",
        customerMetrics: "Métricas de Clientes",
        retentionRate: "Tasa de Retención",
        operationsAnalysis: "Análisis de Operaciones",
        hourlyOrderDistribution: "Distribución Horaria de Pedidos",
        tablePerformance: "Rendimiento de Mesas",
        tablePerformanceDetails: "Detalles de Rendimiento de Mesas",
        from: "Desde",
        to: "Hasta",
        tryAdjustingFilters: "Intente ajustar los filtros de fecha",
        noAttendanceToExport: "No hay registros de asistencia para exportar.",
        totalWorkingHours: "Total de Horas Trabajadas",
        workingHours: "Horas de Trabajo",
        workSessionDetails: "Detalles de Sesión de Trabajo",
        duration: "Duración",
        closeDetails: "Cerrar Detalles",
        noLeaveRequestsFound: "No se encontraron solicitudes de licencia",
        staffPortalQr: "QR Portal de Personal",
        enterRoleName: "Ingresar nombre del rol",
        pinPlaceholder: "PIN (6 dígitos)",
        loadingUsers: "Cargando usuarios...",
        noUsersFound: "No se encontraron usuarios",
        editUser: "Editar Usuario",
        deleteUser: "Eliminar Usuario",
        roleName: "Nombre del Rol",
        permissions: "Permisos",
        loadingRoles: "Cargando roles...",
        noRolesFound: "No se encontraron roles",
        editRole: "Editar Rol",
        deleteRole: "Eliminar Rol",
        custom: "Personalizado",
        phone: "Teléfono",
        actions: "Acciones",
        number: "Número",
        nonACDefault: "Sin AC (Por defecto)",
        saving: "Guardando...",
        qrTable: "QR para Mesa",
        print: "Imprimir",
        ordersForTable: "Pedidos para Mesa",
        order: "Pedido",
        payment: "Pago",
        description: "Descripción",
        noImage: "Sin imagen",
        addItemSub: "Añadir Nuevo Artículo",
        addTableSub: "Añadir Nueva Mesa",
        addGroupSub: "Añadir Nuevo Grupo",
        kitchenDisplay: "Pantalla de Cocina",
        yesterday: "Ayer",
        date: "Fecha",
        name: "Nombre",
        connected: "Conectado",
        disconnected: "Desconectado",
        active: "Activo",
        completed: "Completado",
        time: "Tiempo",
        allOrdersCleared: "¡Todos los pedidos completados!",
        greatJob: "Gran trabajo manteniendo el ritmo.",
        startPreparation: "Iniciar Preparación",
        markReady: "Marcar Listo",
        complete: "Completar",
        analyticsDashboard: "Panel de Analítica",
        analyticsDescription: "Información en tiempo real sobre el rendimiento de su restaurante",
        totalOrders: "Total Pedidos",
        totalRevenue: "Ingresos Totales",
        tablesServed: "Mesas Servidas",
        avgOrderValue: "Valor Medio Pedido",
        businessInsights: "Insights de Negocio",
        topPerformer: "Mejor Rendimiento",
        peakHour: "Hora Pico",
        paymentPreference: "Preferencia de Pago",
        categoryOpportunity: "Oportunidad de Categoría",
        newCustomers: "Nuevos Clientes",
        returningCustomers: "Clientes Recurrentes",
        downloadExcel: "Descargar Excel",
        downloadPDF: "Descargar PDF",
        daily: "Diario",
        weekly: "Semanal",
        monthly: "Mensual",
        yearly: "Anual",
        today: "Hoy",
        overview: "Resumen",
        revenue: "Ingresos",
        items: "Artículos",
        revenueByBranch: "Ingresos por sucursal",
        ordersByBranch: "Pedidos por sucursal",
        topItemsByBranch: "Artículos más vendidos por sucursal",
        paymentReconciliation: "Comparación de pedidos y pagos",
        last7: "Últimos 7 Días",
        operations: "Operaciones",
        customers: "Clientes",
        manageInventory: "Gestiona tu inventario y niveles de stock",
        backToAdmin: "Volver a Admin",
        addIngredient: "Añadir Ingrediente",
        totalIngredients: "Total Ingredientes",
        lowStockItems: "Artículos con Bajo Stock",
        searchIngredients: "Buscar ingredientes...",
        quantity: "Cantidad",
        unit: "Unidad",
        threshold: "Umbral",
        lowStock: "Bajo Stock",
        inStock: "En Stock",
        editIngredient: "Editar Ingrediente",
        deleteIngredientConfirm: "¿Seguro que quieres eliminar este ingrediente?",
        saveIngredient: "Guardar Ingrediente",
        lowStockThreshold: "Umbral de Bajo Stock",
        enterPin: "Ingresar PIN",
        verifying: "Verificando...",
        staffDashboard: "Panel de Personal",
        attendance: "Asistencia",
        leaves: "Licencias",
        punchIn: "Entrada",
        punchOut: "Salida",
        attendanceHistory: "Historial de Asistencia",
        allStaffAttendance: "Toda la Asistencia",
        last7Days: "Últimos 7 Días",
        thisMonth: "Este Mes",
        customRange: "Rango Personalizado",
        staffName: "Nombre Personal",
        checkIn: "Entrada",
        checkOut: "Salida",
        hours: "Horas",
        newLeaveRequest: "Nueva Solicitud",
        fromDate: "Desde",
        toDate: "Hasta",
        reason: "Razón",
        submitRequest: "Enviar Solicitud",
        allLeaveRequests: "Todas las Solicitudes",
        myLeaveHistory: "Mi Historial",
        noAttendanceRecords: "No hay registros",
        noLeaveRequests: "No hay solicitudes",
        approve: "Aprobar",
        reject: "Rechazar",
        attendanceManagement: "Gestión de Asistencia",
        todaySummary: "Resumen de Hoy",
        inTime: "Hora de Entrada",
        outTime: "Hora de Salida",
        startTime: "Hora de Inicio",
        endTime: "Hora de Fin",
        sessions: "Sesiones",
        sessionTimeline: "Línea de Tiempo de Sesiones",
        session: "Sesión",
        activeStaff: "Personal Activo",
        totalSessions: "Sesiones Totales",
        selfService: "Autoservicio",
        selfServiceItem: "Artículo de Autoservicio",
        pickupRequired: "Recogida Requerida",
        selfServiceInfo: "Por favor, recoja este artículo del mostrador después de realizar su pedido.",
        festivalOffer: "Oferta de Festival",
        festivalName: "Nombre del Festival",
        festivalDiscount: "Descuento de Festival (%)",
        festivalDesc: "Este descuento se aplicará a la factura total",
        festivalPlaceholder: "ej. Navidad, Año Nuevo",
        billingSettings: "Ajustes de Facturación y Pago",
        unlockToEdit: "Desbloquear para Editar",
        unlocked: "Desbloqueado",
        paymentSettingsLocked: "Ajustes de Pago Bloqueados",
        lockMessage: "Por razones de seguridad, sus códigos QR, ajustes de UPI y estructura de facturación están bloqueados. Por favor, desbloquee usando su contraseña de administrador.",
        unlockNow: "Desbloquear Ahora",
        sgst: "SGST (%)",
        cgst: "CGST (%)",
        upiIdLabel: "ID de UPI para Click-to-Pay",
        upiIdPlaceholder: "ej. su_nombre@okaxis",
        upiIdDesc: "Ingresar un ID de UPI habilitará los botones de GPay, PhonePe y Paytm en la factura.",
        paymentQRCodes: "Códigos QR de Pago",
        addQR: "Añadir QR",
        renamedSuccess: "Renombrado con éxito",
        renameFailed: "Fallo al renombrar",
        allBranchesFloating: "Todas las sucursales (Flotante)",
        itemNamePlaceholder: "Nombre del artículo (ej. Pollo a la parrilla)",
        itemDescriptionPlaceholder: "Breve descripción del artículo",
        tableNumberPlaceholder: "Número de mesa (Ej: 1)",
        tableNamePlaceholder: "Nombre de la mesa (Ej: Jardín 1)",
        roleTypeAdd: "Añadir Tipo de Rol",
        newCategoryName: "Nombre de Nueva Categoría",
        globalSelfService: "Autoservicio Global",
        globalSelfServiceDesc: "Activar/desactivar el autoservicio para todas las categorías a la vez",
        upgradeForAllLanguages: "Mejora a Premium para los 8 idiomas",
        subscribeForLanguages: "Suscríbete para acceder al soporte de idiomas",
        tablesUsed: "{current}/{total} mesas usadas",
        itemsUsed: "{current}/{total} artículos usados",
        noMenuItemsToExport: "No hay artículos de menú para exportar",
        noDataInFile: "No se encontraron datos en el archivo",
        itemsImportedSuccessfully: "¡{count} artículos importados con éxito!",
        manageCategories: "Gestionar Categorías",
        categoryNamePlaceholder: "Nombre de Nueva Categoría",
        upgradeForBulk: "Mejora para Carga/Descarga Masiva",
        unlockSettings: "Desbloquear Ajustes",
        enterPasswordToProceed: "Por favor, introduzca su contraseña de administrador para continuar",
        settingsUnlocked: "¡Ajustes desbloqueados!",
        invalidPassword: "Contraseña inválida",
        verificationFailed: "Verificación fallida",
        adminEmailNotFound: "Correo electrónico del administrador no encontrado",
        sendingResetLink: "Enviando enlace de restablecimiento...",
        allBranchesCombined: "Todas las sucursales",
        refreshing: "Refrescando todos los datos...",
        refreshed: "¡Todos los datos refrescados!",
        refreshFailed: "Fallo al refrescar",
        statusUpdated: "¡Estado actualizado!",
        updateFailed: "Fallo al actualizar",
        failedLoadMenu: "Fallo al cargar el menú",
        categoryAdded: "¡Categoría añadida!",
        updatedSuccessfully: "Actualizado con éxito",
        enabledForAll: "Habilitado para todos",
        disabledForAll: "Deshabilitado para todos",
        bulkUpdateFailed: "Fallo en la actualización masiva",
        enterGroupName: "Ingrese el nombre del grupo",
        deleted: "Eliminado",
        error: "Error",
        printedPopupBlocked: "Impreso (ventana emergente bloqueada)",
        qrSentToPrinter: "QR enviado a la impresora",
        printFailed: "Fallo al imprimir",
        uploadingLogo: "Subiendo logo...",
        logoUploaded: "¡Logo subido!",
        uploadingBanner: "Subiendo banner...",
        bannerUploaded: "¡Banner subido!",
        generationFailed: "Fallo al generar",
        uploadingQR: "Subiendo QR...",
        qrAdded: "¡Código QR añadido!",
        deleteStaffConfirm: "¿Está seguro de que desea eliminar a este miembro del personal?",
        deleteGroupConfirm: "¿Eliminar grupo? Las mesas en este grupo se configurarán como Sin AC.",
        deleteRoleConfirm: "¿Está seguro de que desea eliminar este rol?",
        deleteUserConfirm: "¿Está seguro de que desea eliminar este usuario?",
        deleteCategoryConfirm: "¿Eliminar categoría \"{name}\"? Los artículos en ella perderán su categoría.",
        inactive: "Inactivo",
        optionalDiscountDesc: "Opcional: Agregue un porcentaje de descuento (ej. 10 para 10% de descuento)",
        paymentSettings: "Ajustes de Pago",
        festivalOfferDesc: "Este descuento se aplicará a la factura total",
        failedResetEmail: "Fallo al enviar correo de restablecimiento",
        branchMgmt: "Gestión de Sucursales",
        branchMgmtSub: "Administre sus ubicaciones y sucursales",
        addNewBranch: "Añadir Nueva Sucursal",
        loadingBranches: "Cargando sucursales...",
        noBranchesYet: "Aún no hay sucursales",
        noBranchesDesc: "Expanda su negocio añadiendo su primera sucursal o ubicación.",
        createFirstBranch: "Crear Primera Sucursal",
        branchActiveDesc: "La sucursal está activa y aceptando pedidos",
        failedLoadBranches: "Error al cargar sucursales",
        branchSavedSuccess: "Sucursal guardada con éxito",
        failedSaveBranch: "Error al guardar sucursal",
        errorSavingBranch: "Error al guardar sucursal",
        deleteBranchConfirm: "¿Está seguro de que desea eliminar esta sucursal? Esto también afectará a los datos asociados.",
        branchDeletedSuccess: "Sucursal eliminada con éxito",
        failedDeleteBranch: "Error al eliminar sucursal",
        errorDeletingBranch: "Error al eliminar sucursal",
        createBranch: "Crear Sucursal",
        updateBranch: "Actualizar Sucursal",
        confirmDeletion: "Confirmar Eliminación",
        branchName: "Nombre de la Sucursal",
        branchNamePlaceholder: "ej., Sucursal Centro, Ubicación Aeropuerto",
        managerName: "Nombre del Gerente",
        managerNamePlaceholder: "Nombre del gerente de la sucursal",
        addressPlaceholder: "Dirección completa de la ubicación de la sucursal",
        leaveEmptyForNoBranch: "Dejar vacío para sin sucursal",
        allowedBranches: "Sucursales Permitidas",
        all: "Todo",
        unlock: "Desbloquear",
        lock: "Bloquear",
    },
    fr: {
        present: "Présent",
        noBranch: "Pas de succursale",
        price: "Prix",
        currency: "Devise",
        absent: "Absent",
        totalHours: "Heures Totales",
        avgHours: "Heures Moyennes",
        clickToViewList: "Cliquez pour voir la liste",
        loading: "Chargement...",
        search: "Rechercher...",
        save: "Enregistrer",
        cancel: "Annuler",
        delete: "Supprimer",
        edit: "Modifier",
        close: "Fermer",
        add: "Ajouter",
        submit: "Soumettre",
        home: "Accueil",
        features: "Fonctionnalités",
        pricing: "Tarifs",
        contact: "Contact",
        login: "Connexion",
        admin: "Administrateur",
        kitchen: "Cuisine",
        customer: "Client",
        analytics: "Analytique",
        ingredients: "Ingrédients",
        staff: "Personnel",
        getStarted: "Commencer",
        heroTitle: "Fini la Faim, Commandez Maintenant",
        heroSubtitle: "Le système de commande QR le plus avancé.",
        adminPanel: "Panneau d'Administration",
        manageMenu: "Gérer Menu & Tables",
        menu: "Menu",
        tables: "Tables",
        orders: "Commandes",
        menuItems: "Articles du Menu",
        addItem: "Ajouter Article",
        allCategories: "Toutes Catégories",
        noItemsFound: "Aucun article trouvé",
        tableGroups: "Groupes de Tables",
        addGroup: "Ajouter Groupe",
        addTable: "Ajouter Table",
        filterByGroup: "Filtrer par Groupe",
        allTables: "Toutes les Tables",
        nonAc: "Sans Clim",
        showOrders: "Voir Commandes",
        qr: "QR",
        menuTitle: "Menu",
        table: "Table",
        yourCart: "Votre Panier",
        placeOrder: "Commander",
        paymentMethod: "Moyen de Paiement",
        paymentStatus: "Statut de Paiement",
        cash: "Espèces",
        online: "En ligne",
        total: "Total",
        addToCart: "Ajouter",
        itemAdded: "Article ajouté au panier !",
        orderPlaced: "Commande passée avec succès !",
        orderId: "ID Commande",
        yourOrders: "Vos Commandes",
        status: "Statut",
        pending: "En attente",
        preparing: "En préparation",
        ready: "Prêt",
        delivered: "Livré",
        todaySpecialItems: "Spéciaux du jour",
        topRatedItems: "Les mieux notés",
        noSpecialItems: "Pas de spéciaux aujourd'hui. Revenez plus tard !",
        noTopRatedItems: "Aucun article bien noté disponible pour le moment.",
        nutritionalInfo: "Infos Nutritionnelles",
        calories: "Calories",
        protein: "Protéines",
        carbs: "Glucides",
        fats: "Lipides",
        vitamins: "Vitamines",
        aiRecommendation: "Remplir avec IA",
        aiDisclaimer: "Généré par IA. Veuillez vérifier.",
        generating: "Génération...",
        aiSuccess: "Généré !",
        available: "Disponible",
        unavailable: "Indisponible",
        outOfStock: "Rupture de stock",
        selectRole: "Sélectionner le rôle",
        fullName: "Nom complet",
        email: "E-mail",
        phoneNumber: "Numéro de téléphone",
        newPassword: "Nouveau mot de passe",
        passwordLabel: "Mot de passe (laisser vide pour conserver l'actuel)",
        saveUser: "Enregistrer l'utilisateur",
        confirmAction: "Confirmer l'action",
        yes: "Oui",
        no: "Non",
        supportChat: "Chat de support",
        chatWithUs: "Discutez avec nous",
        scanToAccessStaff: "Scanner pour accéder au tableau de bord du personnel",
        noOrdersForTable: "Aucune commande trouvée pour cette table.",
        groupNamePlaceholder: "Nom du groupe (ex. AC, Jardin)",
        tableNumber: "Numéro de table",
        nameOptional: "Nom (Optionnel)",
        totalAmount: "Montant total",
        trialExpiredBanner: "Votre essai gratuit a expiré. Certaines fonctionnalités peuvent être limitées.",
        trialExpiresToday: "⚠️ Votre essai gratuit expire aujourd'hui ! Mettez à niveau maintenant pour conserver l'accès.",
        trialDaysLeft: "🎉 Il vous reste {days} jours dans votre période d'accès gratuit de 20 jours.",
        subscribeNow: "S'abonner maintenant",
        upgradePlan: "Mettre à niveau le plan",
        trialExpiredTitle: "Période d'essai expirée",
        trialExpiredDesc: "Votre accès gratuit de 20 jours à <b>{name}</b> est terminé.<br/>Pour continuer à gérer votre restaurant, veuillez passer à un plan premium.",
        unlimitedOrders: "Commandes illimitées",
        detailedAnalytics: "Analytique détaillée",
        support247: "Support 24/7",
        upgradeNowToContinue: "Mettre à niveau pour continuer",
        switchBranch: "Changer de succursale",
        whyChooseTitle: "Pourquoi choisir RedSorm ?",
        mobileFirstTitle: "Mobile d'abord",
        mobileFirstDesc: "Optimisé pour tous les appareils avec un design réactif et une interface tactile",
        lightningFastTitle: "Rapide comme l'éclair",
        lightningFastDesc: "Prise de commande rapide et mises à jour en temps réel pour des opérations de restauration efficaces",
        secureReliableTitle: "Sûr et fiable",
        secureReliableDesc: "Sécurité de niveau entreprise avec paiements cryptés et protection des données",
        testimonialQuote: "RedSorm a transformé nos opérations de restauration. Les commandes sont traitées 40 % plus rapidement et nos clients adorent la commodité de commander depuis leurs tables.",
        testimonialAuthor: "Sarah Johnson",
        testimonialRole: "Propriétaire, The Garden Bistro",
        footerTagline: "Système de commande de restaurant moderne basé sur QR pour des expériences culinaires améliorées.",
        copyright: "Tous droits réservés.",
        product: "Produit",
        company: "Compagnie",
        connect: "Connecter",
        about: "À propos",
        blog: "Blog",
        careers: "Carrières",
        groups: "Groupes",
        users: "Utilisateurs",
        roles: "Rôles",
        branches: "Succursales",
        branchComparison: "Comparaison des succursales",
        settings: "Paramètres",
        welcomeTo: "Bienvenue sur",
        restaurant: "Restaurant",
        heroDescription: "Un système moderne de commande de restaurant par QR qui améliore l'expérience client et simplifie les opérations",
        customerExperience: "Expérience Client",
        customerDesc: "Commande par QR fluide, suivi en temps réel et paiements instantanés.",
        kitchenDisplayFeature: "Écran de Cuisine",
        kitchenDesc: "Gestion efficace des commandes, mises à jour d'état et suivi de préparation.",
        adminDashboardFeature: "Tableau de Bord Admin",
        adminDesc: "Gestion complète du menu, des tables et des paramètres du restaurant.",
        analyticsReports: "Analytiques & Rapports",
        analyticsDesc: "Aperçu approfondi des ventes, des articles populaires et des heures de pointe.",
        ingredientsManagement: "Gestion des Ingrédients",
        ingredientsDesc: "Suivi des stocks, gestion des niveaux et alertes de stock bas.",
        staffPortal: "Portail du Personnel",
        staffDesc: "Connexion du personnel, suivi des présences et gestion des congés.",
        viewCustomerApp: "Voir l'App Client",
        viewKitchenDisplay: "Voir l'Écran Cuisine",
        viewAdminPanel: "Voir le Panneau Admin",
        viewAnalytics: "Voir les Analytiques",
        manageIngredients: "Gérer les Ingrédients",
        staffLogin: "Connexion Personnel",
        menuEmpty: "Votre menu est vide",
        menuEmptyDesc: "Commencez par ajouter de délicieux articles à votre menu.",
        noTables: "Aucune table trouvée",
        noTablesDesc: "Configurez la disposition de votre restaurant en ajoutant des tables.",
        noGroups: "Pas encore de groupes de tables",
        noGroupsDesc: "Les groupes vous aident à organiser vos tables.",
        addFirstItem: "Ajoutez votre premier article",
        addFirstTable: "Ajoutez votre première table",
        allGroups: "Tous les groupes",
        adminDashboard: "Tableau de Bord Admin",
        clickToEditProfile: "Cliquez pour modifier le profil",
        logout: "Déconnexion",
        branch: "Succursale",
        companySettings: "Paramètres de l'entreprise",
        companyName: "Nom de l'entreprise",
        logo: "Logo",
        banner: "Bannière",
        saveChanges: "Enregistrer les modifications",
        staffDirectory: "Répertoire du personnel",
        portalQr: "QR Portail",
        editStaff: "Modifier le personnel",
        addStaff: "Ajouter du personnel",
        updateStaff: "Mettre à jour le personnel",
        activeSubscription: "Abonnement Actif",
        plan: "Plan",
        validUntil: "Valable jusqu'au",
        securePaymentNote: "Paiement sécurisé via Cashfree • Annulation à tout moment",
        waiter: "Serveur",
        kitchenStaff: "Personnel de cuisine",
        manager: "Manager",
        chef: "Chef",
        cashier: "Caissier",
        cleaner: "Nettoyeur",
        otherRole: "Autre rôle",
        noStaff: "Aucun personnel ajouté",
        noStaffDesc: "Commencez par ajouter du personnel.",
        manageAttendanceTitle: "Gérer Présence & Congés",
        manageAttendanceDesc: "Voir les journaux de présence détaillés, les heures de travail et les demandes de congés dans le tableau de bord de gestion du personnel.",
        openAttendanceManager: "Ouvrir le Gestionnaire de Présence",
        staffPortalQr: "QR Portail Personnel",
        enterRoleName: "Entrer le nom du rôle",
        pinPlaceholder: "PIN (6 chiffres)",
        loadingUsers: "Chargement des utilisateurs...",
        noUsersFound: "Aucun utilisateur trouvé",
        editUser: "Modifier l'utilisateur",
        deleteUser: "Supprimer l'utilisateur",
        roleName: "Nom du rôle",
        permissions: "Permissions",
        loadingRoles: "Chargement des rôles...",
        noRolesFound: "Aucun rôle trouvé",
        editRole: "Modifier le rôle",
        deleteRole: "Supprimer le rôle",
        custom: "Personnalisé",
        phone: "Téléphone",
        actions: "Actions",
        number: "Numéro",
        nonACDefault: "Sans Clim (Défaut)",
        saving: "Enregistrement...",
        qrTable: "QR pour la table",
        print: "Imprimer",
        ordersForTable: "Commandes pour la table",
        order: "Commande",
        payment: "Paiement",
        description: "Description",
        noImage: "Pas d'image",
        addItemSub: "Ajouter un nouvel article",
        addTableSub: "Ajouter une nouvelle table",
        addGroupSub: "Ajouter un nouveau groupe",
        kitchenDisplay: "Écran Cuisine",
        yesterday: "Hier",
        date: "Date",
        name: "Nom",
        connected: "Connecté",
        disconnected: "Déconnecté",
        active: "Actif",
        completed: "Terminé",
        time: "Temps",
        allOrdersCleared: "Toutes les commandes terminées !",
        greatJob: "Bon travail, continuez comme ça.",
        startPreparation: "Commencer Préparation",
        markReady: "Marquer Prêt",
        complete: "Terminer",
        analyticsDashboard: "Tableau de Bord Analytique",
        analyticsDescription: "Aperçu en temps réel des performances de votre restaurant",
        totalOrders: "Commandes Totales",
        totalRevenue: "Revenus Totaux",
        tablesServed: "Tables Servies",
        avgOrderValue: "Valeur Moyenne Commande",
        businessInsights: "Informations Commerciales",
        topPerformer: "Meilleure Vente",
        peakHour: "Heure de Pointe",
        paymentPreference: "Préférence Paiement",
        categoryOpportunity: "Opportunité Catégorie",
        newCustomers: "Nouveaux Clients",
        returningCustomers: "Clients Fidèles",
        downloadExcel: "Télécharger Excel",
        downloadPDF: "Télécharger PDF",
        daily: "Quotidien",
        weekly: "Hebdomadaire",
        monthly: "Mensuel",
        yearly: "Annuel",
        today: "Aujourd'hui",
        overview: "Vue d'ensemble",
        revenue: "Revenus",
        items: "Articles",
        revenueByBranch: "Chiffre d'affaires par succursale",
        ordersByBranch: "Commandes par succursale",
        topItemsByBranch: "Articles les plus vendus par succursale",
        paymentReconciliation: "Comparaison des commandes et des paiements",
        last7: "7 Derniers Jours",
        operations: "Opérations",
        customers: "Clients",
        manageInventory: "Gérer votre inventaire et niveaux de stock",
        backToAdmin: "Retour Admin",
        addIngredient: "Ajouter Ingrédient",
        totalIngredients: "Total Ingrédients",
        lowStockItems: "Stock Faible",
        searchIngredients: "Rechercher...",
        quantity: "Quantité",
        unit: "Unité",
        threshold: "Seuil",
        lowStock: "Stock Faible",
        inStock: "En Stock",
        editIngredient: "Modifier Ingrédient",
        deleteIngredientConfirm: "Êtes-vous sûr de vouloir supprimer cet ingrédient ?",
        saveIngredient: "Enregistrer Ingrédient",
        lowStockThreshold: "Seuil Stock Faible",
        enterPin: "Entrer PIN",
        verifying: "Vérification...",
        staffDashboard: "Tableau Personnel",
        attendance: "Présence",
        leaves: "Congés",
        punchIn: "Pointeuse Entrée",
        punchOut: "Pointeuse Sortie",
        attendanceHistory: "Historique Présence",
        allStaffAttendance: "Toute la Présence",
        last7Days: "7 Derniers Jours",
        thisMonth: "Ce Mois",
        customRange: "Personnalisé",
        staffName: "Nom Personnel",
        checkIn: "Entrée",
        checkOut: "Sortie",
        hours: "Heures",
        newLeaveRequest: "Nouvelle Demande",
        fromDate: "Du",
        toDate: "Au",
        reason: "Raison",
        submitRequest: "Soumettre",
        allLeaveRequests: "Toutes Demandes",
        myLeaveHistory: "Mon Historique",
        noAttendanceRecords: "Aucun enregistrement",
        noLeaveRequests: "Aucune demande",
        approve: "Approuver",
        reject: "Rejeter",
        allBranchesCombined: "Toutes les succursales",
        firstIn: "Première Entrée",
        lastOut: "Dernière Sortie",
        dailyRate: "Taux Journalier",
        download: "Télécharger",
        refresh: "Actualiser",
        attendanceManagement: "Gestion des Présences",
        selectBranch: "Sélectionner la Succursale",
        viewingDataFor: "Affichage des données pour",
        filteredByBranchInfo: "Tous les articles du menu, les commandes, les ingrédients et les analyses sont filtrés pour cette succursale.",
        revenueTrend: "Tendance des Revenus",
        ordersOverview: "Aperçu des Commandes",
        revenueBreakdown: "Répartition des Revenus",
        revenueByCategory: "Revenus par Catégorie",
        itemPerformance: "Performance des Articles",
        topSellingItems: "Articles les plus Vendus",
        categoryPerformance: "Performance des Catégories",
        topItemsDetails: "Détails des Articles Principaux",
        customerAnalysis: "Analyse Clients",
        customerRetention: "Rétention Clients",
        customerMetrics: "Mesures Clients",
        retentionRate: "Taux de Rétention",
        operationsAnalysis: "Analyse des Opérations",
        hourlyOrderDistribution: "Distribution Horaire des Commandes",
        tablePerformance: "Performance des Tables",
        tablePerformanceDetails: "Détails de Performance des Tables",
        from: "De",
        to: "À",
        tryAdjustingFilters: "Essayez d'ajuster les filtres de date",
        noAttendanceToExport: "Aucun relevé de présence à exporter.",
        totalWorkingHours: "Total d'Heures de Travail",
        workingHours: "Heures de Travail",
        workSessionDetails: "Détails de la Session de Travail",
        duration: "Durée",
        closeDetails: "Fermer les Détails",
        noLeaveRequestsFound: "Aucune demande de congé trouvée",
        refreshing: "Actualisation de toutes les données...",
        refreshed: "Toutes les données sont actualisées !",
        refreshFailed: "Échec de l'actualisation",
        statusUpdated: "Statut mis à jour !",
        updateFailed: "Échec de la mise à jour",
        failedLoadMenu: "Échec du chargement du menu",
        categoryAdded: "Catégorie ajoutée !",
        updatedSuccessfully: "Mis à jour avec succès",
        enabledForAll: "Activé pour tous",
        disabledForAll: "Désactivé pour tous",
        bulkUpdateFailed: "Échec de la mise à jour massive",
        enterGroupName: "Entrez le nom du groupe",
        deleted: "Supprimé",
        error: "Erreur",
        printedPopupBlocked: "Imprimé (fenêtre contextuelle bloquée)",
        qrSentToPrinter: "QR envoyé à l'imprimante",
        printFailed: "Échec de l'impression",
        uploadingLogo: "Téléchargement du logo...",
        logoUploaded: "Logo téléchargé !",
        uploadingBanner: "Téléchargement de la bannière...",
        bannerUploaded: "Bannière téléchargée !",
        generationFailed: "Échec de la génération",
        uploadingQR: "Téléchargement du QR...",
        qrAdded: "Code QR ajouté !",
        deleteStaffConfirm: "Êtes-vous sûr de vouloir supprimer ce membre du personnel ?",
        deleteGroupConfirm: "Supprimer le groupe ? Les tables de ce groupe seront définies sur Sans AC.",
        deleteRoleConfirm: "Êtes-vous sûr de vouloir supprimer ce rôle ?",
        deleteUserConfirm: "Êtes-vous sûr de vouloir supprimer cet utilisateur ?",
        deleteCategoryConfirm: "Supprimer la catégorie \"{name}\" ? Les articles qu'elle contient perdront leur catégorie.",
        inactive: "Inactif",
        upiIdDesc: "La saisie d'un identifiant UPI activera les boutons GPay, PhonePe et Paytm sur la facture.",
        optionalDiscountDesc: "Facultatif : ajoutez un pourcentage de remise (par exemple, 10 pour 10 % de remise)",
        paymentSettings: "Paramètres de paiement",
        festivalOfferDesc: "Cette remise sera appliquée sur la facture globale",
        failedResetEmail: "Échec de l'envoi de l'e-mail de réinitialisation",

    },
    hi: {
        present: "उपस्थित",
        noBranch: "कोई शाखा नहीं",
        price: "कीमत",
        currency: "मुद्रा",
        absent: "अनुपस्थित",
        totalHours: "कुल घंटे",
        avgHours: "औसत घंटे",
        clickToViewList: "सूची देखने के लिए क्लिक करें",
        loading: "लोड हो रहा है...",
        search: "खोजें...",
        save: "सहेजें",
        cancel: "रद्द करें",
        delete: "हटाएं",
        edit: "संपादित करें",
        close: "बंद करें",
        add: "जोड़ें",
        submit: "जमा करें",
        home: "होम",
        features: "विशेषताएं",
        pricing: "मूल्य निर्धारण",
        contact: "संपर्क",
        login: "लॉग इन",
        admin: "एडमिन",
        kitchen: "रसोई",
        customer: "ग्राहक",
        analytics: "एनालिटिक्स",
        ingredients: "सामग्री",
        getStarted: "शुरू करें",
        heroTitle: "भूख मिटाएं, ऑर्डर शुरू करें",
        heroSubtitle: "सबसे उन्नत क्यूआर-आधारित ऑर्डरिंग सिस्टम।",
        adminPanel: "एडमिन पैनल",
        manageMenu: "मेनू और टेबल प्रबंधित करें",
        menu: "मेनू",
        tables: "टेबल्स",
        orders: "ऑर्डर",
        menuItems: "मेनू आइटम",
        addItem: "आइटम जोड़ें",
        allCategories: "सभी श्रेणियां",
        noItemsFound: "कोई आइटम नहीं मिला",
        tableGroups: "टेबल समूह",
        addGroup: "समूह जोड़ें",
        addTable: "टेबल जोड़ें",
        filterByGroup: "समूह द्वारा फ़िल्टर करें",
        allTables: "सभी टेबल्स",
        nonAc: "नॉन एसी",
        showOrders: "ऑर्डर देखें",
        menuTitle: "मेनू",
        table: "टेबल",
        yourCart: "आपकी कार्ट",
        placeOrder: "ऑर्डर दें",
        paymentMethod: "भुगतान का तरीका",
        paymentStatus: "भुगतान की स्थिति",
        cash: "नकद",
        online: "ऑनलाइन",
        total: "कुल",
        addToCart: "जोड़ें",
        itemAdded: "आइटम कार्ट में जोड़ा गया!",
        orderPlaced: "ऑर्डर सफलतापूर्वक दिया गया!",
        orderId: "ऑर्डर आईडी",
        yourOrders: "आपके ऑर्डर",
        status: "स्थिति",
        pending: "लंबित",
        todaySpecialItems: "आज के विशेष व्यंजन",
        topRatedItems: "टॉप रेटेड व्यंजन",
        noSpecialItems: "आज कोई विशेष व्यंजन नहीं हैं। बाद में फिर से जांचें!",
        noTopRatedItems: "अभी कोई टॉप रेटेड व्यंजन उपलब्ध नहीं हैं।",
        nutritionalInfo: "पोषण संबंधी जानकारी",
        calories: "कैलोरी",
        protein: "प्रोटीन",
        carbs: "कार्ब्स",
        fats: "वसा",
        vitamins: "विटामिन",
        aiRecommendation: "AI से भरें",
        aiDisclaimer: "AI द्वारा जनरेट किया गया। कृपया जांचें।",
        generating: "जनरेट हो रहा है...",
        aiSuccess: "जनरेट हो गया!",
        available: "उपलब्ध",
        outOfStock: "स्टॉक में नहीं",
        selectRole: "भूमिका चुनें",
        fullName: "पूरा नाम",
        email: "ईमेल",
        phoneNumber: "फ़ोन नंबर",
        newPassword: "नया पासवर्ड",
        passwordLabel: "पासवर्ड (वर्तमान रखने के लिए खाली छोड़ दें)",
        saveUser: "उपयोगकर्ता सहेजें",
        supportChat: "सहायతా चैट",
        chatWithUs: "हमसे चैट करें",
        scanToAccessStaff: "स्टाफ डैशबोर्ड तक पहुंचने के लिए स्कैन करें",
        noOrdersForTable: "इस टेबल के लिए कोई ऑर्डर नहीं मिला।",
        groupNamePlaceholder: "समूह का नाम (जैसे, एसी, गार्डन)",
        tableNumber: "टेबल नंबर",
        nameOptional: "नाम (वैकल्पिक)",
        totalAmount: "कुल राशि",
        trialExpiredBanner: "आपका फ्री ट्रायल खत्म हो गया है। कुछ सुविधाएं लिमिटेड हो सकती हैं।",
        trialExpiresToday: "⚠️ आपका फ्री ट्रायल आज खत्म हो रहा है! एक्सेस बनाए रखने के लिए अभी अपग्रेड करें।",
        trialDaysLeft: "🎉 आपके 20-दिवसीय फ्री एक्सेस पीरियड में {days} दिन बचे हैं।",
        subscribeNow: "अभी सब्सक्राइब करें",
        upgradePlan: "प्लान अपग्रेड करें",
        trialExpiredTitle: "ट्रायल पीरियड समाप्त",
        trialExpiredDesc: "<b>{name}</b> के लिए आपका 20-दिवसीय फ्री एक्सेस समाप्त हो गया है।<br/>अपने रेस्टोरेंट का प्रबंधन जारी रखने के लिए, कृपया प्रीमियम प्लान में अपग्रेड करें।",
        unlimitedOrders: "असीमित ऑर्डर",
        detailedAnalytics: "विस्तृत एनालिटिक्स",
        support247: "24/7 सपोर्ट",
        upgradeNowToContinue: "जारी रखने के लिए अभी अपग्रेड करें",
        switchBranch: "शाखा बदलें",
        whyChooseTitle: "RedSorm को क्यों चुनें?",
        mobileFirstTitle: "मोबाइल फर्स्ट",
        mobileFirstDesc: "रिस्पॉन्सिव डिज़ाइन और टच-फ्रेंडली इंटरफेस के साथ सभी डिवाइस के लिए ऑप्टिमाइज़",
        lightningFastTitle: "बिजली की तरह तेज",
        lightningFastDesc: "कुशल रेस्टोरेंट संचालन के लिए त्वरित ऑर्डर प्लेसमेंट और रीयल-टाइम अपडेट",
        secureReliableTitle: "सुरक्षित और विश्वसनीय",
        secureReliableDesc: "एन्क्रिप्टेड भुगतान और डेटा सुरक्षा के साथ एंटरप्राइज-ग्रेड सिक्योरिटी",
        testimonialQuote: "RedSorm ने हमारे रेस्टोरेंट के संचालन को बदल दिया। ऑर्डर 40% तेजी से प्रोसेस होते हैं, और हमारे ग्राहकों को अपनी टेबल से ऑर्डर करने की सुविधा पसंद आती है।",
        testimonialAuthor: "सारा जॉनसन",
        testimonialRole: "मालिक, द गार्डन बिस्ट्रो",
        footerTagline: "बेहतर डाइनिंग अनुभवों के लिए आधुनिक क्यूआर-आधारित रेस्टोरेंट ऑर्डरिंग सिस्टम।",
        copyright: "सर्वाधिकार सुरक्षित।",
        product: "उत्पाद",
        company: "कंपनी",
        connect: "कनेक्ट",
        about: "हमारे बारे में",
        blog: "ब्लॉग",
        careers: "करियर",
        adminDashboard: "एडमिन डैशबोर्ड",
        clickToEditProfile: "प्रोफ़ाइल संपादित करने के लिए क्लिक करें",
        logout: "लॉग आउट",
        groups: "समूह",
        staff: "स्टाफ",
        users: "उपयोगकर्ता",
        roles: "भूमिकाएं",
        branches: "शाखाएं",
        branchComparison: "शाखा तुलना",
        menuEmpty: "आपका मेनू खाली है",
        menuEmptyDesc: "अपने मेनू में स्वादिष्ट आइटम जोड़कर शुरुआत करें। आप उन्हें श्रेणियों में व्यवस्थित कर सकते हैं और पोषण संबंधी जानकारी उत्पन्न करने के लिए AI का उपयोग भी कर सकते हैं!",
        noTables: "कोई टेबल नहीं मिली",
        noTablesDesc: "टेबल जोड़कर अपना रेस्टोरेंट लेआउट सेटअप करें। आप उन्हें बाद में समूहों (जैसे एसी, गार्डन) को सौंप सकते हैं।",
        noGroups: "अभी तक कोई टेबल समूह नहीं",
        noGroupsDesc: "समूह आपको अपनी टेबल व्यवस्थित करने में मदद करते हैं (जैसे, 'एसी सेक्शन', 'आउटडोर', 'पहली मंजिल')।",
        companySettings: "कंपनी सेटिंग्स",
        companyName: "कंपनी का नाम",
        logo: "लोगो",
        banner: "बैनर",
        saveChanges: "परिवर्तन सहेजें",
        staffDirectory: "स्टाफ डायरेक्टरी",
        portalQr: "पोर्टल क्यूआर",
        editStaff: "स्टाफ संपादित करें",
        addStaff: "स्टाफ जोड़ें",
        updateStaff: "स्टाफ अपडेट करें",
        welcomeTo: "स्वागत है",
        restaurant: "रेस्टोरेंट",
        heroDescription: "एक आधुनिक क्यूआर-आधारित रेस्टोरेंट ऑर्डरिंग सिस्टम जो ग्राहक अनुभव को बढ़ाता है और संचालन को सुव्यवस्थित करता है",
        customerExperience: "ग्राहक अनुभव",
        customerDesc: "निर्बाध क्यूआर-आधारित ऑर्डरिंग, रीयल-टाइम ट्रैकिंग और तत्काल भुगतान।",
        kitchenDisplayFeature: "किचन डिस्प्ले",
        kitchenDesc: "कुशल ऑर्डर प्रबंधन, स्थिति अद्यतन और तैयारी ट्रैकिंग।",
        adminDashboardFeature: "एडमिन डैशबोर्ड",
        adminDesc: "मेनू, टेबल और रेस्टोरेंट सेटिंग्स का व्यापक प्रबंधन।",
        analyticsReports: "एनालिटिक्स और रिपोर्ट्स",
        analyticsDesc: "बिक्री, लोकप्रिय आइटम और पीक आवर्स में गहरी अंतर्दृष्टि।",
        ingredientsDesc: "इन्वेंट्री ट्रैक करें, स्टॉक स्तर प्रबंधित करें और कम स्टॉक अलर्ट प्राप्त करें।",
        staffDesc: "स्टाफ लॉगिन, उपस्थिति ट्रैकिंग और छुट्टी प्रबंधन।",
        viewCustomerApp: "ग्राहक ऐप देखें",
        viewKitchenDisplay: "किचन डिस्प्ले देखें",
        viewAdminPanel: "एडमिन पैनल देखें",
        viewAnalytics: "एनालिटिक्स देखें",
        manageIngredients: "सामग्री प्रबंधित करें",
        staffLogin: "स्टाफ लॉगिन",
        activeSubscription: "सक्रिय सदस्यता",
        plan: "प्लान",
        validUntil: "तक मान्य",
        securePaymentNote: "Cashfree के माध्यम से सुरक्षित भुगतान • कभी भी रद्द करें",
        settings: "सेटिंग्स",
        addFirstItem: "अपना पहला आइटम जोड़ें",
        addFirstTable: "अपनी पहली टेबल जोड़ें",
        allGroups: "सभी समूह",
        waiter: "वेटर",
        kitchenStaff: "किचन स्टाफ",
        manager: "मैनेजर",
        chef: "शेफ",
        cashier: "कैशियर",
        cleaner: "क्लीनर",
        otherRole: "अन्य भूमिका",
        noStaff: "अभी तक कोई स्टाफ नहीं जोड़ा गया",
        noStaffDesc: "स्टाफ जोड़कर शुरुआत करें।",
        staffPortalQr: "स्टाफ पोर्टल क्यूआर",
        enterRoleName: "भूमिका का नाम दर्ज करें",
        pinPlaceholder: "पिन (6 अंक)",
        loadingUsers: "उपयोगकर्ताओं को लोड किया जा रहा है...",
        noUsersFound: "कोई उपयोगकर्ता नहीं मिला",
        editUser: "उपयोगकर्ता संपादित करें",
        deleteUser: "उपयोगकर्ता हटाएं",
        roleName: "भूमिका का नाम",
        permissions: "अनुमतियाँ",
        loadingRoles: "भूमिकाएं लोड हो रही हैं...",
        noRolesFound: "कोई भूमिका नहीं मिली",
        editRole: "भूमिका संपादित करें",
        deleteRole: "भूमिका हटाएं",
        custom: "कस्टम",
        phone: "फ़ोन",
        actions: "क्रियाएं",
        number: "नंबर",
        qrTable: "टेबल के लिए क्यूआर",
        print: "प्रिंट",
        ordersForTable: "टेबल के लिए ऑर्डर",
        order: "ऑर्डर",
        payment: "भुगतान",
        description: "विवरण",
        noImage: "कोई छवि नहीं",
        addItemSub: "नया आइटम जोड़ें",
        addTableSub: "नई टेबल जोड़ें",
        addGroupSub: "नया समूह जोड़ें",
        kitchenDisplay: "किचन डिस्प्ले",
        preparing: "तैयारी",
        ready: "तैयार",
        delivered: "वितरित",
        yesterday: "कल",
        date: "दिनांक",
        name: "नाम",
        connected: "कनेक्टेड",
        disconnected: "डिस्कनेक्टेड",
        completed: "पूर्ण",
        time: "समय",
        allOrdersCleared: "सभी ऑर्डर साफ़ हो गए!",
        greatJob: "बहुत बढ़िया काम।",
        startPreparation: "तैयारी शुरू करें",
        markReady: "तैयार चिह्नित करें",
        complete: "पूर्ण",
        analyticsDashboard: "एनालिटिक्स डैशबोर्ड",
        analyticsDescription: "आपके रेस्तरां प्रदर्शन की रीयल-टाइम अंतर्दृष्टि",
        totalOrders: "कुल ऑर्डर",
        totalRevenue: "कुल राजस्व",
        tablesServed: "टेबल सेवित",
        avgOrderValue: "औसत ऑर्डर मूल्य",
        businessInsights: "व्यावसायिक अंतर्दृष्टि",
        topPerformer: "शीर्ष प्रदर्शनकर्ता",
        peakHour: "पीक आवर",
        paymentPreference: "भुगतान प्राथमिकता",
        categoryOpportunity: "श्रेणी अवसर",
        newCustomers: "नए ग्राहक",
        returningCustomers: "लौटने वाले ग्राहक",
        downloadExcel: "एक्सेल डाउनलोड करें",
        downloadPDF: "पीडीएफ डाउनलोड करें",
        daily: "दैनिक",
        weekly: "साप्ताहिक",
        monthly: "मासिक",
        yearly: "वार्षिक",
        today: "आज",
        overview: "अवलोकन",
        revenue: "राजस्व",
        items: "आइटम",
        revenueByBranch: "शाखा के अनुसार राजस्व",
        ordersByBranch: "शाखा के अनुसार ऑर्डर",
        topItemsByBranch: "शाखा के अनुसार शीर्ष आइटम",
        paymentReconciliation: "ऑर्डर और भुगतान तुलना",
        last7: "पिछले 7 दिन",
        operations: "संचालन",
        customers: "ग्राहक",
        ingredientsManagement: "सामग्री प्रबंधन",
        manageInventory: "अपनी इन्वेंट्री और स्टॉक स्तर प्रबंधित करें",
        backToAdmin: "एडमिन पर वापस जाएं",
        addIngredient: "सामग्री जोड़ें",
        totalIngredients: "कुल सामग्री",
        lowStockItems: "कम स्टॉक वाले आइटम",
        searchIngredients: "सामग्री खोजें...",
        quantity: "मात्रा",
        unit: "इकाई",
        threshold: "थ्रेशोल्ड",
        lowStock: "कम स्टॉक",
        inStock: "स्टॉक में",
        editIngredient: "सामग्री संपादित करें",
        deleteIngredientConfirm: "क्या आप वाकई इस सामग्री को हटाना चाहते हैं?",
        saveIngredient: "सामग्री सहेजें",
        lowStockThreshold: "कम स्टॉक थ्रेशोल्ड",
        staffPortal: "स्टाफ पोर्टल",
        enterPin: "पिन दर्ज करें",
        verifying: "सत्यापन हो रहा है...",
        staffDashboard: "स्टाफ डैशबोर्ड",
        attendance: "उपस्थिति",
        leaves: "छुत्तियां",
        punchIn: "पंच इन",
        punchOut: "पंच आउट",
        attendanceHistory: "उपस्थिति इतिहास",
        allStaffAttendance: "सभी स्टाफ उपस्थिति",
        last7Days: "पिछले 7 दिन",
        thisMonth: "इस महीने",
        customRange: "कस्टम रेंज",
        staffName: "स्टाफ का नाम",
        checkIn: "इन",
        checkOut: "आउट",
        hours: "घंटे",
        newLeaveRequest: "नई छुट्टी अनुरोध",
        fromDate: "से",
        toDate: "तक",
        reason: "कारण",
        submitRequest: "अनुरोध जमा करें",
        allLeaveRequests: "सभी छुट्टी अनुरोध",
        myLeaveHistory: "मेरा छुट्टी इतिहास",
        noAttendanceRecords: "कोई उपस्थिति रिकॉर्ड नहीं मिला",
        noLeaveRequests: "कोई छुट्टी अनुरोध नहीं मिला",
        approve: "स्वीकृत करें",
        reject: "अस्वीकार करें",
        firstIn: "पहली एंट्री",
        lastOut: "अंतिम आउट",
        dailyRate: "दैनिक दर",
        download: "डाउनलोड",
        refresh: "रीफ्रेश",
        manageAttendanceTitle: "उपस्थिति और छुट्टी प्रबंधित करें",
        attendanceManagement: "उपस्थिति प्रबंधन",
        manageAttendanceDesc: "स्टाफ प्रबंधन डैशबोर्ड में विस्तृत उपस्थिति लॉग, काम के घंटे और छुट्टी के अनुरोध देखें।",
        openAttendanceManager: "अटेंडेंस मैनेजर खोलें",
        selectBranch: "शाखा चुनें",
        viewingDataFor: "डेटा देख रहे हैं",
        filteredByBranchInfo: "सभी मेनू आइटम, ऑर्डर, सामग्री और एनालिटिक्स इस शाखा के लिए फ़िल्टर किए गए हैं।",
        revenueTrend: "राजस्व रुझान",
        ordersOverview: "ऑर्डर अवलोकन",
        revenueBreakdown: "राजस्व विवरण",
        revenueByCategory: "श्रेणी के अनुसार राजस्व",
        itemPerformance: "आइटम प्रदर्शन",
        topSellingItems: "सबसे ज्यादा बिकने वाले आइटम",
        categoryPerformance: "श्रेणी प्रदर्शन",
        topItemsDetails: "शीर्ष आइटम विवरण",
        customerAnalysis: "ग्राहक विश्लेषण",
        customerRetention: "ग्राहक प्रतिधारण",
        customerMetrics: "ग्राहक मेट्रिक्स",
        retentionRate: "प्रतिधारण दर",
        operationsAnalysis: "संचालन विश्लेषण",
        hourlyOrderDistribution: "प्रति घंटा ऑर्डर वितरण",
        tablePerformance: "टेबल प्रदर्शन",
        tablePerformanceDetails: "टेबल प्रदर्शन विवरण",
        from: "से",
        to: "तक",
        tryAdjustingFilters: "दिनांक फ़िल्टर को समायोजित करने का प्रयास करें",
        noAttendanceToExport: "निर्यात करने के लिए कोई उपस्थिति रिकॉर्ड नहीं है।",
        totalWorkingHours: "काम के कुल घंटे",
        workingHours: "काम के घंटे",
        workSessionDetails: "काम के सत्र का विवरण",
        duration: "अवधि",
        closeDetails: "विवरण बंद करें",
        allBranchesCombined: "सभी शाखाएं",
        branchMgmt: "शाखा प्रबंधन",
        branchMgmtSub: "अपने स्थान और शाखाएँ प्रबंधित करें",
        addNewBranch: "नई शाखा जोड़ें",
        loadingBranches: "शाखाएँ लोड हो रही हैं...",
        noBranchesYet: "अभी तक कोई शाखा नहीं है",
        noBranchesDesc: "अपनी पहली शाखा या स्थान जोड़कर अपने व्यवसाय का विस्तार करें।",
        createFirstBranch: "पहली शाखा बनाएँ",
        branchActiveDesc: "शाखा सक्रिय है और ऑर्डर स्वीकार कर रही है",
        failedLoadBranches: "शाखाएँ लोड करने में विफल",
        branchSavedSuccess: "शाखा सफलतापूर्वक सहेजी गई",
        failedSaveBranch: "शाखा सहेजने में विफल",
        errorSavingBranch: "शाखा सहेजने में त्रुटि",
        deleteBranchConfirm: "क्या आप वाकई इस शाखा को हटाना चाहते हैं? यह संबंधित डेटा को भी प्रभावित करेगा।",
        branchDeletedSuccess: "शाखा सफलतापूर्वक हटा दी गई",
        failedDeleteBranch: "शाखा हटाने में विफल",
        errorDeletingBranch: "शाखा हटाने में त्रुटि",
        createBranch: "शाखा बनाएँ",
        updateBranch: "शाखा अपडेट करें",
        confirmDeletion: "हटाने की पुष्टि करें",
        branchName: "शाखा का नाम",
        branchNamePlaceholder: "जैसे, डाउनटाउन शाखा, हवाई अड्डा स्थान",
        managerName: "प्रबंधक का नाम",
        managerNamePlaceholder: "शाखा प्रबंधक का नाम",
        addressPlaceholder: "शाखा स्थान का पूरा पता",
        unavailable: "अनुपलब्ध",
        leaveEmptyForNoBranch: "कोई शाखा नहीं के लिए खाली छोड़ें",
        allowedBranches: "अनुमत शाखाएँ",
        all: "सभी",
        branch: "शाखा",
        nonACDefault: "नॉन एसी (डिफ़ॉल्ट)",
        unlock: "अनलॉक करें",
        lock: "लॉक करें",
        refreshing: "सभी डेटा रिफ्रेश किया जा रहा है...",
        refreshed: "सभी डेटा रिफ्रेश हो गया!",
        refreshFailed: "रिफ्रेश विफल रहा",
        statusUpdated: "स्टेटस अपडेट हो गया!",
        updateFailed: "अपडेट विफल रहा",
        failedLoadMenu: "मेन्यू लोड करने में विफल",
        categoryAdded: "श्रेणी जोड़ी गई!",
        updatedSuccessfully: "सफलतापूर्वक अपडेट किया गया",
        enabledForAll: "सभी के लिए सक्षम",
        disabledForAll: "सभी के लिए अक्षम",
        bulkUpdateFailed: "थोक अपडेट विफल रहा",
        enterGroupName: "समूह का नाम दर्ज करें",
        deleted: "हटा दिया गया",
        error: "त्रुटि",
        printedPopupBlocked: "मुद्रित (पॉपअप अवरुद्ध)",
        qrSentToPrinter: "QR प्रिंटर पर भेजा गया",
        printFailed: "प्रिंट विफल रहा",
        uploadingLogo: "लोगो अपलोड किया जा रहा है...",
        logoUploaded: "लोगो अपलोड हो गया!",
        uploadingBanner: "बैनर अपलोड किया जा रहा है...",
        bannerUploaded: "बैनर अपलोड हो गया!",
        generationFailed: "जेनरेशन विफल रहा",
        uploadingQR: "QR अपलोड किया जा रहा है...",
        qrAdded: "QR कोड जोड़ा गया!",
        deleteStaffConfirm: "क्या आप वाकई इस स्टाफ सदस्य को हटाना चाहते हैं?",
        deleteGroupConfirm: "समूह हटाएं? इस समूह की मेजों को Non AC पर सेट कर दिया जाएगा।",
        deleteRoleConfirm: "क्या आप वाकई इस भूमिका को हटाना चाहते हैं?",
        deleteUserConfirm: "क्या आप वाकई इस उपयोगकर्ता को हटाना चाहते हैं?",
        deleteCategoryConfirm: "श्रेणी \"{name}\" हटाएं? इसमें मौजूद आइटम अपनी श्रेणी खो देंगे।",
        active: "सक्रिय",
        inactive: "निष्क्रिय",
        upiIdDesc: "UPI ID दर्ज करने से बिल पर GPay, PhonePe और Paytm बटन सक्षम हो जाएंगे।",
        optionalDiscountDesc: "वैकల్పिक: छूट प्रतिशत जोड़ें (जैसे, 10% छूट के लिए 10)",
        paymentSettings: "भुगतान सेटिंग्स",
        festivalOfferDesc: "यह छूट कुल बिल पर लागू की जाएगी",
        yes: "हाँ",
        no: "नहीं",
        confirmAction: "कार्रवाई की पुष्टि करें",
        failedResetEmail: "पासवर्ड रीसेट ईमेल भेजने में विफल",
        saving: "सहेजा जा रहा है...",
        qr: "QR",
    },


    zh: {
        present: "出勤",
        noBranch: "无分店",
        price: "价格",
        currency: "货币",
        absent: "缺勤",
        totalHours: "总小时数",
        avgHours: "平均小时数",
        clickToViewList: "点击查看列表",
        loading: "加载中...",
        search: "搜索...",
        save: "保存",
        cancel: "取消",
        delete: "删除",
        edit: "编辑",
        close: "关闭",
        add: "添加",
        submit: "提交",
        home: "首页",
        features: "功能",
        pricing: "价格",
        contact: "联系我们",
        login: "登录",
        admin: "管理员",
        kitchen: "厨房",
        customer: "客户",
        analytics: "分析",
        ingredients: "配料",
        staff: "员工",
        getStarted: "开始使用",
        heroTitle: "终结饥饿，开始点餐",
        heroSubtitle: "最先进的二维码点餐系统。",
        adminPanel: "管理面板",
        manageMenu: "管理菜单和餐桌",
        menu: "菜单",
        tables: "餐桌",
        orders: "订单",
        menuItems: "菜单项",
        addItem: "添加项目",
        allCategories: "所有分类",
        noItemsFound: "未找到项目",
        tableGroups: "餐桌组",
        addGroup: "添加组",
        addTable: "添加餐桌",
        filterByGroup: "按组筛选",
        allTables: "所有餐桌",
        nonAc: "非空调",
        showOrders: "显示订单",
        qr: "二维码",
        menuTitle: "菜单",
        table: "餐桌",
        yourCart: "您的购物车",
        placeOrder: "下单",
        paymentMethod: "支付方式",
        paymentStatus: "支付状态",
        cash: "现金",
        online: "在线支付",
        total: "总计",
        addToCart: "添加",
        itemAdded: "已添加到购物车！",
        orderPlaced: "下单成功！",
        orderId: "订单编号",
        yourOrders: "您的订单",
        status: "状态",
        pending: "待处理",
        preparing: "准备中",
        ready: "就绪",
        delivered: "已送达",
        nutritionalInfo: "营养信息",
        calories: "卡路里",
        protein: "蛋白质",
        carbs: "碳水化合物",
        fats: "脂肪",
        vitamins: "维生素",
        aiRecommendation: "AI 自动填充",
        aiDisclaimer: "由 AI 生成。请核实。",
        generating: "生成中...",
        aiSuccess: "已生成！",
        available: "可用",
        unavailable: "不可用",
        outOfStock: "缺货",
        selectRole: "选择角色",
        fullName: "全名",
        email: "电子邮件",
        phoneNumber: "电话号码",
        newPassword: "新密码",
        passwordLabel: "密码（留空则保持不变）",
        saveUser: "保存用户",
        confirmAction: "确认操作",
        yes: "是",
        no: "否",
        supportChat: "支持聊天",
        chatWithUs: "与我们聊天",
        scanToAccessStaff: "扫描以访问员工仪表板",
        noOrdersForTable: "未找到该餐桌的订单。",
        groupNamePlaceholder: "组名（例如：空调房、花园）",
        tableNumber: "桌号",
        nameOptional: "名称（可选）",
        totalAmount: "总金额",
        trialExpiredBanner: "您的免费试用已过期。某些功能可能会受到限制。",
        trialExpiresToday: "⚠️ 您的免费试用今日到期！立即升级以保持访问权限。",
        trialDaysLeft: "🎉 您的 20 天免费访问期还剩 {days} 天。",
        subscribeNow: "立即订阅",
        upgradePlan: "升级方案",
        trialExpiredTitle: "试用期已过",
        trialExpiredDesc: "您对 <b>{name}</b> 的 20 天免费访问已结束。<br/>要继续管理您的餐厅，请升级至高级方案。",
        unlimitedOrders: "无限订单",
        detailedAnalytics: "详细分析",
        support247: "24/7 支持",
        upgradeNowToContinue: "立即升级以继续",
        switchBranch: "切换分店",
        whyChooseTitle: "为什么选择 RedSorm？",
        mobileFirstTitle: "移动优先",
        mobileFirstDesc: "采用响应式设计和触控友好界面，针对所有设备进行了优化",
        lightningFastTitle: "极速体验",
        lightningFastDesc: "快速下单和实时更新，实现高效的餐厅运营",
        secureReliableTitle: "安全可靠",
        secureReliableDesc: "具有加密支付和数据保护的企业级安全性",
        testimonialQuote: "RedSorm 改变了我们餐厅的运营。订单处理速度提高了 40%，我们的客户非常喜欢在桌位上点餐的便利性。",
        testimonialAuthor: "莎拉·约翰逊",
        testimonialRole: "The Garden Bistro 店主",
        footerTagline: "现代二维码点餐系统，提升用餐体验。",
        copyright: "版权所有。",
        product: "产品",
        company: "公司",
        connect: "连接",
        about: "关于我们",
        blog: "博客",
        careers: "职业",
        adminDashboard: "管理仪表板",
        clickToEditProfile: "点击编辑个人资料",
        logout: "退出",
        branch: "分店",
        groups: "分组",
        users: "用户",
        roles: "角色",
        branches: "分店",
        branchComparison: "分店比较",
        menuEmpty: "您的菜单是空的",
        menuEmptyDesc: "从添加美味的菜品到您的菜单开始。您可以将其分类，甚至使用 AI 生成营养信息！",
        noTables: "未找到餐桌",
        noTablesDesc: "通过添加餐桌来设置您的餐厅布局。稍后您可以将它们分配给分组（如空调房、花园）。",
        noGroups: "尚无餐桌分组",
        noGroupsDesc: "分组帮助您整理餐桌（例如 '空调区'，'户外'，'一楼'）。",
        companySettings: "公司设置",
        companyName: "公司名称",
        logo: "标志",
        banner: "横幅",
        saveChanges: "保存更改",
        staffDirectory: "员工名录",
        portalQr: "门户二维码",
        editStaff: "编辑员工",
        addStaff: "添加员工",
        updateStaff: "更新员工",
        welcomeTo: "欢迎来到",
        restaurant: "餐厅",
        heroDescription: "现代二维码餐厅点餐系统，提升客户体验并简化运营",
        customerExperience: "客户体验",
        customerDesc: "无缝二维码点餐，实时跟踪和即时支付。",
        kitchenDisplayFeature: "厨房显示",
        kitchenDesc: "高效的订单管理，状态更新和准备跟踪。",
        adminDashboardFeature: "管理仪表板",
        adminDesc: "菜单，餐桌和餐厅设置的全面管理。",
        analyticsReports: "分析与报告",
        analyticsDesc: "深入了解销售，热门商品和高峰时段。",
        ingredientsManagement: "配料管理",
        ingredientsDesc: "跟踪库存，管理库存水平并获取低库存警报。",
        staffPortal: "员工门户",
        staffDesc: "员工登录，考勤跟踪和休假管理。",
        viewCustomerApp: "查看客户应用",
        viewKitchenDisplay: "查看厨房显示",
        viewAdminPanel: "查看管理面板",
        viewAnalytics: "查看分析",
        manageIngredients: "管理配料",
        staffLogin: "员工登录",
        activeSubscription: "有效订阅",
        plan: "方案",
        validUntil: "有效期至",
        securePaymentNote: "通过 Cashfree 安全支付 • 随时取消",
        settings: "设置",
        addFirstItem: "添加您的第一个项目",
        addFirstTable: "添加您的第一张餐桌",
        allGroups: "所有分组",
        waiter: "服务员",
        kitchenStaff: "厨房员工",
        manager: "经理",
        chef: "厨师",
        cashier: "收银员",
        cleaner: "清洁工",
        otherRole: "其他角色",
        noStaff: "尚未添加员工",
        noStaffDesc: "从添加员工开始。",
        manageAttendanceTitle: "管理考勤与请假",
        attendanceManagement: "考勤管理",
        manageAttendanceDesc: "在员工管理仪表板中查看详细的考勤记录，工作时间和休假请求。",
        openAttendanceManager: "打开考勤管理器",
        staffPortalQr: "员工门户二维码",
        enterRoleName: "输入角色名称",
        pinPlaceholder: "PIN（6位数字）",
        loadingUsers: "正在加载用户...",
        noUsersFound: "未找到用户",
        editUser: "编辑用户",
        deleteUser: "删除用户",
        roleName: "角色名称",
        permissions: "权限",
        loadingRoles: "正在加载角色...",
        noRolesFound: "未找到角色",
        editRole: "编辑角色",
        deleteRole: "删除角色",
        custom: "自定义",
        phone: "电话",
        actions: "操作",
        number: "编号",
        nonACDefault: "非空调（默认）",
        saving: "保存中...",
        qrTable: "餐桌二维码",
        print: "打印",
        ordersForTable: "餐桌订单",
        order: "订单",
        payment: "支付",
        description: "描述",
        noImage: "无图片",
        addItemSub: "添加新项目",
        addTableSub: "添加新餐桌",
        addGroupSub: "添加新分组",
        kitchenDisplay: "厨房显示",
        todaySpecialItems: "今日特价菜品",
        topRatedItems: "最高评分菜品",
        noSpecialItems: "今日暂无特价菜品。请稍后再试！",
        noTopRatedItems: "目前暂无高评分菜品。",
        yesterday: "昨天",
        date: "日期",
        name: "名称",
        connected: "已连接",
        disconnected: "已断开",
        active: "活动",
        completed: "已完成",
        time: "时间",
        allOrdersCleared: "所有订单已清除！",
        greatJob: "干得好，保持节奏。",
        startPreparation: "开始准备",
        markReady: "标记为就绪",
        complete: "完成",
        analyticsDashboard: "分析仪表板",
        analyticsDescription: "实时洞察您的餐厅业绩",
        totalOrders: "总订单",
        totalRevenue: "总收入",
        tablesServed: "服务桌数",
        avgOrderValue: "平均订单价值",
        businessInsights: "商业洞察",
        topPerformer: "表现最佳",
        peakHour: "高峰时段",
        paymentPreference: "支付偏好",
        categoryOpportunity: "类别机会",
        newCustomers: "新客户",
        returningCustomers: "回头客",
        downloadExcel: "下载 Excel",
        downloadPDF: "下载 PDF",
        daily: "每日",
        weekly: "每周",
        monthly: "每月",
        yearly: "每年",
        today: "今天",
        overview: "概览",
        revenue: "营业额",
        items: "商品",
        revenueByBranch: "分店营业额",
        ordersByBranch: "分店订单数",
        topItemsByBranch: "分店畅销商品",
        paymentReconciliation: "订单与付款对比",
        last7: "过去 7 天",
        operations: "运营",
        customers: "客户",
        manageInventory: "管理您的库存和库存水平",
        backToAdmin: "返回管理",
        addIngredient: "添加配料",
        totalIngredients: "总配料",
        lowStockItems: "低库存物品",
        searchIngredients: "搜索配料...",
        quantity: "数量",
        unit: "单位",
        threshold: "阈值",
        lowStock: "低库存",
        inStock: "有货",
        editIngredient: "编辑配料",
        deleteIngredientConfirm: "您确定要删除此配料吗？",
        saveIngredient: "保存配料",
        lowStockThreshold: "低库存阈值",
        enterPin: "输入 PIN",
        verifying: "验证中...",
        staffDashboard: "员工仪表板",
        attendance: "考勤",
        leaves: "休假",
        punchIn: "打卡上班",
        punchOut: "打卡下班",
        attendanceHistory: "考勤记录",
        allStaffAttendance: "所有员工考勤",
        last7Days: "过去 7 天",
        thisMonth: "本月",
        customRange: "自定义范围",
        staffName: "员工姓名",
        checkIn: "上班",
        checkOut: "下班",
        hours: "工时",
        newLeaveRequest: "新休假申请",
        fromDate: "从",
        toDate: "至",
        reason: "原因",
        submitRequest: "提交申请",
        allLeaveRequests: "所有休假申请",
        myLeaveHistory: "我的休假记录",
        noAttendanceRecords: "未找到考勤记录",
        noLeaveRequests: "未找到休假申请",
        approve: "批准",
        reject: "拒绝",
        allBranchesCombined: "所有分店",
        firstIn: "最早进入",
        lastOut: "最后离开",
        dailyRate: "日薪",
        download: "下载",
        refresh: "刷新",
        selectBranch: "选择分店",
        viewingDataFor: "正在查看数据",
        filteredByBranchInfo: "所有菜单项、订单、材料和分析均已针对此分店进行过滤。",
        revenueTrend: "收入趋势",
        ordersOverview: "订单概览",
        revenueBreakdown: "收入明细",
        revenueByCategory: "各类别收入",
        itemPerformance: "项目绩效",
        topSellingItems: "畅销项目",
        categoryPerformance: "类别绩效",
        topItemsDetails: "热门项目详情",
        customerAnalysis: "客户分析",
        customerRetention: "客户留存",
        customerMetrics: "客户指标",
        retentionRate: "留存率",
        operationsAnalysis: "运营分析",
        hourlyOrderDistribution: "每小时订单分布",
        tablePerformance: "餐桌绩效",
        tablePerformanceDetails: "餐桌绩效详情",
        from: "自",
        to: "至",
        tryAdjustingFilters: "尝试调整日期过滤器",
        noAttendanceToExport: "没有可导出的出勤记录。",
        totalWorkingHours: "总工作时间",
        workingHours: "工作时间",
        workSessionDetails: "工作环节详情",
        duration: "时长",
        closeDetails: "关闭详情",
        noLeaveRequestsFound: "未找到休假申请",
        festivalOffer: "节日优惠",
        festivalName: "节日名称",
        festivalDiscount: "节日折扣 (%)",
        festivalDesc: "此折扣将应用于总账单",
        festivalPlaceholder: "例如：排灯节、圣诞节、元旦",
        billingSettings: "账单和支付设置",
        unlockToEdit: "解锁以编辑",
        unlocked: "已解锁",
        paymentSettingsLocked: "支付设置已锁定",
        lockMessage: "出于安全原因，您的二维码、UPI 设置和账单结构已锁定。请使用管理员密码解锁。",
        unlockNow: "立即解锁",
        sgst: "SGST (%)",
        cgst: "CGST (%)",
        upiIdLabel: "点击支付的 UPI ID",
        upiIdPlaceholder: "例如：yourname@okaxis",
        upiIdDesc: "输入 UPI ID 将在账单上启用 GPay、PhonePe 和 Paytm 按钮。",
        paymentQRCodes: "支付二维码",
        addQR: "添加二维码",
        renamedSuccess: "重命名成功",
        renameFailed: "重命名失败",
        allBranchesFloating: "所有分店（浮动）",
        itemNamePlaceholder: "菜品名称（例如：烤鸡）",
        itemDescriptionPlaceholder: "菜品简短描述",
        capacityPlaceholder: "容量（例如：4）",
        tableNamePlaceholder: "餐桌名称/编号（例如：1号桌）",
        roleTypeAdd: "添加角色类型",
        newCategoryName: "新分类名称",
        globalSelfService: "全局自助服务",
        globalSelfServiceDesc: "一次性开启/关闭所有分类的自助服务",
        upgradeForAllLanguages: "升级到尊享版以使用所有 8 种语言",
        subscribeForLanguages: "订阅以获取语言支持",
        tablesUsed: "{current}/{total} 张餐桌已使用",
        itemsUsed: "{current}/{total} 个项目已使用",
        noMenuItemsToExport: "没有可导出的菜单项",
        noDataInFile: "文件中未找到数据",
        itemsImportedSuccessfully: "成功导入 {count} 个项目！",
        manageCategories: "管理分类",
        categoryNamePlaceholder: "新分类名称",
        upgradeForBulk: "升级以使用批量上传/下载",
        unlockSettings: "解锁设置",
        enterPasswordToProceed: "请输入管理员密码以继续",
        settingsUnlocked: "设置已解锁！",
        invalidPassword: "密码无效",
        verificationFailed: "验证失败",
        adminEmailNotFound: "未找到管理员电子邮件",
        sendingResetLink: "正在发送重置链接...",
        branchMgmt: "分店管理",
        branchMgmtSub: "管理您的位置和分店",
        addNewBranch: "添加新分店",
        loadingBranches: "正在加载分店...",
        noBranchesYet: "尚无分店",
        noBranchesDesc: "通过添加您的第一个分店或位置来扩展您的业务。",
        createFirstBranch: "创建第一个分店",
        branchActiveDesc: "分店处于活动状态并接受订单",
        failedLoadBranches: "加载分店失败",
        branchSavedSuccess: "分店保存成功",
        failedSaveBranch: "保存分店失败",
        errorSavingBranch: "保存分店时出错",
        deleteBranchConfirm: "您确定要删除此分店吗？这也将影响相关数据。",
        branchDeletedSuccess: "分店删除成功",
        failedDeleteBranch: "删除分店失败",
        errorDeletingBranch: "删除分店时出错",
        createBranch: "创建分店",
        updateBranch: "更新分店",
        confirmDeletion: "确认删除",
        branchName: "分店名称",
        branchNamePlaceholder: "例如：市中心店、机场店",
        managerName: "经理姓名",
        managerNamePlaceholder: "分店经理姓名",
        addressPlaceholder: "分店完整地址",
        leaveEmptyForNoBranch: "留空表示无分店",
        allowedBranches: "允许的分店",
        inactive: "非活动",
        all: "所有",
        unlock: "解锁",
        lock: "锁定",
        refreshing: "正在刷新所有数据...",
        refreshed: "所有数据已刷新！",
        refreshFailed: "刷新失败",
        statusUpdated: "状态已更新！",
        updateFailed: "更新失败",
        failedLoadMenu: "加载菜单失败",
        categoryAdded: "类目已添加！",
        updatedSuccessfully: "更新成功",
        enabledForAll: "已为所有启用",
        disabledForAll: "已为所有禁用",
        bulkUpdateFailed: "批量更新失败",
        enterGroupName: "输入群组名称",
        deleted: "已删除",
        error: "错误",
        printedPopupBlocked: "已打印（弹出窗口被阻止）",
        qrSentToPrinter: "QR码已发送到打印机",
        printFailed: "打印失败",
        uploadingLogo: "正在上传徽标...",
        logoUploaded: "徽标已上传！",
        uploadingBanner: "正在上传横幅...",
        bannerUploaded: "横幅已上传！",
        generationFailed: "生成失败",
        uploadingQR: "正在上传二维码...",
        qrAdded: "二维码已添加！",
        deleteStaffConfirm: "您确定要删除这位员工吗？",
        deleteGroupConfirm: "删除群组？该群组中的桌子将被设置为 Non AC。",
        deleteRoleConfirm: "您确定要删除这个角色吗？",
        deleteUserConfirm: "您确定要删除这位用户吗？",
        deleteCategoryConfirm: "删除类目 \"{name}\"？其中的项目将失去其类目。",
        optionalDiscountDesc: "可选：添加折扣百分比（例如，10 表示 10% 折扣）",
        paymentSettings: "支付设置",
        festivalOfferDesc: "此折扣将应用于总账单",
        failedResetEmail: "发送重置邮件失败",
    },


    ta: {
        present: "வருகை",
        noBranch: "கிளை இல்லை",
        price: "விலை",
        currency: "நாணயம்",
        absent: "இல்லை",
        totalHours: "மொத்த மணிநேரம்",
        avgHours: "சராசரி மணிநேரம்",
        clickToViewList: "பட்டியலைப் பார்க்க கிளிக் செய்யவும்",
        loading: "ஏற்றுகிறது...",
        search: "தேடு...",
        save: "சேமி",
        cancel: "ரத்துசெய்",
        delete: "நீக்கு",
        edit: "திருத்து",
        close: "மூடு",
        add: "சேர்",
        submit: "சமர்ப்பி",
        home: "முகப்பு",
        features: "அம்சங்கள்",
        pricing: "விலை",
        contact: "தொடர்பு",
        login: "உள்நுழை",
        admin: "நிர்வாகி",
        kitchen: "சமையலறை",
        customer: "வாடிக்கையாளர்",
        analytics: "பகுப்பாய்வு",
        ingredients: "தேவையான பொருட்கள்",
        staff: "பணியாளர்கள்",
        getStarted: "தொடங்கு",
        heroTitle: "பசியை முடிவுக்கு கொண்டு வாருங்கள், ஆர்டர் செய்யத் தொடங்குங்கள்",
        heroSubtitle: "மிகவும் மேம்பட்ட QR அடிப்படையிலான ஆர்டர் அமைப்பு.",
        adminPanel: "நிர்வாக குழு",
        manageMenu: "மெனு & மேசைகளை நிர்வகி",
        menu: "மெனு",
        tables: "மேசைகள்",
        orders: "ஆர்டர்கள்",
        menuItems: "மெனு உருப்படிகள்",
        addItem: "உருப்படியைச் சேர்",
        allCategories: "அனைத்து வகைகளும்",
        noItemsFound: "உருப்படிகள் எதுவும் இல்லை",
        tableGroups: "மேசை குழுக்கள்",
        addGroup: "குழுவைச் சேர்",
        addTable: "மேசையைச் சேர்",
        filterByGroup: "குழு வாரியாக வடிகட்டு",
        allTables: "அனைத்து மேசைகளும்",
        nonAc: "ஏசி அல்லாதது",
        showOrders: "ஆர்டர்களைக் காட்டு",
        qr: "QR",
        menuTitle: "மெனு",
        table: "மேசை",
        yourCart: "உங்கள் கூடை",
        placeOrder: "ஆர்டர் செய்",
        paymentMethod: "கட்டண முறை",
        paymentStatus: "கட்டண நிலை",
        cash: "ரொக்கம்",
        online: "ஆன்லைன்",
        total: "மொத்தம்",
        addToCart: "சேர்",
        itemAdded: "கூடையில் சேர்க்கப்பட்டது!",
        orderPlaced: "ஆர்டர் வெற்றிகரமாக வைக்கப்பட்டது!",
        orderId: "ஆர்டர் ஐடி",
        yourOrders: "உங்கள் ஆர்டர்கள்",
        status: "நிலை",
        pending: "நிலுவையில்",
        preparing: "தயாராகிறது",
        ready: "தயார்",
        delivered: "வழங்கப்பட்டது",
        todaySpecialItems: "இன்றைய சிறப்பு உணவுகள்",
        topRatedItems: "சிறந்த மதிப்பீடு பெற்றவை",
        noSpecialItems: "இன்று சிறப்பு உணவுகள் எதுவும் இல்லை. பின்னர் பார்க்கவும்!",
        noTopRatedItems: "தற்போது சிறந்த மதிப்பீடு பெற்ற உணவுகள் எதுவும் இல்லை.",
        nutritionalInfo: "ஊட்டச்சத்து தகவல்",
        calories: "கலோரிகள்",
        protein: "புரதம்",
        carbs: "கார்போஹைட்ரேட்",
        fats: "கொழுப்பு",
        vitamins: "வைட்டமின்கள்",
        aiRecommendation: "AI உடன் நிரப்பு",
        aiDisclaimer: "AI ஆல் உருவாக்கப்பட்டது. சரிபார்க்கவும்.",
        generating: "உருவாக்குகிறது...",
        aiSuccess: "உருவாக்கப்பட்டது!",
        available: "கிடைக்கிறது",
        unavailable: "கிடைக்கவில்லை",
        outOfStock: "கையிருப்பில் இல்லை",
        selectRole: "பணியைத் தேர்ந்தெடுக்கவும்",
        fullName: "முழு பெயர்",
        email: "மின்னஞ்சல்",
        phoneNumber: "தொலைபேசி எண்",
        newPassword: "புதிய கடவுச்சொல்",
        passwordLabel: "கடவுச்சொல் (தற்போதையதை வைத்திருக்க காலியாக விடவும்)",
        saveUser: "பயனரைச் சேமி",
        confirmAction: "செயலை உறுதிப்படுத்தவும்",
        yes: "ஆம்",
        no: "இல்லை",
        supportChat: "ஆதரவு அரட்டை",
        chatWithUs: "எங்களுடன் அரட்டையடிக்கவும்",
        scanToAccessStaff: "பணியாளர் டாஷ்போர்டை அணுக ஸ்கேன் செய்யவும்",
        noOrdersForTable: "இந்த மேசைக்கு ஆர்டர்கள் எதுவும் இல்லை.",
        groupNamePlaceholder: "குழு பெயர் (எ.கா., ஏசி, தோட்டம்)",
        tableNumber: "மேசை எண்",
        nameOptional: "பெயர் (விருப்பமானது)",
        totalAmount: "மொத்த தொகை",
        trialExpiredBanner: "உங்கள் இலவச சோதனை காலம் முடிந்துவிட்டது. சில அம்சங்கள் வரையறுக்கப்படலாம்.",
        trialExpiresToday: "⚠️ உங்கள் இலவச சோதனை இன்றுடன் முடிகிறது! அணுகலைத் தொடர இப்போதே மேம்படுத்தவும்.",
        trialDaysLeft: "🎉 உங்கள் 20 நாள் இலவச அணுகல் காலத்தில் {days} நாட்கள் மீதமுள்ளன.",
        subscribeNow: "இப்போதே சந்தா சேரவும்",
        upgradePlan: "திட்டத்தை மேம்படுத்து",
        trialExpiredTitle: "சோதனை காலம் முடிந்தது",
        trialExpiredDesc: "<b>{name}</b> க்கான உங்கள் 20 நாள் இலவச அணுகல் முடிந்தது.<br/>உங்கள் உணவகத்தை தொடர்ந்து நிர்வகிக்க, தயவுசெய்து பிரீமியம் திட்டத்திற்கு மேம்படுத்தவும்.",
        unlimitedOrders: "வரம்பற்ற ஆர்டர்கள்",
        detailedAnalytics: "விரிவான பகுப்பாய்வு",
        support247: "24/7 ஆதரவு",
        upgradeNowToContinue: "தொடர இப்போதே மேம்படுத்து",
        switchBranch: "கிளையை மாற்றவும்",
        whyChooseTitle: "ஏன் ரெட்சார்மை (RedSorm) தேர்ந்தெடுக்க வேண்டும்?",
        mobileFirstTitle: "முதலில் மொபைல்",
        mobileFirstDesc: "அனைத்து சாதனங்களுக்கும் உகந்ததாக மாற்றியமைக்கக்கூடிய வடிவமைப்பு மற்றும் தொடு-நட்பு இடைமுகம்",
        lightningFastTitle: "மின்னல் வேகம்",
        lightningFastDesc: "திறமையான உணவக செயல்பாடுகளுக்கு விரைவான ஆர்டர் மற்றும் நிகழ்நேர புதுப்பிப்புகள்",
        secureReliableTitle: "பாதுகாப்பானது மற்றும் நம்பகமானது",
        secureReliableDesc: "மறைக்கப்பட்ட கட்டணங்கள் மற்றும் தரவு பாதுகாப்புடன் கூடிய நிறுவன தர பாதுகாப்பு",
        testimonialQuote: "ரெட்சார்ம் எங்கள் உணவக செயல்பாடுகளை மாற்றியுள்ளது. ஆர்டர்கள் 40% வேகமாக செயலாக்கப்படுகின்றன, மேலும் எங்கள் வாடிக்கையாளர்கள் தங்கள் மேசைகளிலிருந்து ஆர்டர் செய்யும் வசதியை விரும்புகிறார்கள்.",
        testimonialAuthor: "சாரா ஜான்சன்",
        testimonialRole: "உரிமையாளர், தி கார்டன் பிஸ்ட்ரோ",
        footerTagline: "மேம்பட்ட உணவு அனுபவங்களுக்கு நவீன QR-அடிப்படையிலான உணவக ஆர்டர் அமைப்பு.",
        copyright: "அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.",
        product: "தயாரிப்பு",
        company: "நிறுவனம்",
        connect: "இணைப்பு",
        about: "எங்களைப் பற்றி",
        blog: "வலைப்பதிவு",
        careers: "வேலைவாய்ப்புகள்",
        adminDashboard: "நிர்வாக டாஷ்போர்டு",
        clickToEditProfile: "சுயவிவரத்தைத் திருத்த கிளிக் செய்யவும்",
        logout: "வெளியேறு",
        branch: "கிளை",
        groups: "குழுக்கள்",
        users: "பயனர்கள்",
        roles: "பங்குகள்",
        branches: "கிளைகள்",
        branchComparison: "கிளை ஒப்பீடு",
        menuEmpty: "உங்கள் மெனு காலியாக உள்ளது",
        menuEmptyDesc: "உங்கள் மெனுவில் சுவையான பொருட்களைச் சேர்ப்பதன் மூலம் தொடங்கவும். நீங்கள் அவற்றை வகைகளாக ஒழுங்கமைக்கலாம் மற்றும் ஊட்டச்சத்து தகவல்களை உருவாக்க AI ஐப் பயன்படுத்தலாம்!",
        noTables: "மேசைகள் எதுவும் காணப்படவில்லை",
        noTablesDesc: "உங்கள் உணவக அமைப்பை நிறுவ மேசைகளைச் சேர்க்கவும். நீங்கள் அவற்றை பின்னர் குழுக்களுக்கு (ஏசி, தோட்டம் போன்றவை) ஒதுக்கலாம்.",
        noGroups: "இன்னும் மேசை குழுக்கள் இல்லை",
        noGroupsDesc: "குழுக்கள் உங்கள் மேசைகளை ஒழுங்கமைக்க உதவும் (எ.கா., 'ஏசி பிரிவு', 'வெளிப்புறம்', 'முதல் தளம்').",
        companySettings: "நிறுவன அமைப்புகள்",
        companyName: "நிறுவனத்தின் பெயர்",
        logo: "லோகோ",
        banner: "பேனர்",
        saveChanges: "மாற்றங்களைச் சேமி",
        staffDirectory: "பணியாளர் பட்டியல்",
        portalQr: "போர்டல் QR",
        editStaff: "பணியாளரைத் திருத்து",
        addStaff: "பணியாளரைச் சேர்",
        updateStaff: "பணியாளரைப் புதுப்பி",
        welcomeTo: "இதற்கு வரவேற்கிறோம்",
        restaurant: "உணவகம்",
        heroDescription: "வாடிக்கையாளர் அனுபவத்தை மேம்படுத்தும் மற்றும் செயல்பாடுகளை எளிதாக்கும் நவீன QR அடிப்படையிலான உணவக ஆர்டர் அமைப்பு",
        customerExperience: "வாடிக்கையாளர் அனுபவம்",
        customerDesc: "தடையில்லாத QR ஆர்டர், நிகழ்நேர கண்காணிப்பு மற்றும் உடனடி கட்டணங்கள்.",
        kitchenDisplayFeature: "சமையலறை காட்சி",
        kitchenDesc: "திறமையான ஆர்டர் மேலாண்மை, நிலை புதுப்பிப்புகள் மற்றும் தயாரிப்பு கண்காணிப்பு.",
        adminDashboardFeature: "நிர்வாக டாஷ்போர்டு",
        adminDesc: "மெனு, மேசைகள் மற்றும் உணவக அமைப்புகளின் முழுமையான மேலாண்மை.",
        analyticsReports: "பகுப்பாய்வு & அறிக்கைகள்",
        analyticsDesc: "விற்பனை, பிரபலமான பொருட்கள் மற்றும் உச்ச நேரங்கள் பற்றிய ஆழமான பார்வை.",
        ingredientsManagement: "பொருட்கள் மேலாண்மை",
        ingredientsDesc: "சரக்கு, இருப்பு நிலைகள் மற்றும் குறைந்த இருப்பு எச்சரிக்கைகளைக் கண்காணிக்கவும்.",
        staffPortal: "பணியாளர் போர்டல்",
        staffDesc: "பணியாளர் உள்நுழைவு, வருகை கண்காணிப்பு மற்றும் விடுப்பு மேலாண்மை.",
        viewCustomerApp: "வாடிக்கையாளர் பயன்பாட்டைப் பார்",
        viewKitchenDisplay: "சமையலறை காட்சையைப் பார்",
        viewAdminPanel: "நிர்வாக பலகையைப் பார்",
        viewAnalytics: "பகுப்பாய்வைப் பார்",
        manageIngredients: "பொருட்களை நிர்வகி",
        staffLogin: "பணியாளர் உள்நுழைவு",
        activeSubscription: "செயலில் உள்ள சந்தா",
        plan: "திட்டம்",
        validUntil: "வரை செல்லுபடியாகும்",
        securePaymentNote: "Cashfree மூலம் பாதுகாப்பான கட்டணம் • எப்போது வேண்டுமானாலும் ரத்துசெய்யலாம்",
        settings: "அமைப்புகள்",
        addFirstItem: "உங்கள் முதல் உருப்படியைச் சேர்க்கவும்",
        addFirstTable: "உங்கள் முதல் மேசையைச் சேர்க்கவும்",
        allGroups: "அனைத்து குழுக்களும்",
        waiter: "வெயிட்டர்",
        kitchenStaff: "சமையலறை ஊழியர்கள்",
        manager: "மேலாளர்",
        chef: "சமையல்காரர்",
        cashier: "காசாளர்",
        cleaner: "துப்புரவாளர்",
        otherRole: "பிற பங்கு",
        noStaff: "பணியாளர்கள் இன்னும் சேர்க்கப்படவில்லை",
        noStaffDesc: "பணியாளர்களைச் சேர்ப்பதன் மூலம் தொடங்கவும்.",
        manageAttendanceTitle: "வருகை மற்றும் விடுப்புகளை நிர்வகிக்கவும்",
        attendanceManagement: "வருகை மேலாண்மை",
        manageAttendanceDesc: "பணியாளர் மேலாண்மை டாஷ்போர்டில் விரிவான வருகை பதிவுகள், வேலை நேரம் மற்றும் விடுப்பு கோரிக்கைகளை காண்க.",
        openAttendanceManager: "வருகை மேலாளரைத் திற",
        staffPortalQr: "பணியாளர் போர்டல் QR",
        enterRoleName: "பங்கு பெயரை உள்ளிடவும்",
        pinPlaceholder: "PIN (6 இலக்கங்கள்)",
        loadingUsers: "பயனர்களை ஏற்றுகிறது...",
        noUsersFound: "பயனர்கள் காணப்படவில்லை",
        editUser: "பயனரைத் திருத்து",
        deleteUser: "பயனரை நீக்கு",
        roleName: "பங்கு பெயர்",
        permissions: "அனுமதிகள்",
        loadingRoles: "பங்குகளை ஏற்றுகிறது...",
        noRolesFound: "பங்குகள் காணப்படவில்லை",
        editRole: "பங்கைத் திருத்து",
        deleteRole: "பங்கை நீக்கு",
        custom: "விருப்ப",
        phone: "தொலைபேசி",
        actions: "செயல்கள்",
        number: "எண்",
        nonACDefault: "ஏசி அல்லாதது (இயல்புநிலை)",
        saving: "சேமிக்கிறது...",
        qrTable: "மேசைக்கான QR",
        print: "அச்சிடு",
        ordersForTable: "மேசைக்கான ஆர்டர்கள்",
        order: "ஆர்டர்",
        payment: "கட்டணம்",
        description: "விளக்கம்",
        noImage: "படம் இல்லை",
        addItemSub: "புதிய உருப்படியைச் சேர்",
        addTableSub: "புதிய மேசையைச் சேர்",
        addGroupSub: "புதிய குழுவைச் சேர்",
        kitchenDisplay: "சமையலறை காட்சி",
        yesterday: "நேற்று",
        date: "தேதி",
        name: "பெயர்",
        connected: "இணைக்கப்பட்டுள்ளது",
        disconnected: "துண்டிக்கப்பட்டது",
        active: "செயலில்",
        completed: "முடிந்தது",
        time: "நேரம்",
        allOrdersCleared: "அனைத்து ஆர்டர்களும் அழிக்கப்பட்டன!",
        greatJob: "சிறந்த வேலை, வேகத்தைத் தொடரவும்.",
        startPreparation: "தயாரிப்பைத் தொடங்கவும்",
        markReady: "தயார் எனக் குறிக்கவும்",
        complete: "முடிக்கவும்",
        analyticsDashboard: "பகுப்பாய்வு பலகை",
        analyticsDescription: "உங்கள் உணவக செயல்திறன் குறித்த நிகழ்நேர நுண்ணறிவு",
        totalOrders: "மொத்த ஆர்டர்கள்",
        totalRevenue: "மொத்த வருவாய்",
        tablesServed: "சேவை செய்யப்பட்ட மேசைகள்",
        avgOrderValue: "சராசரி ஆர்டர் மதிப்பு",
        businessInsights: "வணிக நுண்ணறிவு",
        topPerformer: "சிறந்த செயல்படுபவர்",
        peakHour: "உச்ச நேரம்",
        paymentPreference: "கட்டண முன்னுரிமை",
        categoryOpportunity: "வகை வாய்ப்பு",
        newCustomers: "புதிய வாடிக்கையாளர்கள்",
        returningCustomers: "திரும்பும் வாடிக்கையாளர்கள்",
        downloadExcel: "Excel பதிவிறக்கவும்",
        downloadPDF: "PDF பதிவிறக்கவும்",
        daily: "தினசரி",
        weekly: "வாராந்திர",
        monthly: "மாதாந்திர",
        yearly: "ஆண்டுதோறும்",
        today: "இன்று",
        overview: "கண்ணோட்டம்",
        revenue: "வருவாய்",
        items: "பொருட்கள்",
        revenueByBranch: "கிளை வாரியாக வருவாய்",
        ordersByBranch: "கிளை வாரியாக ஆர்டர்கள்",
        topItemsByBranch: "கிளை வாரியாக சிறந்த பொருட்கள்",
        paymentReconciliation: "ஆர்டர்கள் மற்றும் கொடுப்பனவுகள் ஒப்பீடு",
        last7: "கடந்த 7 நாட்கள்",
        operations: "செயல்பாடுகள்",
        customers: "வாடிக்கையாளர்கள்",
        manageInventory: "உங்கள் சரக்கு மற்றும் இருப்பு நிலைகளை நிர்வகிக்கவும்",
        backToAdmin: "நிர்வாகிக்குத் திரும்பு",
        addIngredient: "தேவையான பொருளைச் சேர்",
        totalIngredients: "மொத்த பொருட்கள்",
        lowStockItems: "குறைந்த இருப்பு உருப்படிகள்",
        searchIngredients: "தேவையான பொருட்களைத் தேடு...",
        quantity: "அளவு",
        unit: "அலகு",
        threshold: "வரம்பு",
        lowStock: "குறைந்த இருப்பு",
        inStock: "கையிருப்பில்",
        editIngredient: "தேவையான பொருளைத் திருத்து",
        deleteIngredientConfirm: "இந்த பொருளை நிச்சயமாக நீக்க விரும்புகிறீர்களா?",
        saveIngredient: "பொருளைச் சேமி",
        lowStockThreshold: "குறைந்த இருப்பு வரம்பு",
        enterPin: "PIN ஐ உள்ளிடவும்",
        verifying: "சரிபார்க்கிறது...",
        staffDashboard: "பணியாளர் டாஷ்போர்டு",
        attendance: "வருகை",
        leaves: "விடுப்பு",
        punchIn: "உள்நுழை",
        punchOut: "வெளியேறு",
        attendanceHistory: "வருகை வரலாறு",
        allStaffAttendance: "அனைத்து பணியாளர் வருகை",
        last7Days: "கடந்த 7 நாட்கள்",
        thisMonth: "இந்த மாதம்",
        customRange: "விருப்ப வரம்பு",
        staffName: "பணியாளர் பெயர்",
        checkIn: "உள்",
        checkOut: "வெளி",
        hours: "மணிநேரம்",
        newLeaveRequest: "புதிய விடுப்பு கோரிக்கை",
        fromDate: "முதல்",
        toDate: "வரை",
        reason: "காரணம்",
        submitRequest: "கோரிக்கையைச் சமர்ப்பி",
        allLeaveRequests: "அனைத்து விடுப்பு கோரிக்கைகளும்",
        myLeaveHistory: "எனது விடுப்பு வரலாறு",
        noAttendanceRecords: "வருகை பதிவுகள் இல்லை",
        noLeaveRequests: "விடுப்பு கோரிக்கைகள் இல்லை",
        approve: "ஒப்புதல்",
        reject: "நிராகரி",
        allBranchesCombined: "அனைத்து கிளைகள்",
        firstIn: "முதல் வருகை",
        lastOut: "கடைசி வெளியேற்றம்",
        dailyRate: "தினசரி கட்டணம்",
        download: "பதிவிறக்கம்",
        refresh: "புதுப்பித்தல்",
        selectBranch: "கிளையைத் தேர்ந்தெடுக்கவும்",
        viewingDataFor: "தரவுகளைப் பார்க்கிறீர்கள்",
        filteredByBranchInfo: "மெனு உருப்படிகள், ஆர்டர்கள், பொருட்கள் மற்றும் பகுப்பாய்வுகள் இந்த கிளைக்காக வடிகட்டப்பட்டுள்ளன.",
        revenueTrend: "வருவாய் போக்கு",
        ordersOverview: "ஆர்டர்கள் மேலோட்டம்",
        revenueBreakdown: "வருவாய் முறிவு",
        revenueByCategory: "வகை வாரியான வருவாய்",
        itemPerformance: "உருப்படி செயல்திறன்",
        topSellingItems: "அதிகம் விற்பனையாகும் உருப்படிகள்",
        categoryPerformance: "வகை செயல்திறன்",
        topItemsDetails: "சிறந்த உருப்படிகளின் விவரங்கள்",
        customerAnalysis: "வாடிக்கையாளர் பகுப்பாய்வு",
        customerRetention: "வாடிக்கையாளர் தக்கவைப்பு",
        customerMetrics: "வாடிக்கையாளர் அளவீடுகள்",
        retentionRate: "தக்கவைப்பு விகிதம்",
        operationsAnalysis: "செயல்பாட்டு பகுப்பாய்வு",
        hourlyOrderDistribution: "மணிநேர ஆர்டர் விநியோகம்",
        tablePerformance: "மேசை செயல்திறன்",
        tablePerformanceDetails: "மேசை செயல்திறன் விவரங்கள்",
        from: "இருந்து",
        to: "வரை",
        tryAdjustingFilters: "தேதி வடிகட்டிகளை சரிசெய்ய முயற்சிக்கவும்",
        noAttendanceToExport: "ஏற்றுமதி செய்ய வருகை பதிவுகள் எதுவும் இல்லை.",
        totalWorkingHours: "மொத்த வேலை நேரம்",
        workingHours: "வேலை நேரம்",
        workSessionDetails: "வேலை அமர்வு விவரங்கள்",
        duration: "காலம்",
        closeDetails: "விவரங்களை மூடு",
        noLeaveRequestsFound: "விடுப்பு கோரிக்கைகள் எதுவும் பெறப்படவில்லை",
        festivalOffer: "பண்டிகை சலுகை",
        festivalName: "பண்டிகை பெயர்",
        festivalDiscount: "பண்டிகை தள்ளுபடி (%)",
        festivalDesc: "இந்த தள்ளுபடி மொத்த பில்லுக்கும் பொருந்தும்",
        festivalPlaceholder: "எ.கா, தீபாவலி, கிறிஸ்துமஸ், புத்தாண்டு",
        billingSettings: "பில்லிங் மற்றும் கட்டண அமைப்புகள்",
        unlockToEdit: "திருத்த திறக்கவும்",
        unlocked: "திறக்கப்பட்டது",
        paymentSettingsLocked: "கட்டண அமைப்புகள் பூட்டப்பட்டுள்ளன",
        lockMessage: "பாதுகாப்பு காரணங்களுக்காக, உங்கள் QR குறியீடுகள், UPI அமைப்புகள் மற்றும் பில்லிங் அமைப்பு பூட்டப்பட்டுள்ளன. உங்கள் நிர்வாகி கடவுச்சொல்லைப் பயன்படுத்தி திறக்கவும்.",
        unlockNow: "இப்போது திறக்கவும்",
        sgst: "SGST (%)",
        cgst: "CGST (%)",
        upiIdLabel: "Click-to-Pay க்கான UPI ID",
        upiIdPlaceholder: "எ.கா, yourname@okaxis",
        upiIdDesc: "UPI ID ஐ உள்ளிடுவது பில்லில் GPay, PhonePe மற்றும் Paytm பொத்தான்களை இயக்கும்.",
        paymentQRCodes: "கட்டண QR குறியீடுகள்",
        addQR: "QR ஐச் சேர்",
        renamedSuccess: "வெற்றிகரமாக பெயர் மாற்றப்பட்டது",
        renameFailed: "பெயர் மாற்றம் தோல்வியடைந்தது",
        allBranchesFloating: "அனைத்து கிளைகளும் (மிதக்கும்)",
        itemNamePlaceholder: "உருப்படியின் பெயர் (எ.கா, கிரில்டு சிக்கன்)",
        itemDescriptionPlaceholder: "உருப்படியின் சுருக்கமான விளக்கம்",
        tableNumberPlaceholder: "மேசை எண் (எ.கா: 1)",
        tableNamePlaceholder: "மேசை பெயர் (எ.கா: கார்டன் 1)",
        roleTypeAdd: "பங்கு வகையைச் சேர்",
        newCategoryName: "புதிய வகை பெயர்",
        globalSelfService: "உலகளாவிய சுய சேவை",
        globalSelfServiceDesc: "அனைத்து வகைகளுக்கும் ஒரே நேரத்தில் சுய சேவையை ஆன்/ஆப் செய்யவும்",
        upgradeForAllLanguages: "அனைத்து 8 மொழிகளுக்கும் பிரீமியத்திற்கு மேம்படுத்தவும்",
        subscribeForLanguages: "மொழி ஆதரவிற்காக சந்தா செலுத்தவும்",
        tablesUsed: "{current}/{total} மேசைகள் பயன்படுத்தப்பட்டுள்ளன",
        itemsUsed: "{current}/{total} உருப்படிகள் பயன்படுத்தப்பட்டுள்ளன",
        noMenuItemsToExport: "ஏற்றுமதி செய்ய மெனு உருப்படிகள் இல்லை",
        noDataInFile: "கோப்பில் தரவு எதுவும் இல்லை",
        itemsImportedSuccessfully: "{count} உருப்படிகள் வெற்றிகரமாக இறக்குமதி செய்யப்பட்டன!",
        manageCategories: "வகைகளை நிர்வகி",
        categoryNamePlaceholder: "புதிய வகை பெயர்",
        upgradeForBulk: "மொத்த பதிவேற்றம்/பதிவிறக்கத்திற்கு மேம்படுத்தவும்",
        unlockSettings: "அமைப்புகளைத் திறக்கவும்",
        enterPasswordToProceed: "தொடர உங்கள் நிர்வாகி கடவுச்சொல்லை உள்ளிடவும்",
        settingsUnlocked: "அமைப்புகள் திறக்கப்பட்டன!",
        invalidPassword: "தவறான கடவுச்சொல்",
        verificationFailed: "சரிபார்ப்பு தோல்வியடைந்தது",
        adminEmailNotFound: "நிர்வாகி மின்னஞ்சல் காணப்படவில்லை",
        sendingResetLink: "மீட்டமைப்பு இணைப்பை அனுப்புகிறது...",
        branchMgmt: "கிளை மேலாண்மை",
        branchMgmtSub: "உங்கள் இடங்கள் மற்றும் கிளைகளை நிர்வகிக்கவும்",
        addNewBranch: "புதிய கிளையைச் சேர்",
        loadingBranches: "கிளைகள் ஏற்றப்படுகின்றன...",
        noBranchesYet: "கிளைகள் இன்னும் இல்லை",
        noBranchesDesc: "உங்கள் முதல் கிளை அல்லது இடத்தைச் சேர்ப்பதன் மூலம் உங்கள் வணிகத்தை விரிவுபடுத்துங்கள்.",
        createFirstBranch: "முதல் கிளையை உருவாக்கு",
        branchActiveDesc: "கிளை செயலில் உள்ளது மற்றும் ஆர்டர்களை ஏற்றுக்கொள்கிறது",
        failedLoadBranches: "கிளைகளை ஏற்றுவதில் தோல்வி",
        branchSavedSuccess: "கிளை வெற்றிகரமாகச் சேமிக்கப்பட்டது",
        failedSaveBranch: "கிளையைச் சேமிப்பதில் தோல்வி",
        errorSavingBranch: "கிளையைச் சேமிப்பதில் பிழை",
        deleteBranchConfirm: "நிச்சயமாக இந்தக் கிளையை நீக்க விரும்புகிறீர்களா? இது தொடர்புடைய தரவுகளையும் பாதிக்கும்.",
        branchDeletedSuccess: "கிளை வெற்றிகரமாக நீக்கப்பட்டது",
        failedDeleteBranch: "கிளையை நீக்குவதில் தோல்வி",
        errorDeletingBranch: "கிளையை நீக்குவதில் பிழை",
        createBranch: "கிளையை உருவாக்கு",
        updateBranch: "கிளையைப் புதுப்பி",
        confirmDeletion: "நீக்குதலை உறுதிப்படுத்து",
        branchName: "கிளையின் பெயர்",
        branchNamePlaceholder: "எ.கா., டவுன்டவுன் கிளை, விமான நிலைய இடம்",
        managerName: "மேலாளர் பெயர்",
        managerNamePlaceholder: "கிளை மேலாளரின் பெயர்",
        addressPlaceholder: "கிளை இருப்பிடத்தின் முழு முகவரி",
        leaveEmptyForNoBranch: "கிளை இல்லை என்பதற்கு காலியாக விடவும்",
        allowedBranches: "அனுமதிக்கப்பட்ட கிளைகள்",
        inactive: "செயலற்றது",
        all: "அனைத்தும்",
        unlock: "திறக்க",
        lock: "பூட்டு",
        refreshing: "அனைத்து தரவுகளும் புதுப்பிக்கப்படுகின்றன...",
        refreshed: "அனைத்து தரவுகளும் புதுப்பிக்கப்பட்டன!",
        refreshFailed: "புதுப்பித்தல் தோல்வியடைந்தது",
        statusUpdated: "நிலை புதுப்பிக்கப்பட்டது!",
        updateFailed: "புதுப்பித்தல் தோல்வியடைந்தது",
        failedLoadMenu: "மெனுவை ஏற்றுவதில் தோல்வி",
        categoryAdded: "வகை சேர்க்கப்பட்டது!",
        updatedSuccessfully: "வெற்றிகரமாக புதுப்பிக்கப்பட்டது",
        enabledForAll: "அனைத்திற்கும் செயல்படுத்தப்பட்டது",
        disabledForAll: "அனைத்திற்கும் முடக்கப்பட்டது",
        bulkUpdateFailed: "மொத்தப் புதுப்பித்தல் தோல்வியடைந்தது",
        enterGroupName: "குழுவின் பெயரை உள்ளிடவும்",
        deleted: "நீக்கப்பட்டது",
        error: "பிழை",
        printedPopupBlocked: "அச்சிடப்பட்டது (பாப்அப் தடுக்கப்பட்டது)",
        qrSentToPrinter: "QR அச்சுப்பொறிக்கு அனுப்பப்பட்டது",
        printFailed: "அச்சிடுதல் தோல்வியடைந்தது",
        uploadingLogo: "லோகோ பதிவேற்றப்படுகிறது...",
        logoUploaded: "லோகோ பதிவேற்றப்பட்டது!",
        uploadingBanner: "பேனர் பதிவேற்றப்படுகிறது...",
        bannerUploaded: "பேனர் பதிவேற்றப்பட்டது!",
        generationFailed: "உருவாக்கம் தோல்வியடைந்தது",
        uploadingQR: "QR பதிவேற்றப்படுகிறது...",
        qrAdded: "QR குறியீடு சேர்க்கப்பட்டது!",
        deleteStaffConfirm: "இந்த பணியாளரை நிச்சயமாக நீக்க விரும்புகிறீர்களா?",
        deleteGroupConfirm: "குழுவை நீக்கவா? இந்தக் குழுவில் உள்ள மேஜைகள் Non AC ஆக அமைக்கப்படும்.",
        deleteRoleConfirm: "இந்த பொறுப்பை நிச்சயமாக நீக்க விரும்புகிறீர்களா?",
        deleteUserConfirm: "இந்த பயனரை நிச்சயமாக நீக்க விரும்புகிறீர்களா?",
        deleteCategoryConfirm: "\"{name}\" வகையை நீக்கவா? அதில் உள்ள உருப்படிகள் தங்கள் வகையை இழக்கும்.",
        optionalDiscountDesc: "விருப்பத்தேர்வு: தள்ளுபடி சதவீதத்தைச் சேர்க்கவும் (எ.கா., 10% தள்ளுபடிக்கு 10)",
        paymentSettings: "பணம் செலுத்தும் அமைப்புகள்",
        festivalOfferDesc: "இந்த தள்ளுபடி மொத்த பில்லுக்குப் பயன்படுத்தப்படும்",
        failedResetEmail: "மீட்டமைப்பு மின்னஞ்சலை அனுப்புவதில் தோல்வி",
    },


    ml: {
        present: "ഹാജർ",
        noBranch: "ബ്രാഞ്ചില്ല",
        price: "വില",
        currency: "കറൻസി",
        absent: "ഹാജരില്ല",
        totalHours: "ആകെ മണിക്കൂർ",
        avgHours: "ശരാശരി മണിക്കൂർ",
        clickToViewList: "ലിസ്റ്റ് കാണാൻ ക്ലിക്ക് ചെയ്യുക",
        loading: "ലോഡുചെയ്യുന്നു...",
        search: "തിരയുക...",
        save: "സംരക്ഷിക്കുക",
        cancel: "റദ്ദാക്കുക",
        delete: "നീക്കം ചെയ്യുക",
        edit: "എഡിറ്റ് ചെയ്യുക",
        close: "അടയ്ക്കുക",
        add: "ചേർക്കുക",
        submit: "സമർപ്പിക്കുക",
        home: "ഹോം",
        features: "സവിശേഷതകൾ",
        pricing: "വില",
        contact: "ബന്ധപ്പെടുക",
        login: "ലോഗിൻ",
        admin: "അഡ്മിൻ",
        kitchen: "അടുക്കള",
        customer: "ഉപഭോക്താവ്",
        analytics: "അനലിറ്റിക്സ്",
        ingredients: "ചേരുവകൾ",
        staff: "സ്റ്റാഫ്",
        getStarted: "ആരംഭിക്കുക",
        heroTitle: "വിശപ്പ് അവസാനിപ്പിക്കൂ, ഓർഡർ ചെയ്യാൻ തുടങ്ങൂ",
        heroSubtitle: "ഏറ്റവും നൂതനമായ QR അടിസ്ഥാനമാക്കിയുള്ള ഓർഡറിംഗ് സിസ്റ്റം.",
        adminPanel: "അഡ്മിൻ പാനൽ",
        manageMenu: "മെനു & ടേബിളുകൾ നിയന്ത്രിക്കുക",
        menu: "മെനു",
        tables: "ടേബിളുകൾ",
        orders: "ഓർഡറുകൾ",
        menuItems: "മെനു ഇനങ്ങൾ",
        addItem: "ഇനം ചേർക്കുക",
        allCategories: "എല്ലാ വിഭാഗങ്ങളും",
        noItemsFound: "ഇനങ്ങളൊന്നും കണ്ടെത്തിയില്ല",
        tableGroups: "ടേബിൾ ഗ്രൂപ്പുകൾ",
        addGroup: "ഗ്രൂപ്പ് ചേർക്കുക",
        addTable: "ടേബിൾ ചേർക്കുക",
        filterByGroup: "ഗ്രൂപ്പ് തിരിച്ച് ഫിൽട്ടർ ചെയ്യുക",
        allTables: "എല്ലാ ടേബിളുകളും",
        nonAc: "നോൺ എസി",
        showOrders: "ഓർഡറുകൾ കാണിക്കുക",
        qr: "QR",
        menuTitle: "മെനു",
        table: "ടേബിൾ",
        yourCart: "നിങ്ങളുടെ കാർട്ട്",
        placeOrder: "ഓർഡർ ചെയ്യുക",
        paymentMethod: "പേയ്മെന്റ് രീതി",
        paymentStatus: "പേയ്‌മെന്റ് നില",
        cash: "പണം",
        online: "ഓൺലൈൻ",
        total: "ആകെ",
        addToCart: "ചേർക്കുക",
        itemAdded: "കാർട്ടിലേക്ക് ചേർത്തു!",
        orderPlaced: "ഓർഡർ വിജയകരമായി നൽകി!",
        orderId: "ഓർഡർ ഐഡി",
        yourOrders: "നിങ്ങളുടെ ഓർഡറുകൾ",
        status: "അവസ്ഥ",
        pending: "തീർപ്പുകൽപ്പിക്കാത്തത്",
        preparing: "തയ്യാറാക്കുന്നു",
        ready: "തയ്യാർ",
        delivered: "ഡെലിവർ ചെയ്തു",
        todaySpecialItems: "ഇന്നത്തെ വിശേഷ വിഭവങ്ങൾ",
        topRatedItems: "മികച്ച റേറ്റിംഗ് ഉള്ളവ",
        noSpecialItems: "ഇന്ന് വിശേഷ വിഭവങ്ങൾ ലഭ്യമല്ല. പിന്നീട് ശ്രമിക്കുക!",
        noTopRatedItems: "നിലവിൽ മികച്ച റേറ്റിംഗ് ഉള്ള വിഭവങ്ങൾ ലഭ്യമല്ല.",
        nutritionalInfo: "പോഷകാഹാര വിവരങ്ങൾ",
        calories: "കലോറി",
        protein: "പ്രോട്ടീൻ",
        carbs: "കാർബോഹൈഡ്രേറ്റ്",
        fats: "കൊഴുപ്പ്",
        vitamins: "വിറ്റാമിനുകൾ",
        aiRecommendation: "AI ഉപയോഗിച്ച് പൂരിപ്പിക്കുക",
        aiDisclaimer: "AI സൃഷ്ടിച്ചത്. ദയവായി പരിശോധിക്കുക.",
        generating: "സൃഷ്ടിക്കുന്നു...",
        aiSuccess: "സൃഷ്ടിച്ചു!",
        available: "ലഭ്യമാണ്",
        unavailable: "ലഭ്യമല്ല",
        outOfStock: "സ്റ്റോക്കില്ല",
        selectRole: "റോൾ തിരഞ്ഞെടുക്കുക",
        fullName: "മുഴുവൻ പേര്",
        email: "ഇമെയിൽ",
        phoneNumber: "ഫോൺ നമ്പർ",
        newPassword: "പുതിയ പാസ്‌വേഡ്",
        passwordLabel: "പാസ്‌വേഡ് (നിലവിലുള്ളത് നിലനിർത്താൻ ശൂന്യമായി വിടുക)",
        saveUser: "ഉപയോക്താവിനെ സംരക്ഷിക്കുക",
        confirmAction: "നടപടി സ്ഥിരീകരിക്കുക",
        yes: "അതെ",
        no: "അല്ല",
        supportChat: "സപ്പോർട്ട് ചാറ്റ്",
        chatWithUs: "ഞങ്ങളുമായി ചാറ്റ് ചെയ്യുക",
        scanToAccessStaff: "സ്റ്റാഫ് ഡാഷ്‌ബോർഡ് ആക്‌സസ് ചെയ്യാൻ സ്കാൻ ചെയ്യുക",
        noOrdersForTable: "ഈ ടേബിളിനായി ഓർഡറുകളൊന്നും കണ്ടെത്തിയില്ല.",
        groupNamePlaceholder: "ഗ്രൂപ്പ് പേര് (ഉദാഹരണത്തിന്: എസി, ഗാർഡൻ)",
        tableNumber: "ടേബിൾ നമ്പർ",
        nameOptional: "പേര് (നിർബന്ധമില്ല)",
        totalAmount: "ആകെ തുക",
        trialExpiredBanner: "നിങ്ങളുടെ സൗജന്യ ട്രയൽ കാലാവധി കഴിഞ്ഞു. ചില ഫീച്ചറുകൾ പരിമിതപ്പെടുത്തിയേക്കാം.",
        trialExpiresToday: "⚠️ നിങ്ങളുടെ സൗജന്യ ട്രയൽ ഇന്ന് അവസാനിക്കുന്നു! ആക്സസ് നിലനിർത്താൻ ഇപ്പോൾ അപ്‌ഗ്രേഡ് ചെയ്യുക.",
        trialDaysLeft: "🎉 നിങ്ങളുടെ 20 ദിവസത്തെ സൗജന്യ ആക്സസ് കാലയളവിൽ {days} ദിവസം കൂടി ബാക്കിയുണ്ട്.",
        subscribeNow: "ഇപ്പോൾ സബ്‌സ്‌ക്രൈബ് ചെയ്യുക",
        upgradePlan: "പ്ലാൻ അപ്‌ഗ്രേഡ് ചെയ്യുക",
        trialExpiredTitle: "ട്രയൽ കാലാവധി കഴിഞ്ഞു",
        trialExpiredDesc: "<b>{name}</b> ലേക്കുള്ള നിങ്ങളുടെ 20 ദിവസത്തെ സൗജന്യ ആക്സസ് അവസാനിച്ചു.<br/>നിങ്ങളുടെ റെസ്റ്റോറന്റ് മാനേജ് ചെയ്യുന്നത് തുടരാൻ, ദയവായി ഒരു പ്രീമിയം പ്ലാനിലേക്ക് അപ്‌ഗ്രേഡ് ചെയ്യുക.",

        detailedAnalytics: "വിശദമായ അനലിറ്റിക്സ്",
        support247: "24/7 പിന്തുണ",
        upgradeNowToContinue: "തുടരുന്നതിന് ഇപ്പോൾ അപ്‌ഗ്രേഡ് ചെയ്യുക",
        switchBranch: "ബ്രാഞ്ച് മാറ്റുക",
        whyChooseTitle: "എന്തുകൊണ്ട് റെഡ്‌സോം (RedSorm) തിരഞ്ഞെടുക്കണം?",
        mobileFirstTitle: "മൊബൈൽ ഫസ്റ്റ്",
        mobileFirstDesc: "റെസ്‌പോൺസീവ് ഡിസൈനും ടച്ച്-ഫ്രണ്ട്‌ലി ഇന്റർഫേസും ഉള്ള എല്ലാ ഉപകരണങ്ങൾക്കും അനുയോജ്യമായ രീതിയിൽ ഒപ്റ്റിമൈസ് ചെയ്തിരിക്കുന്നു",
        lightningFastTitle: "മിന്നൽ വേഗം",
        lightningFastDesc: "കാര്യക്ഷമമായ റെസ്റ്റോറന്റ് പ്രവർത്തനങ്ങൾക്കായി വേഗത്തിലുള്ള ഓർഡർ പ്ലേസ്‌മെന്റും തത്സമയ അപ്‌ഡേറ്റുകളും",
        secureReliableTitle: "സുരക്ഷിതവും വിശ്വസനീയവും",
        secureReliableDesc: "എൻക്രിപ്റ്റ് ചെയ്ത പേയ്‌മെന്റുകളും ഡാറ്റ പരിരക്ഷയും ഉള്ള എന്റർപ്രൈസ്-ഗ്രേഡ് സുരക്ഷ",
        testimonialQuote: "റെഡ്‌സോം ഞങ്ങളുടെ റെസ്റ്റോറന്റ് പ്രവർത്തനങ്ങളെ മാറ്റിമറിച്ചു. ഓർഡറുകൾ 40% വേഗത്തിൽ പ്രോസസ്സ് ചെയ്യുന്നു, കൂടാതെ ഞങ്ങളുടെ ഉപഭോക്താക്കൾ അവരുടെ ടേബിളുകളിൽ നിന്ന് ഓർഡർ ചെയ്യുന്നതിന്റെ സൗകര്യം ഇഷ്ടപ്പെടുന്നു.",
        testimonialAuthor: "സാറ ജോൺസൺ",
        testimonialRole: "ഉടമ, ദ ഗാർഡൻ ബിസ്ട്രോ",
        footerTagline: "മെച്ചപ്പെട്ട ഡൈനിംഗ് അനുഭവങ്ങൾക്കായി ആധുനിക ക്യുആർ-അധിഷ്ഠിത റെസ്റ്റോറന്റ് ഓർഡറിംഗ് സിസ്റ്റം.",
        copyright: "എല്ലാ അവകാശങ്ങളും നിക്ഷിപ്തം.",
        product: "ഉൽപ്പന്നം",
        company: "കമ്പനി",
        connect: "ബന്ധപ്പെടുക",
        about: "ഞങ്ങളെക്കുറിച്ച്",
        blog: "ബ്ലോഗ്",
        careers: "കരിയർ",
        adminDashboard: "അഡ്മിൻ ഡാഷ്‌ബോർഡ്",
        clickToEditProfile: "പ്രൊഫൈൽ എഡിറ്റുചെയ്യാൻ ക്ലിക്കുചെയ്യുക",
        logout: "ലോഗ് ഔട്ട്",
        branch: "ശാഖ",
        groups: "ഗ്രൂപ്പുകൾ",
        users: "ഉപയോക്താക്കൾ",
        roles: "റോളുകൾ",
        branches: "ശാഖകൾ",
        branchComparison: "ശാഖ താരതമ്യം",
        menuEmpty: "നിങ്ങളുടെ മെനു ശൂന്യമാണ്",
        menuEmptyDesc: "നിങ്ങളുടെ മെനുവിലേക്ക് രുചികരമായ ഇനങ്ങൾ ചേർത്തുകൊണ്ട് ആരംഭിക്കുക. നിങ്ങൾക്ക് അവയെ വിഭാഗങ്ങളായി ക്രമീകരിക്കാനും പോഷകാഹാര വിവരങ്ങൾ സൃഷ്ടിക്കാൻ AI ഉപയോഗിക്കാനും കഴിയും!",
        noTables: "ടേബിളുകളൊന്നും കണ്ടെത്തിയില്ല",
        noTablesDesc: "ടേബിളുകൾ ചേർത്ത് നിങ്ങളുടെ റെസ്റ്റോറന്റ് ലേഔട്ട് സജ്ജമാക്കുക. നിങ്ങൾക്ക് അവ പിന്നീട് ഗ്രൂപ്പുകളിലേക്ക് (എസി, ഗാർഡൻ പോലുള്ളവ) നൽകാം.",
        noGroups: "ഇതുവരെ ടേബിൾ ഗ്രൂപ്പുകളില്ല",
        noGroupsDesc: "നിങ്ങളുടെ ടേബിളുകൾ ക്രമീകരിക്കാൻ ഗ്രൂപ്പുകൾ സഹായിക്കുന്നു (ഉദാ. 'എസി വിഭാഗം', 'ഔട്ട്‌ഡോർ', 'ഒന്നാം നില').",
        companySettings: "കമ്പനി ക്രമീകരണങ്ങൾ",
        companyName: "കമ്പനിയുടെ പേര്",
        logo: "ലോഗോ",
        banner: "ബാനർ",
        saveChanges: "മാറ്റങ്ങൾ സംരക്ഷിക്കുക",
        staffDirectory: "ജീവനക്കാരുടെ ഡയറക്ടറി",
        portalQr: "പോർട്ടൽ QR",
        editStaff: "ജീവനക്കാരെ എഡിറ്റുചെയ്യുക",
        addStaff: "ജീവനക്കാരെ ചേർക്കുക",
        updateStaff: "ജീവനക്കാരെ അപ്‌ഡേറ്റുചെയ്യുക",
        welcomeTo: "സ്വാഗതം",
        restaurant: "റെസ്റ്റോറന്റ്",
        heroDescription: "ഉപഭോക്തൃ അനുഭവം മെച്ചപ്പെടുത്തുകയും പ്രവർത്തനങ്ങൾ കാര്യക്ഷമമാക്കുകയും ചെയ്യുന്ന ഒരു ആധുനിക QR-അടിസ്ഥാന റെസ്റ്റോറന്റ് ഓർഡറിംഗ് സിസ്റ്റം",
        customerExperience: "ഉപഭോക്തൃ അനുഭവം",
        customerDesc: "തടസ്സമില്ലാത്ത QR ഓർഡറിംഗ്, തത്സമയ ട്രാക്കിംഗ്, തൽക്ഷണ പേയ്‌മെന്റുകൾ.",
        kitchenDisplayFeature: "അടുക്കള ഡിസ്പ്ലേ",
        kitchenDesc: "കാര്യക്ഷമമായ ഓർഡർ മാനേജ്‌മെന്റ്, സ്റ്റാറ്റസ് അപ്‌ഡേറ്റുകൾ, തയ്യാറെടുപ്പ് ട്രാക്കിംഗ്.",
        adminDashboardFeature: "അഡ്മിൻ ഡാഷ്‌ബോർഡ്",
        adminDesc: "മെനു, ടേബിളുകൾ, റെസ്റ്റോറന്റ് ക്രമീകരണങ്ങൾ എന്നിവയുടെ സമഗ്ര മാനേജ്‌മെന്റ്.",
        analyticsReports: "അനലിറ്റിക്‌സും റിപ്പോർട്ടുകളും",
        analyticsDesc: "വിൽപ്പന, ജനപ്രിയ ഇനങ്ങൾ, തിരക്കുള്ള സമയങ്ങൾ എന്നിവയെക്കുറിച്ചുള്ള ആഴത്തിലുള്ള അറിവ്.",
        ingredientsManagement: "ചേരുവകളുടെ മാനേജ്‌മെന്റ്",
        ingredientsDesc: "ഇൻവെന്ററി ട്രാക്കുചെയ്യുക, സ്റ്റോക്ക് ലെവലുകൾ നിയന്ത്രിക്കുക, കുറഞ്ഞ സ്റ്റോക്ക് അലേർട്ടുകൾ നേടുക.",
        staffPortal: "സ്റ്റാഫ് പോർട്ടൽ",
        staffDesc: "സ്റ്റാഫ് ലോഗിൻ, ഹാജർ ട്രാക്കിംഗ്, അവധി മാനേജ്‌മെന്റ്.",
        viewCustomerApp: "ഉപഭോക്തൃ ആപ്പ് കാണുക",
        viewKitchenDisplay: "അടുക്കള ഡിസ്പ്ലേ കാണുക",
        viewAdminPanel: "അഡ്മിൻ പാനൽ കാണുക",
        viewAnalytics: "അനലിറ്റിക്സ് കാണുക",
        manageIngredients: "ചേരുവകൾ നിയന്ത്രിക്കുക",
        staffLogin: "സ്റ്റാഫ് ലോഗിൻ",
        activeSubscription: "സജീവ സബ്‌സ്‌ക്രിപ്‌ഷൻ",
        plan: "പ്ലാൻ",
        validUntil: "വരെ സാധുവാണ്",
        securePaymentNote: "Cashfree വഴിയുള്ള സുരക്ഷിത പേയ്‌മെന്റ് • എപ്പോൾ വേണമെങ്കിലും റദ്ദാക്കാം",
        settings: "ക്രമീകരണങ്ങൾ",
        addFirstItem: "നിങ്ങളുടെ ആദ്യ ഇനം ചേർക്കുക",
        addFirstTable: "നിങ്ങളുടെ ആദ്യ ടേബിൾ ചേർക്കുക",
        allGroups: "എല്ലാ ഗ്രൂപ്പുകളും",
        waiter: "വെയ്റ്റർ",
        kitchenStaff: "അടുക്കള ജീവനക്കാർ",
        manager: "മാനേജർ",
        chef: "ഷെഫ്",
        cashier: "കാഷ്യർ",
        cleaner: "ക്ലീനർ",
        otherRole: "മറ്റ് വേഷം",
        noStaff: "ഇതുവരെ ജീവനക്കാരെ ചേർത്തിട്ടില്ല",
        noStaffDesc: "ജീവനക്കാരെ ചേർത്ത് ആരംഭിക്കുക.",
        manageAttendanceTitle: "ഹാജരും അവധിയും നിയന്ത്രിക്കുക",
        manageAttendanceDesc: "സ്റ്റാഫ് മാനേജ്‌മെന്റ് ഡാഷ്‌ബോർഡിൽ വിശദമായ ഹാജർ ലോഗുകൾ, ജോലി സമയം, അവധി അഭ്യർത്ഥനകൾ എന്നിവ കാണുക.",
        openAttendanceManager: "അറ്റൻഡൻസ് മാനേജർ തുറക്കുക",
        staffPortalQr: "സ്റ്റാഫ് പോർട്ടൽ QR",
        enterRoleName: "റോൾ പേര് നൽകുക",
        pinPlaceholder: "PIN (6 അക്കങ്ങൾ)",
        loadingUsers: "ഉപയോക്താക്കളെ ലോഡുചെയ്യുന്നു...",
        noUsersFound: "ഉപയോക്താക്കളെ കണ്ടെത്തിയില്ല",
        editUser: "ഉപയോക്താവിനെ എഡിറ്റുചെയ്യുക",
        deleteUser: "ഉപയോക്താവിനെ നീക്കം ചെയ്യുക",
        roleName: "റോൾ പേര്",
        permissions: "അനുമതികൾ",
        loadingRoles: "റോളുകൾ ലോഡുചെയ്യുന്നു...",
        noRolesFound: "റോളുകൾ കണ്ടെത്തിയില്ല",
        editRole: "റോൾ എഡിറ്റുചെയ്യുക",
        deleteRole: "റോൾ നീക്കം ചെയ്യുക",
        custom: "കസ്റ്റം",
        phone: "ഫോൺ",
        actions: "പ്രവർത്തനങ്ങൾ",
        number: "നമ്പർ",
        nonACDefault: "നോൺ എസി (ഡിഫോൾട്ട്)",
        saving: "സംരക്ഷിക്കുന്നു...",
        qrTable: "ടേബിളിനുള്ള QR",
        print: "പ്രിന്റ്",
        ordersForTable: "ടേബിളിനുള്ള ഓർഡറുകൾ",
        order: "ഓർഡർ",
        payment: "പേയ്‌മെന്റ്",
        description: "വിവരണം",
        noImage: "ചിത്രമില്ല",
        addItemSub: "പുതിയ ഇനം ചേർക്കുക",
        addTableSub: "പുതിയ ടേബിൾ ചേർക്കുക",
        addGroupSub: "പുതിയ ഗ്രൂപ്പ് ചേർക്കുക",
        kitchenDisplay: "അടുക്കള ഡിസ്പ്ലേ",
        yesterday: "ഇന്നലെ",
        date: "തീയതി",
        name: "പേര്",
        connected: "ബന്ധിപ്പിച്ചു",
        disconnected: "വിച്ഛേദിച്ചു",
        active: "സജീവം",
        completed: "പൂർത്തിയായി",
        time: "സമയം",
        allOrdersCleared: "എല്ലാ ഓർഡറുകളും മായ്ച്ചു!",
        greatJob: "നല്ല ജോലി, വേഗത നിലനിർത്തുക.",
        startPreparation: "തയ്യാറാക്കൽ ആരംഭിക്കുക",
        markReady: "തയ്യാറാണെന്ന് അടയാളപ്പെടുത്തുക",
        complete: "പൂർത്തിയാക്കുക",
        analyticsDashboard: "അനലിറ്റിക്സ് ഡാഷ്ബോർഡ്",
        analyticsDescription: "നിങ്ങളുടെ റെസ്റ്റോറന്റ് പ്രകടനത്തെക്കുറിച്ചുള്ള തത്സമയ വിവരങ്ങൾ",
        totalOrders: "ആകെ ഓർഡറുകൾ",
        totalRevenue: "ആകെ വരുമാനം",
        tablesServed: "സേവിച്ച ടേബിളുകൾ",
        avgOrderValue: "ശരാശരി ഓർഡർ മൂല്യം",
        businessInsights: "ബിസിനസ്സ് വിവരങ്ങൾ",
        topPerformer: "മികച്ച പ്രകടനം",
        peakHour: "തിരക്കുള്ള സമയം",
        paymentPreference: "പേയ്മെന്റ് മുൻഗണന",
        categoryOpportunity: "വിഭാഗം അവസരം",
        newCustomers: "പുതിയ ഉപഭോക്താക്കൾ",
        returningCustomers: "തിരിച്ചെത്തുന്ന ഉപഭോക്താക്കൾ",
        downloadExcel: "Excel ഡൗൺലോഡ് ചെയ്യുക",
        downloadPDF: "PDF ഡൗൺലോഡ് ചെയ്യുക",
        daily: "ദിവസേന",
        weekly: "പ്രതിവാര",
        monthly: "പ്രതിമാസ",
        yearly: "വാർഷിക",
        today: "ഇന്ന്",
        overview: "അവലോകനം",
        revenue: "വരുമാനം",
        items: "ഇനങ്ങൾ",
        revenueByBranch: "ശാഖ തിരിച്ചുള്ള വരുമാനം",
        ordersByBranch: "ശാഖ തിരിച്ചുള്ള ഓർഡറുകൾ",
        topItemsByBranch: "ശാഖ തിരിച്ചുള്ള മികച്ച ഇനങ്ങൾ",
        paymentReconciliation: "ഓർഡറുകളും പേയ്‌മെന്റുകളും താരതമ്യം",
        last7: "കഴിഞ്ഞ 7 ദിവസങ്ങൾ",
        operations: "പ്രവർത്തനങ്ങൾ",
        customers: "ഉപഭോക്താക്കൾ",
        manageInventory: "നിങ്ങളുടെ ഇൻവെന്ററിയും സ്റ്റോക്ക് ലെവലുകളും നിയന്ത്രിക്കുക",
        backToAdmin: "അഡ്മിനിലേക്ക് തിരികെ",
        addIngredient: "ചേരുവ ചേർക്കുക",
        totalIngredients: "ആകെ ചേരുവകൾ",
        lowStockItems: "കുറഞ്ഞ സ്റ്റോക്ക് ഇനങ്ങൾ",
        searchIngredients: "ചേരുവകൾ തിരയുക...",
        quantity: "അളവ്",
        unit: "യൂണിറ്റ്",
        threshold: "പരിധി",
        lowStock: "കുറഞ്ഞ സ്റ്റോക്ക്",
        inStock: "കൈവശമുണ്ട്",
        editIngredient: "ചേരുവ എഡിറ്റ് ചെയ്യുക",
        deleteIngredientConfirm: "ഈ ചേരുവ നീക്കം ചെയ്യാൻ നിങ്ങൾ ആഗ്രഹിക്കുന്നുവോ?",
        saveIngredient: "ചേരുവ സംരക്ഷിക്കുക",
        lowStockThreshold: "കുറഞ്ഞ സ്റ്റോക്ക് പരിധി",
        enterPin: "PIN നൽകുക",
        verifying: "പരിശോധിക്കുന്നു...",
        staffDashboard: "സ്റ്റാഫ് ഡാഷ്ബോർഡ്",
        attendance: "ഹാജർ",
        leaves: "അവധി",
        punchIn: "പഞ്ച് ഇൻ",
        punchOut: "പഞ്ച് ഔട്ട്",
        attendanceHistory: "ഹാജർ ചരിത്രം",
        allStaffAttendance: "എല്ലാ സ്റ്റാഫ് ഹാജരും",
        last7Days: "കഴിഞ്ഞ 7 ദിവസങ്ങൾ",
        thisMonth: "ഈ മാസം",
        customRange: "കസ്റ്റം റേഞ്ച്",
        staffName: "സ്റ്റാഫ് പേര്",
        checkIn: "ഇൻ",
        checkOut: "ഔട്ട്",
        hours: "മണിക്കൂർ",
        newLeaveRequest: "പുതിയ അവധി അപേക്ഷ",
        fromDate: "മുതൽ",
        toDate: "വരെ",
        reason: "കാരണം",
        submitRequest: "അപേക്ഷ സമർപ്പിക്കുക",
        allLeaveRequests: "എല്ലാ അവധി അപേക്ഷകളും",
        myLeaveHistory: "എന്റെ അവധി ചരിത്രം",
        noAttendanceRecords: "ഹാജർ രേഖകളൊന്നും കണ്ടെത്തിയില്ല",
        noLeaveRequests: "അവധി അപേക്ഷകളൊന്നും കണ്ടെത്തിയില്ല",
        approve: "അംഗീകരിക്കുക",
        reject: "നിരസിക്കുക",
        allBranchesCombined: "എല്ലാ ശാഖകളും",
        firstIn: "ആദ്യ പ്രവേശനം",
        lastOut: "അവസാന പുറത്തുപോകൽ",
        dailyRate: "പ്രതിദിന നിരക്ക്",
        download: "ഡൗൺലോഡ്",
        refresh: "പുതുക്കുക",
        attendanceManagement: "ഹാജർ മാനേജ്മെന്റ്",
        selectBranch: "ശാഖ തിരഞ്ഞെടുക്കുക",
        viewingDataFor: "ഡാറ്റ കാണുന്നു",
        filteredByBranchInfo: "എല്ലാ മെനു ഇനങ്ങളും, ഓർഡറുകളും, ചേരുവകളും, അനലിറ്റിക്സും ഈ ശാഖയിലേക്ക് ഫിൽട്ടർ ചെയ്തിരിക്കുന്നു.",
        revenueTrend: "വരുമാന പ്രവണത",
        ordersOverview: "ഓർഡറുകളുടെ അവലോകനം",
        revenueBreakdown: "വരുമാന വിശകലനം",
        revenueByCategory: "വിഭാഗം തിരിച്ചുള്ള വരുമാനം",
        itemPerformance: "ഇനങ്ങളുടെ പ്രകടനം",
        topSellingItems: "ഏറ്റവും കൂടുതൽ വിറ്റഴിക്കപ്പെടുന്ന ഇനങ്ങൾ",
        categoryPerformance: "വിഭാഗങ്ങളുടെ പ്രകടനം",
        topItemsDetails: "പ്രധാന ഇനങ്ങളുടെ വിശദാംശങ്ങൾ",
        customerAnalysis: "ഉപഭോക്തൃ വിശകലനം",
        customerRetention: "ഉപഭോക്തൃ നിലനിർത്തൽ",
        customerMetrics: "ഉപഭോക്തൃ അളവുകൾ",
        retentionRate: "നിലനിർത്തൽ നിരക്ക്",
        operationsAnalysis: "പ്രവർത്തന വിശകലനം",
        hourlyOrderDistribution: "മണിക്കൂർ തിരിച്ചുള്ള ഓർഡർ വിതരണം",
        tablePerformance: "ടേബിൾ പ്രകടനം",
        tablePerformanceDetails: "ടേബിൾ പ്രകടന വിശദാംശങ്ങൾ",
        from: "മുതൽ",
        to: "വരെ",
        tryAdjustingFilters: "തീയതി ഫിൽട്ടറുകൾ ക്രമീകരിക്കാൻ ശ്രമിക്കുക",
        noAttendanceToExport: "കയറ്റുമതി ചെയ്യാൻ ഹാജർ രേഖകളൊന്നുമില്ല.",
        totalWorkingHours: "ആകെ ജോലി സമയം",
        workingHours: "ജോലി സമയം",
        workSessionDetails: "ജോലി സെഷൻ വിശദാംശങ്ങൾ",
        duration: "ദൈർഘ്യം",
        closeDetails: "വിശദാംശങ്ങൾ അടയ്ക്കുക",
        noLeaveRequestsFound: "അവധി അപേക്ഷകളൊന്നും കണ്ടെത്തിയില്ല",
        festivalOffer: "ഫെസ്റ്റിവൽ ഓഫർ",
        festivalName: "ഫെസ്റ്റിവൽ പേര്",
        festivalDiscount: "ഫെസ്റ്റിവൽ ഡിസ്കൗണ്ട് (%)",
        festivalDesc: "ഈ ഡിസ്കൗണ്ട് മൊത്തം ബില്ലിന് ബാധകമാകും",
        festivalPlaceholder: "ഉദാ: ദീപാവലി, ക്രിസ്മസ്, ന്യൂ ഇയർ",
        billingSettings: "ബില്ലിംഗ് & പേയ്‌മെന്റ് ക്രമീകരണങ്ങൾ",
        unlockToEdit: "തിരുത്താൻ അൺലോക്ക് ചെയ്യുക",
        unlocked: "അൺലോക്ക് ചെയ്തു",
        paymentSettingsLocked: "പേയ്‌മെന്റ് ക്രമീകരണങ്ങൾ ലോക്ക് ചെയ്തിരിക്കുന്നു",
        lockMessage: "സുരക്ഷാ കാരണങ്ങളാൽ, നിങ്ങളുടെ QR കോഡുകൾ, UPI ക്രമീകരണങ്ങൾ, ബില്ലിംഗ് ഘടന എന്നിവ ലോക്ക് ചെയ്തിരിക്കുന്നു. നിങ്ങളുടെ അഡ്മിൻ പാസ്‌വേഡ് ഉപയോഗിച്ച് അൺലോക്ക് ചെയ്യുക.",
        unlockNow: "ഇപ്പോൾ അൺലോക്ക് ചെയ്യുക",
        sgst: "SGST (%)",
        cgst: "CGST (%)",
        upiIdLabel: "Click-to-Pay-യ്ക്കുള്ള UPI ID",
        upiIdPlaceholder: "ഉദാ: yourname@okaxis",
        upiIdDesc: "ഒരു UPI ID നൽകുന്നത് ബില്ലിൽ GPay, PhonePe, Paytm ബട്ടണുകൾ പ്രവർത്തനക്ഷമമാക്കും.",
        paymentQRCodes: "പേയ്‌മെന്റ് QR കോഡുകൾ",
        addQR: "QR ചേർക്കുക",
        renamedSuccess: "പേര് വിജയകരമായി മാറ്റി",
        renameFailed: "പേര് മാറ്റുന്നതിൽ പരാജയപ്പെട്ടു",
        allBranchesFloating: "എല്ലാ ബ്രാഞ്ചുകളും (ഫ്ലോട്ടിംഗ്)",
        itemNamePlaceholder: "ഇനത്തിന്റെ പേര് (ഉദാ: ഗ്രിൽഡ് ചിക്കൻ)",
        itemDescriptionPlaceholder: "ഇനത്തിന്റെ ഹ്രസ്വ വിവരണം",
        tableNumberPlaceholder: "ടേബിൾ നമ്പർ (ഉദാ: 1)",
        tableNamePlaceholder: "ടേബിൾ പേര് (ഉദാ: ഗാർഡൻ 1)",
        roleTypeAdd: "റോൾ തരം ചേർക്കുക",
        newCategoryName: "പുതിയ വിഭാഗത്തിന്റെ പേര്",
        globalSelfService: "ഗ്ലോബൽ സെൽഫ് സർവീസ്",
        globalSelfServiceDesc: "എല്ലാ വിഭാഗങ്ങളിലും ഒരേസമയം സെൽഫ് സർവീസ് ഓൺ/ഓഫ് ചെയ്യുക",
        upgradeForAllLanguages: "എല്ലാ 8 ഭാഷകൾക്കുമായി പ്രീമിയത്തിലേക്ക് അപ്‌ഗ്രേഡ് ചെയ്യുക",
        subscribeForLanguages: "ഭാഷാ പിന്തുണയ്ക്കായി സബ്‌സ്‌ക്രൈബ് ചെയ്യുക",
        tablesUsed: "{current}/{total} ടേബിളുകൾ ഉപയോഗിച്ചു",
        itemsUsed: "{current}/{total} ഇനങ്ങൾ ഉപയോഗിച്ചു",
        noMenuItemsToExport: "കയറ്റുമതി ചെയ്യാൻ മെനു ഇനങ്ങളൊന്നുമില്ല",
        noDataInFile: "ഫയലിൽ ഡാറ്റയൊന്നും കണ്ടെത്തിയില്ല",
        itemsImportedSuccessfully: "{count} ഇനങ്ങൾ വിജയകരമായി ഇറക്കുമതി ചെയ്യപ്പെട്ടു!",
        manageCategories: "വിഭാഗങ്ങൾ നിയന്ത്രിക്കുക",
        categoryNamePlaceholder: "പുതിയ വിഭാഗത്തിന്റെ പേര്",
        upgradeForBulk: "ബൾക്ക് അപ്‌ലോഡ്/ഡൗൺലോഡ് എന്നിവയ്ക്കായി അപ്‌ഗ്രേഡ് ചെയ്യുക",
        unlockSettings: "ക്രമീകരണങ്ങൾ അൺലോക്ക് ചെയ്യുക",
        enterPasswordToProceed: "തുടരുന്നതിന് ദയവായി നിങ്ങളുടെ അഡ്മിൻ പാസ്‌വേഡ് നൽകുക",
        settingsUnlocked: "ക്രമീകരണങ്ങൾ അൺലോക്ക് ചെയ്തു!",
        invalidPassword: "അസാധുവായ പാസ്‌വേഡ്",
        verificationFailed: "പരിശോധന പരാജയപ്പെട്ടു",
        adminEmailNotFound: "അഡ്മിൻ ഇമെയിൽ കണ്ടെത്തിയില്ല",
        sendingResetLink: "റീസെറ്റ് ലിങ്ക് അയയ്ക്കുന്നു...",
        branchMgmt: "ബ്രാഞ്ച് മാനേജ്‌മെന്റ്",
        branchMgmtSub: "നിങ്ങളുടെ ലൊക്കേഷനുകളും ബ്രാഞ്ചുകളും മാനേജ് ചെയ്യുക",
        addNewBranch: "പുതിയ ബ്രാഞ്ച് ചേർക്കുക",
        loadingBranches: "ബ്രാഞ്ചുകൾ ലോഡ് ചെയ്യുന്നു...",
        noBranchesYet: "ബ്രാഞ്ചുകളൊന്നും ലഭ്യമല്ല",
        noBranchesDesc: "നിങ്ങളുടെ ആദ്യത്തെ ബ്രാഞ്ച് അല്ലെങ്കിൽ ലൊക്കേഷൻ ചേർത്ത് ബിസിനസ്സ് വിപുലീകരിക്കുക.",
        createFirstBranch: "ആദ്യത്തെ ബ്രാഞ്ച് സൃഷ്ടിക്കുക",
        branchActiveDesc: "ബ്രാഞ്ച് സജീവമാണ്, ഓർഡറുകൾ സ്വീകരിക്കുന്നു",
        failedLoadBranches: "ബ്രാഞ്ചുകൾ ലോഡ് ചെയ്യുന്നതിൽ പരാജയപ്പെട്ടു",
        branchSavedSuccess: "ബ്രാഞ്ച് വിജയകരമായി സംരക്ഷിച്ചു",
        failedSaveBranch: "ബ്രാഞ്ച് സംരക്ഷിക്കുന്നതിൽ പരാജയപ്പെട്ടു",
        errorSavingBranch: "ബ്രാഞ്ച് സംരക്ഷിക്കുന്നതിൽ പിശക്",
        deleteBranchConfirm: "നിങ്ങൾക്ക് തീർച്ചയായും ഈ ബ്രാഞ്ച് നീക്കം ചെയ്യണോ? ഇത് അനുബന്ധ ഡാറ്റയെയും ബാധിക്കും.",
        branchDeletedSuccess: "ബ്രാഞ്ച് വിജയകരമായി നീക്കം ചെയ്തു",
        failedDeleteBranch: "ബ്രാഞ്ച് നീക്കം ചെയ്യുന്നതിൽ പരാജയപ്പെട്ടു",
        errorDeletingBranch: "ബ്രാഞ്ച് നീക്കം ചെയ്യുന്നതിൽ പിശക്",
        createBranch: "ബ്രാഞ്ച് സൃഷ്ടിക്കുക",
        updateBranch: "ബ്രാഞ്ച് അപ്‌ഡേറ്റ് ചെയ്യുക",
        confirmDeletion: "നീക്കം ചെയ്യൽ സ്ഥിരീകരിക്കുക",
        branchName: "ബ്രാഞ്ചിന്റെ പേര്",
        branchNamePlaceholder: "ഉദാ: ടൗൺ ബ്രാഞ്ച്, എയർപോർട്ട് ലൊക്കേഷൻ",
        managerName: "മാനേജരുടെ പേര്",
        managerNamePlaceholder: "ബ്രാഞ്ച് മാനേജരുടെ പേര്",
        addressPlaceholder: "ബ്രാഞ്ച് ലൊക്കേഷന്റെ പൂർണ്ണ വിലാസം",
        leaveEmptyForNoBranch: "ബ്രാഞ്ചില്ല എന്നതിന് ശൂന്യമായി വിടുക",
        allowedBranches: "അനുവദനീയമായ ബ്രാഞ്ചുകൾ",
        inactive: "നിഷ്ക്രിയം",
        all: "എല്ലാം",
        unlock: "അൺലോക്ക്",
        lock: "ലോക്ക്",
        refreshing: "എല്ലാ ഡാറ്റയും പുതുക്കുന്നു...",
        refreshed: "എല്ലാ ഡാറ്റയും പുതുക്കി!",
        refreshFailed: "പുതുക്കാൻ കഴിഞ്ഞില്ല",
        statusUpdated: "നില പുതുക്കി!",
        updateFailed: "പുതുക്കാൻ കഴിഞ്ഞില്ല",
        failedLoadMenu: "മെനു ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല",
        categoryAdded: "വിഭാഗം ചേർത്തു!",
        updatedSuccessfully: "വിജയകരമായി പുതുക്കി",
        enabledForAll: "എല്ലാവർക്കും പ്രവർത്തനക്ഷമമാക്കി",
        disabledForAll: "എല്ലാവർക്കും പ്രവർത്തനരഹിതമാക്കി",
        bulkUpdateFailed: "ബൾക്ക് അപ്‌ഡേറ്റ് പരാജയപ്പെട്ടു",
        enterGroupName: "ഗ്രൂപ്പ് പേര് നൽകുക",
        deleted: "ഡിലീറ്റ് ചെയ്തു",
        error: "പിശക്",
        printedPopupBlocked: "പ്രിന്റ് ചെയ്തു (പോപ്പ്അപ്പ് ബ്ലോക്ക് ചെയ്തു)",
        qrSentToPrinter: "QR പ്രിന്ററിലേക്ക് അയച്ചു",
        printFailed: "പ്രിന്റ് ചെയ്യാൻ കഴിഞ്ഞില്ല",
        uploadingLogo: "ലോഗോ അപ്‌ലോഡ് ചെയ്യുന്നു...",
        logoUploaded: "ലോഗോ അപ്‌ലോഡ് ചെയ്തു!",
        uploadingBanner: "ബാനർ അപ്‌ലോഡ് ചെയ്യുന്നു...",
        bannerUploaded: "ബാനർ അപ്‌ലോഡ് ചെയ്തു!",
        generationFailed: "ഉണ്ടാക്കാൻ കഴിഞ്ഞില്ല",
        uploadingQR: "QR അപ്‌ലോഡ് ചെയ്യുന്നു...",
        qrAdded: "QR കോഡ് ചേർത്തു!",
        deleteStaffConfirm: "ഈ സ്റ്റാഫ് അംഗത്തെ ഡിലീറ്റ് ചെയ്യണമെന്ന് ഉറപ്പാണോ?",
        deleteGroupConfirm: "ഗ്രൂപ്പ് ഡിലീറ്റ് ചെയ്യണോ? ഈ ഗ്രൂപ്പിലെ ടേബിളുകൾ Non AC ആയി മാറും.",
        deleteRoleConfirm: "ഈ റോൾ ഡിലീറ്റ് ചെയ്യണമെന്ന് ഉറപ്പാണോ?",
        deleteUserConfirm: "ഈ ഉപയോക്താവിനെ ഡിലീറ്റ് ചെയ്യണമെന്ന് ഉറപ്പാണോ?",
        deleteCategoryConfirm: "\"{name}\" വിഭാഗം ഡിലീറ്റ് ചെയ്യണോ? അതിലുള്ള ഇനങ്ങൾ മറ്റ് വിഭാഗങ്ങളിലേക്ക് മാറും.",
        optionalDiscountDesc: "ഓപ്ഷണൽ: ഡിസ്കൗണ്ട് ശതമാനം ചേർക്കുക (ഉദാ. 10% ഡിസ്കൗണ്ടിന് 10)",
        paymentSettings: "പേയ്‌മെന്റ് ക്രമീകരണങ്ങൾ",
        festivalOfferDesc: "ഈ ഡിസ്കൗണ്ട് മൊത്തം ബില്ലിൽ ബാധകമാകും",
        failedResetEmail: "റീസെറ്റ് ഇമെയിൽ അയയ്ക്കുന്നതിൽ പരാജയപ്പെട്ടു",
    },


    te: {
        present: "హాజరు",
        noBranch: "బ్రాంచ్ లేదు",
        price: "ధర",
        currency: "కరెన్సీ",
        pending: "పెండింగ్",
        menu: "మెనూ",
        orders: "ఆర్డర్లు",
        absent: "హాజరుకాని",
        totalHours: "మొత్తం గంటలు",
        avgHours: "సగటు గంటలు",
        totalAmount: "మొత్తం మొత్తం",
        trialExpiredBanner: "మీ ఉచిత ట్రయల్ గడువు ముగిసింది. కొన్ని ఫంక్షనాలిటీలు పరిమితం కావచ్చు.",
        trialExpiresToday: "⚠️ మీ ఉచిత ట్రయల్ ఈరోజుతో ముగుస్తుంది! యాక్సెస్‌ను కొనసాగించడానికి ఇప్పుడే అప్‌గ్రేడ్ చేయండి.",
        trialDaysLeft: "🎉 మీ 20 రోజుల ఉచిత యాక్సెస్ పీరియడ్‌లో మీకు ఇంకా {days} రోజులు ఉన్నాయి。",
        subscribeNow: "ఇప్పుడే సబ్స్క్రయిబ్ చేయండి",
        upgradePlan: "ప్లాన్ అప్‌గ్రేడ్ చేయండి",
        trialExpiredTitle: "ట్రయల్ పీరియడ్ ముగిసింది",
        trialExpiredDesc: "<b>{name}</b>కి మీ 20 రోజుల ఉచిత యాక్సెస్ ముగిసింది。<br/>మీ రెస్టారెంట్‌ను నిర్వహించడం కొనసాగించడానికి, దయచేసి ప్రీమియం ప్లాన్‌కు అప్‌గ్రేడ్ చేయండి。",
        festivalPlaceholder: "ఉదా: దీపావలి, క్రిస్మస్, నూతన సంవత్సర వేడుకలు",
        billingSettings: "బిల్లింగ్ & పేమెంట్ సెట్టింగ్స్",
        unlockToEdit: "సవరించడానికి అన్‌లాక్ చేయండి",
        unlocked: "అన్‌లాక్ చేయబడింది",
        paymentSettingsLocked: "పేమెంట్ సెట్టింగ్స్ లాక్ చేయబడ్డాయి",
        lockMessage: "భద్రత కారణాల దృష్ట్యా, మీ QR కోడ్‌లు మరియు UPI సెట్టింగ్స్ మరియు బిల్లింగ్ ఫోర్మాట్ లాక్ చేయబడ్డాయి. దయచేసి మీ అడ్మిన్ పాస్‌వర్డ్‌తో అన్‌లాక్ చేయండి.",
        unlockNow: "ఇప్పుడే అన్‌లాక్ చేయండి",
        sgst: "SGST (%)",
        cgst: "CGST (%)",
        upiIdLabel: "క్లిక్-టు-పే కోసం UPI ID",
        upiIdPlaceholder: "ఉదా: yourname@okaxis",
        paymentQRCodes: "పేమెంట్ QR కోడ్‌లు",
        addQR: "QR జోడించు",
        renamedSuccess: "విజయవంతంగా పేరు మార్చబడింది",
        renameFailed: "పేరు మార్చడం విఫలమైంది",
        allBranchesFloating: "అన్ని బ్రాంచీలు (ఫ్లోటింగ్)",
        scanToAccessStaff: "చెక్-ఇన్/అవుట్ కోసం స్కాన్ చేయండి",
        roleTypeAdd: "రోల్ రకాన్ని జోడించండి",
        newCategoryName: "కొత్త కేటగిరీ పేరు",
        upgradeForAllLanguages: "అన్ని 8 భాషల కోసం ప్రీమియంకు అప్‌గ్రేడ్ చేయండి",
        subscribeForLanguages: "భాషల సపోర్ట్ కోసం సబ్స్క్రయిబ్ చేయండి",
        tablesUsed: "{current}/{total} టేబుల్స్ ఉపయోగించబడ్డాయి",
        itemsUsed: "{current}/{total} ఐటమ్స్ ఉపయోగించబడ్డాయి",
        noMenuItemsToExport: "ఎగుమతి చేయడానికి ఎటువంటి మెనూ ఐటమ్స్ లేవు",
        noDataInFile: "ఫైల్‌లో డేటా కనుగొనబడలేదు",
        itemsImportedSuccessfully: "{count} ఐటమ్స్ విజయవంతంగా ఇంపోర్ట్ చేయబడ్డాయి!",
        upgradeForBulk: "బల్క్ అప్‌లోడ్/డౌన్‌లోడ్ కోసం అప్‌గ్రేడ్ చేయండి",
        unlockSettings: "సెట్టింగ్స్ అన్‌లాక్ చేయండి",
        enterPasswordToProceed: "కొనసాగడానికి దయచేసి మీ అడ్మిన్ పాస్‌వర్డ్ ఎంటర్ చేయండి",
        settingsUnlocked: "సెట్టింగ్స్ అన్‌లాక్ చేయబడ్డాయి!",
        invalidPassword: "చెల్లని పాస్‌వర్డ్",
        verificationFailed: "ధృవీకరణ విఫలమైంది",
        adminEmailNotFound: "అడ్మిన్ ఈమెయిల్ కనుగొనబడలేదు",
        sendingResetLink: "రీసెట్ లింక్ పంపుతోంది...",
        detailedAnalytics: "వివరమైన అనలిటిక్స్",
        support247: "24/7 సపోర్ట్",
        upgradeNowToContinue: "కొనసాగించడానికి ఇప్పుడే అప్‌గ్రేడ్ చేయండి",
        switchBranch: "బ్రాంచ్ మార్చండి",
        whyChooseTitle: "రెడ్‌సార్మ్‌ను ఎందుకు ఎంచుకోవాలి?",
        mobileFirstTitle: "మొబైల్ ఫస్ట్",
        mobileFirstDesc: "రెస్పాన్సివ్ డిజైన్ మరియు టచ్-ఫ్రెండ్లీ ఇంటర్‌ఫేస్‌తో అన్ని పరికరాల కోసం ఆప్టిమైజ్ చేయబడింది",
        lightningFastTitle: "మెరుపు వేగం",
        lightningFastDesc: "సమర్థవంతమైన రెస్టారెంట్ కార్యకలాపాల కోసం శీఘ్ర ఆర్డర్ ప్లేస్‌మెంట్ మరియు రియల్ టైమ్ అప్‌డేట్‌లు",
        secureReliableTitle: "సురక్షితమైనది & నమ్మదగినది",
        secureReliableDesc: "ఎన్‌క్రిప్టెడ్ పేమెంట్స్ మరియు డేటా ప్రొటెక్షన్‌తో ఎంటర్‌ప్రైజ్-గ్రేడ్ సెక్యూరిటీ",
        testimonialQuote: "రెడ్‌సార్మ్ మా రెస్టారెంట్ కార్యకలాపాలను మార్చివేసింది. ఆర్డర్‌లు 40% వేగంగా ప్రాసెస్ చేయబడుతున్నాయి మరియు మా కస్టమర్‌లు వారి టేబుల్స్ నుండి ఆర్డర్ చేసే సౌకర్యాన్ని ఇష్టపడుతున్నారు.",
        testimonialAuthor: "సారా జాన్సన్",
        testimonialRole: "యజమాని, ది గార్డెన్ బిస్ట్రో",
        footerTagline: "మెరుగైన డైనింగ్ అనుభవాల కోసం ఆధునిక QR-ఆధారిత రెస్టారెంట్ ఆర్డరింగ్ సిస్టమ్.",
        copyright: "అన్ని హక్కులు ప్రత్యేకించబడ్డాయి.",
        product: "ఉత్పత్తి",
        company: "కంపెనీ",
        connect: "కనెక్ట్",
        about: "మా గురించి",
        blog: "బ్లాగ్",
        careers: "కెరీర్",
        adminDashboard: "అడ్మిన్ డ్యాష్‌బోర్డ్",
        clickToEditProfile: "ప్రొఫైల్‌ను సవరించడానికి క్లిక్ చేయండి",
        logout: "లాగ్ అవుట్",
        groups: "గ్రూప్స్",
        staff: "స్టాఫ్",
        users: "యూజర్స్",
        roles: "రోల్స్",
        branches: "బ్రాంచీలు",
        branchComparison: "బ్రాంచ్ పోలిక",
        menuEmpty: "మీ మెనూ ఖాళీగా ఉంది",
        menuEmptyDesc: "మీ మెనూకు రుచికరమైన ఐటమ్స్ జోడించడం ద్వారా ప్రారంభించండి. మీరు వాటిని వర్గాలుగా నిర్వహించవచ్చు మరియు పోషకాహార సమాచారాన్ని రూపొందించడానికి AIని కూడా ఉపయోగించవచ్చు!",
        noTables: "టేబుల్స్ ఏవీ కనుగొనబడలేదు",
        noTablesDesc: "టేబుల్స్ జోడించడం ద్వారా మీ రెస్టారెంట్ లేఅవుట్‌ను సెటప్ చేయండి. మీరు వాటిని తర్వాత గ్రూపులకు (ఏసీ, గార్డెన్ వంటివి) కేటాయించవచ్చు.",
        noGroups: "ఇంకా టేబుల్ గ్రూపులు లేవు",
        noGroupsDesc: "గ్రూపులు మీ టేబుల్స్‌ను నిర్వహించడంలో మీకు సహాయపడతాయి (ఉదా., 'ఏసీ సెక్షన్', 'అవుట్‌డోర్', 'మొదటి అంతస్తు').",
        companySettings: "కంపెనీ సెట్టింగ్స్",
        companyName: "కంపెనీ పేరు",
        logo: "లోగో",
        banner: "బ్యానర్",
        saveChanges: "మార్పులను సేవ్ చేయండి",
        staffDirectory: "స్టాఫ్ డైరెక్టరీ",
        portalQr: "పోర్టల్ QR",
        editStaff: "స్టాఫ్ సవరించు",
        addStaff: "స్టాఫ్ జోడించు",
        updateStaff: "స్టాఫ్ అప్‌డేట్ చేయండి",
        fullName: "పూర్తి పేరు",
        signIn: "సైన్ ఇన్",
        signUp: "సైన్ అప్",
        enterFullName: "మీ పూర్తి పేరును నమోదు చేయండి",
        enterPhone: "మీ ఫోన్ నంబర్‌ను నమోదు చేయండి",
        enterEmail: "మీ ఈమెయిల్‌ను నమోదు చేయండి",
        enterPassword: "మీ పాస్‌వర్డ్‌ను నమోదు చేయండి",
        alreadyHaveAccount: "ఇప్పటికే ఖాతా ఉందా?",
        needAccount: "ఖాతా కావాలా?",
        heroDescription: "కస్టమర్ అనుభవాన్ని మెరుగుపరిచే మరియు కార్యకలాపాలను క్రమబద్ధీకరించే ఆధునిక QR-ఆధారిత రెస్టారెంట్ ఆర్డరింగ్ సిస్టమ్",
        customerExperience: "కస్టమర్ అనుభవం",
        customerDesc: "అతుకులు లేని QR-ఆధారిత ఆర్డరింగ్, నిజ-సమయ ట్రాకింగ్ మరియు తక్షణ చెల్లింపులు.",
        kitchenDisplayFeature: "కిచెన్ డిస్ప్లే",
        kitchenDesc: "సమర్థవంతమైన ఆర్డర్ నిర్వహణ, స్థితి నవీకరణలు మరియు ప్రిపరేషన్ ట్రాకింగ్.",
        adminDashboardFeature: "అడ్మిన్ డ్యాష్‌బోర్డ్",
        adminDesc: "మెనూ, టేబుల్స్ మరియు రెస్టారెంట్ సెట్టింగ్స్ యొక్క సమగ్ర నిర్వహణ.",
        analyticsReports: "అనలిటిక్స్ & రిపోర్ట్స్",
        analyticsDesc: "అమ్మకాలు, జనాదరణ పొందిన ఐటమ్స్ మరియు పీక్ అవర్స్ గురించి లోతైన అవగాహన.",
        ingredientsDesc: "ఇన్వెంటరీని ట్రాక్ చేయండి, స్టాక్ లెవల్స్ నిర్వహించండి మరియు తక్కువ స్టాక్ హెచ్చరికలను పొందండి.",
        staffDesc: "స్టాఫ్ లాగిన్, హాజరు ట్రాకింగ్ మరియు లీవ్ మేనేజ్మెంట్.",
        viewCustomerApp: "కస్టమర్ యాప్‌ను చూడండి",
        viewKitchenDisplay: "కిచెన్ డిస్ప్లే చూడండి",
        viewAdminPanel: "అడ్మిన్ ప్యానెల్ చూడండి",
        viewAnalytics: "అనలిటిక్స్ చూడండి",
        manageIngredients: "ఇన్ గ్రీడియంట్స్ నిర్వహించండి",
        staffLogin: "స్టాఫ్ లాగిన్",
        activeSubscription: "యాక్టివ్ సబ్‌స్క్రిప్షన్",
        plan: "ప్లాన్",
        validUntil: "వరకు చెల్లుబాటు అవుతుంది",
        securePaymentNote: "Cashfree ద్వారా సురక్షిత చెల్లింపు • ఎప్పుడైనా రద్దు చేయండి",
        settings: "సెట్టింగ్స్",
        addFirstItem: "మీ మొదటి ఐటమ్‌ను జోడించండి",
        addFirstTable: "మీ మొదటి టేబుల్‌ను జోడించండి",
        allGroups: "అన్ని గ్రూపులు",
        waiter: "వెయిటర్",
        kitchenStaff: "కిచెన్ స్టాఫ్",
        manager: "మేనేజర్",
        chef: "షెఫ్",
        cashier: "క్యాషియర్",
        cleaner: "క్లీనర్",
        noStaff: "ఇంకా సిబ్బందిని చేర్చలేదు",
        noStaffDesc: "సిబ్బందిని చేర్చడం ద్వారా ప్రారంభించండి.",
        staffPortalQr: "స్టాఫ్ పోర్టల్ QR",
        custom: "కస్టమ్",
        phone: "ఫోన్",
        number: "నెంబర్",
        description: "వివరణ",
        noImage: "చిత్రం లేదు",
        addItemSub: "కొత్త ఐటమ్ జోడించు",
        addTableSub: "కొత్త టేబుల్ జోడించు",
        addGroupSub: "కొత్త గ్రూప్ జోడించు",
        kitchenDisplay: "వంటగది ప్రదర్శన",
        preparing: "తయారవుతోంది",
        ready: "సిద్ధంగా ఉంది",
        delivered: "డెలివరీ చేయబడింది",
        yesterday: "నిన్న",
        date: "తేదీ",
        name: "పేరు",
        role: "పాత్ర",
        pin: "పిన్",
        salary: "జీతం",
        salaryInr: "జీతం (₹)",
        pinPlaceholder: "పిన్ (6 అంకెలు)",
        enterRoleName: "పాత్ర పేరును నమోదు చేయండి",
        otherRole: "ఇతర పాత్ర",
        actions: "చర్యలు",
        addUser: "యూజర్ ని జోడించు",
        editUser: "యూజర్‌ని సవరించు",
        deleteUser: "యూజర్‌ని తొలగించు",
        loadingUsers: "యూజర్లను లోడ్ చేస్తోంది...",
        noUsersFound: "యూజర్లు కనుగొనబడలేదు",
        roleName: "పాత్ర పేరు",
        roleNameExample: "ఉదా: సీనియర్ షెఫ్, నైట్ మేనేజర్",
        permissions: "అనుమతులు",
        loadingRoles: "పాత్రలను లోడ్ చేస్తోంది...",
        noRolesFound: "పాత్రలు కనుగొనబడలేదు",
        editRole: "పాత్రను సవరించు",
        deleteRole: "పాత్రను తొలగించు",
        createRole: "పాత్రను సృష్టించండి",
        updateRole: "పాత్రను అప్‌డేట్ చేయండి",
        manageCategories: "వర్గాలను నిర్వహించండి",
        globalSelfService: "గ్లోబల్ సెల్ఫ్ సర్వీస్",
        globalSelfServiceDesc: "అన్ని వర్గాలకు పికప్ మోడ్‌ను ఎనేబుల్ చేయండి",
        selfService: "సెల్ఫ్ సర్వీస్ (కస్టమర్ ఆర్డర్‌ను పికప్ చేస్తారు)",
        markAsSpecial: "प्रत्यేక ఐటమ్‌గా గుర్తించండి",
        specialItemDesc: "ప్రత్యేకంగా గుర్తించినప్పుడు, ఈ ఐటమ్ కస్టమర్ పేజీలోని ప్రీమియం టాప్ సెక్షన్‌లో కనిపిస్తుంది.",
        specialDuration: "ప్రత్యేక ఐటమ్ వ్యవధి",
        expireIn1Day: "1 రోజులో గడువు ముగుస్తుంది",
        expireIn2Days: "2 రోజుల్లో గడువు ముగుస్తుంది",
        expireIn3Days: "3 రోజుల్లో గడువు ముగుస్తుంది",
        expireIn7Days: "7 రోజుల్లో గడువు ముగుస్తుంది",
        expireIn30Days: "30 రోజుల్లో గడువు ముగుస్తుంది",
        branchManagement: "బ్రాంచ్ మేనేజ్‌మెంట్",
        editBranch: "బ్రాంచ్‌ను సవరించు",
        address: "చిరునామా",
        ingredientUpdated: "పదార్థం అప్‌డేట్ చేయబడింది",
        ingredientAdded: "పదార్థం జోడించబడింది",
        failedLoadIngredients: "పదార్థాలను లోడ్ చేయడంలో విఫలమైంది",
        errorSavingIngredient: "పదార్థాన్ని సేవ్ చేయడంలో లోపం",
        errorDeletingIngredient: "పదార్థాన్ని తొలగించడంలో లోపం",
        paymentMethods: "చెల్లింపు పద్ధతులు",
        paymentMethod: "చెల్లింపు పద్ధతి",
        paymentStatus: "చెల్లింపు స్థితి",
        revenue: "రాబడి",
        items: "ఐటమ్స్",
        busiestTimeDesc: "{hour}:00 అనేది {orders} ఆర్డర్లతో మీ అత్యంత రద్దీగా ఉండే సమయం.",
        topPerformerDesc: "{name} అనేది {orders} ఆర్డర్లతో మీ అత్యధికంగా అమ్ముడవుతున్న ఐటమ్.",
        preferredPaymentDesc: "కస్టమర్లు {method} చెల్లింపులను ఇష్టపడుతున్నారు ({orders} ఆర్డర్లు).",
        fewestOrdersDesc: "{category} కి అతి తక్కువ ఆర్డర్లు ఉన్నాయి. అమ్మకాలను పెంచడానికి ప్రమోషన్‌లను పరిగణించండి.",
        loyaltyProgramDesc: "లాయల్టీ ప్రోగ్రామ్‌లను పరిగణించండి.",
        phonePlaceholder: "999-999-9999",
        itemNamePlaceholder: "ఐటమ్ పేరు (ఉదా: గ్రిల్డ్ చికెన్)",
        itemPricePlaceholder: "ధర (₹)",
        itemDescriptionPlaceholder: "ఐటమ్ గురించి చిన్న వివరణ",
        tableNamePlaceholder: "టేబుల్ పేరు/నంబర్ (ఉదా: టేబుల్ 1)",
        capacityPlaceholder: "సామర్థ్యం (ఉదా: 4)",
        categoryNamePlaceholder: "కొత్త వర్గం పేరు",
        ingredientNamePlaceholder: "పదార్థం పేరు (ఉదా: టొమాటో)",
        quantityPlaceholder: "పరిమాణం",
        thresholdPlaceholder: "తక్కువ స్టాక్ హెచ్చరిక వద్ద...",
        refreshData: "డేటాను రిఫ్రెష్ చేయండి",
        exportExcel: "Excel నివేదికను ఎగుమతి చేయండి",
        exportPDF: "PDF నివేదికను ఎగుమతి చేయండి",
        qrTable: "టేబుల్ QR",
        print: "ప్రింట్",
        ordersForTable: "టేబుల్ కోసం ఆర్డర్లు",
        order: "ఆర్డర్",
        total: "మొత్తం",
        payment: "చెల్లింపు",
        noItemsSold: "ఈ కాలంలో ఎటువంటి వస్తువులు అమ్ముడ కాలేదు",
        noTableData: "టేబుల్ డేటా ఏదీ అందుబాటులో లేదు",
        noDataAvailable: "ఈ కాలానికి ఎటువంటి డేటా అందుబాటులో లేదు",
        tryAgain: "మళ్ళీ ప్రయత్నించండి",
        // Common Categories
        "Dessert": "డెజర్ట్",
        "Desserts": "డెజర్ట్‌లు",
        "Main Course": "ప్రధాన వంటకం",
        "Starters": "స్టార్టర్స్",
        "Beverages": "పానీయాలు",
        "Drinks": "డ్రింక్స్",
        "Appetizers": "అపెటైజర్స్",
        // Common Menu Items
        "Grilled Chicken": "గ్రిల్డ్ చికెన్",
        "Burger": "బర్గర్",
        "Pizza": "పిజ్జా",
        "Pasta": "పాస్తా",
        "Noodles": "నూడుల్స్",
        "Salad": "సలాడ్",
        "Ice Cream": "ఐస్ క్రీమ్",
        "Coffee": "కాఫీ",
        "Tea": "టీ",
        "Juice": "జ్యూస్",
        "Water": "నీరు",
        "Soda": "సోడా",
        "Beer": "బీర్",
        "Wine": "వైన్",
        "Spirits": "స్పిరిట్స్",
        "Cocktails": "కాక్టెయిల్స్",
        "Mocktails": "మాక్టెయిల్స్",
        "Milkshake": "మిల్క్ షేక్",
        "Smoothie": "స్మూతీ",
        "Snacks": "స్నాక్స్",
        "Sides": "సైడ్ డిషెష్",
        "Extras": "ఎక్స్ట్రాలు",
        "Vegetarian": "శాకాహారం",
        "Non-Vegetarian": "మాంసాహారం",
        "Vegan": "వీగన్",
        "Gluten-Free": "గ్లూటెన్-ఫ్రీ",
        "Spicy": "కారంగా",
        "Mild": "తక్కువ కారం",
        "Hot": "వేడి",
        "Cold": "చల్లని",
        "Sweet": "తీపి",
        "Sour": "పులుపు",
        "Salty": "ఉప్పగా",
        "Bitter": "చేదు",
        "Umami": "ఉమామి",
        "Crunchy": "కరకరలాడే",
        "Soft": "మెత్తని",
        "Crispy": "క్రిస్పీ",
        "Juicy": "జుసీ",
        "Tender": "లేత",
        "Fresh": "తాజా",
        "Healthy": "ఆరోగ్యకరమైన",
        "Organic": "సేంద్రీయ",
        "Homemade": "ఇంట్లో తయారుచేసిన",
        "Traditional": "సంప్రదాయ",
        "Modern": "ఆధునిక",
        "International": "అంతర్జాతీయ",
        "Regional": "ప్రాంతీయ",
        "Seasonal": "కాలానుగుణ",
        "Daily Special": "నేటి ప్రత్యేకం",
        "Chef's Choice": "చెఫ్ ఎంపిక",
        "House Special": "హౌస్ స్పెషల్",
        "Signature Dish": "సిగ్నేచర్ డిష్",
        "Bestseller": "బెస్ట్ సెల్లర్",
        "Popular": "ప్రజాదరణ పొందిన",
        "Recommended": "సిఫార్సు చేయబడింది",
        "New Arrival": "కొత్తగా వచ్చినవి",
        "Limited Time": "పరిమిత సమయం",
        "Discounted": "డిస్కౌంట్",
        "Combo": "కాంబో",
        "Platter": "ప్లాటర్",
        "Bigger Portion": "పెద్ద పోర్షన్",
        "Small Portion": "చిన్న పోర్షన్",
        // Specific user items
        "Biryani": "బిర్యానీ",
        "Chicken Biryani": "చికెన్ బిర్యానీ",
        "Mutton Biryani": "మటన్ బిర్యానీ",
        "Akaveedu": "అకవీడు",
        "Kanigiri": "కనిగిరి",
        "Podili": "పొదిలి",
        "Ongole": "ఒంగోలు",
        "Nellore": "నెల్లూరు",
        "Vijayawada": "విజయవాడ",
        "Guntur": "గుంటూరు",
        "Hyderabad": "హైదరాబాద్",
        // Common Ingredients
        "Tomato": "టొమాటో",
        "Onion": "ఉల్లిపాయ",
        "Chicken": "చికెన్",
        "Mutton": "మటన్",
        "Oil": "నూనె",
        "Sugar": "చక్కెర",
        "Salt": "ఉప్పు",
        "Rice": "బియ్యం",
        "Egg": "గుడ్డు",
        "Milk": "పాలు",
        "Paneer": "పనీర్",
        "Fish": "చేప",
        "Prawns": "రొయ్యలు",
        "Garlic": "వెల్లుల్లి",
        "Ginger": "అల్లం",
        "Chilli": "మిర్చి",
        "Masala": "మసాలా",
        "Pulav": "పులావ్",
        "Roti": "రోటీ",
        "Chapathi": "చపాతి",
        "Curry": "కూర",
        "Fry": "ఫ్రై",
        "Snack": "స్నాక్",
        "Cool Drink": "కూల్ డ్రింక్",
        "Mineral Water": "మినరల్ వాటర్",
        "Table 1": "టేబుల్ 1",
        "Table 2": "టేబుల్ 2",
        "Table 3": "టేబుల్ 3",
        "Table 4": "టేబుల్ 4",
        "Table 5": "టేబుల్ 5",
        "Table 6": "టేబుల్ 6",
        "Table 7": "టేబుల్ 7",
        "Table 8": "టేబుల్ 8",
        "Table 9": "టేబుల్ 9",
        "Table 10": "టేబుల్ 10",
        "Table 11": "టేబుల్ 11",
        "Table 12": "టేబుల్ 12",
        "Table 13": "టేబుల్ 13",
        "Table 14": "టేబుల్ 14",
        "Table 15": "టేబుల్ 15",
        "Table 16": "టేబుల్ 16",
        "Table 17": "టేబుల్ 17",
        "Table 18": "టేబుల్ 18",
        "Table 19": "టేబుల్ 19",
        "Table 20": "టేబుల్ 20",
        "General Area": "సాధారణ ప్రాంతం",
        "Balcony": "బాల్కనీ",
        "Garden": "గార్డెన్",
        "AC Room": "ఏసీ రూమ్",
        "Non AC Room": "నాన్ ఏసీ రూమ్",
        "Reception": "రిసెప్షన్",
        "Parking": "పార్కింగ్",
        "Waiting Area": "వెయిటింగ్ ఏరియా",
        "Large": "పెద్దది",
        "Small": "చిన్నది",
        "Regular": "సాధారణ",
        "Junior": "జూనియర్",
        "Senior": "సీనియర్",
        "Family Size": "ఫ్యామిలీ సైజ్",
        "Party Size": "పార్టీ సైజ్",
        "Extra Large": "ఎక్స్ట్రా లార్జ్",
        "Extra Small": "ఎక్స్ట్రా స్మాల్",
        "Quarter": "పావు",
        "Single": "సింగిల్",
        "Quadruple": "క్వాడ్రపుల్",
        "Zero": "సున్నా",
        "One": "ఒకటి",
        "Two": "రెండు",
        "Three": "మూడు",
        "Four": "నాలుగు",
        "Five": "ఐదు",
        "Six": "ఆరు",
        "Seven": "ఏడు",
        "Eight": "ఎనిమిది",
        "Nine": "తొమ్మిది",
        "Ten": "పది",
        "Eleven": "పదకొండు",
        "Twelve": "పన్నెండు",
        "Thirteen": "పదమూడు",
        "Fourteen": "పద్నాలుగు",
        "Fifteen": "పదిహేను",
        "Sixteen": "పదహారు",
        "Seventeen": "పదిహేడు",
        "Eighteen": "పద్దెనిమిది",
        "Nineteen": "పందొమ్మిది",
        "Twenty": "ఇరవై",
        "Thirty": "ముప్పై",
        "Forty": "నలభై",
        "Fifty": "యాభై",
        "Sixty": "అరవై",
        "Seventy": "డెబ్బై",
        "Eighty": "ఎనభై",
        "Ninety": "తొంభై",
        "Hundred": "వంద",
        "Thousand": "వేయి",
        "Million": "మిలియన్",
        "Billion": "బిలియన్",
        "Trillion": "ట్రిలియన్",
        "Infinite": "అనంతం",
        "Unknown": "తెలియదు",
        "N/A": "వర్తించదు",
        "None": "ఏమీ లేదు",
        "All": "అన్నీ",

        "Many": "చాలా",
        "Few": "కొన్ని",
        "Several": "కొన్ని",
        "Half": "సగం",
        "Double": "డబుల్",
        "Triple": "ట్రిపుల్",
        "Multiple": "బహుళ",
        "Variable": "మారుతూ ఉంటుంది",
        "Fixed": "స్థిరమైన",
        "Dynamic": "డైమిక్",
        "Static": "స్టాటిక్",
        "Automatic": "ఆటోమేటిక్",
        "Manual": "మాన్యువల్",
        "Semi-Automatic": "సెమీ ఆటోమేటిక్",
        "Smart": "స్మార్ట్",
        "Intelligent": "తెలివైన",
        "Fast": "వేగంగా",
        "Slow": "నెమ్మదిగా",
        "Quick": "త్వరిత",
        "Rapid": "వేగవంతమైన",
        "Delayed": "ఆలస్యమైంది",
        "Scheduled": "షెడ్యూల్ చేయబడింది",
        "Planned": "ప్రణాళికాబద్ధమైన",
        "Real-time": "రియల్ టైమ్",
        "Live": "లైవ్",
        "Online": "ఆన్‌లైన్",
        "Offline": "ఆఫ్‌లైన్",
        "Connected": "కనెక్ట్ చేయబడింది",
        "Disconnected": "డిస్‌కనెక్ట్ చేయబడింది",
        "Syncing": "సింక్ అవుతోంది",
        "Synced": "సింక్ అయింది",
        "Error": "లోపం",
        "Success": "విజయం",
        "Failure": "వైఫల్యం",
        "Info": "సమాచారం",
        "Debug": "డీబగ్",
        "Trace": "ట్రేస్",
        "Critical": "క్లిష్టమైన",
        "Alert": "అప్రమత్తం",
        "Notification": "నోటిఫికేషన్",
        "Message": "సందేశం",
        "System": "సిస్టమ్",
        "User": "వినియోగదారు",
        "Admin": "అడ్మిన్",
        "Guest": "అతిథి",
        "Client": "క్లయింట్",
        "Server": "సర్వర్",
        "Host": "హోస్ట్",
        "Remote": "రిమోట్",
        "Local": "స్థానిక",
        "External": "బాహ్య",
        "Internal": "అంతర్గత",
        "Shared": "భాగస్వామ్యం చేయబడింది",
        "Private": "ప్రైవేట్",
        "Public": "పబ్లిక్",
        "Secret": "రహస్యం",
        "Hidden": "దాచబడింది",
        "Visible": "కనిపిస్తుంది",
        "Enabled": "ప్రారంభించబడింది",
        "Disabled": "నిలిపివేయబడింది",
        "Active": "యాక్టివ్",
        "Inactive": "నిష్క్రియం",
        "Locked": "లాక్ చేయబడింది",
        "Unlocked": "అన్‌లాక్ చేయబడింది",
        "Protected": "రక్షించబడింది",
        "Unprotected": "రక్షించబడలేదు",
        "Secure": "సురక్షితమైన",
        "Insecure": "అసురక్షితమైన",
        "Safe": "క్షేమకరమైన",
        "Unsafe": "అసురక్షితమైన",
        "Danger": "ప్రమాదం",
        "Risk": "రిస్క్",
        "Hazard": "ప్రమాదం",
        "Caution": "జాగ్రత్త",
        "Attention": "శ్రద్ధ",
        "Careful": "జాగ్రత్తగా",
        "Warning": "హెచ్చరిక",
        "Notice": "గమనిక",
        "Report": "నివేదిక",
        "Analytics": "విశ్లేషణలు",
        "Dashboard": "డాష్‌బోర్డ్",
        "Management": "నిర్వహణ",
        "Settings": "సెట్టింగ్‌లు",
        "Profile": "ప్రొఫైల్",
        "Account": "ఖాతా",
        "Logout": "లాగ్ అవుట్",
        "Login": "లాగిన్",
        "Register": "రిజిస్టర్",
        "Sign Up": "సైన్ అప్",
        "Sign In": "సైన్ ఇన్",
        "ForgotPassword": "పాస్‌వర్డ్ మరిచిపోయారా",
        "ResetPassword": "పాస్‌వర్డ్ రీసెట్ చేయండి",
        "UpdatePassword": "పాస్‌వర్డ్ అప్‌డేట్ చేయండి",
        "VerifyEmail": "ఈమెయిల్ ధృవీకరించండి",
        "ConfirmEmail": "ఈమెయిల్ నిర్ధారించండి",
        "Support": "మద్దతు",
        "Help": "సహాయం",
        "Documentation": "పత్రాలు",
        "About": "గురించి",
        "Contact": "సంప్రదించండి",
        "Terms": "నిబంధనలు",
        "Privacy": "గోప్యత",
        "Cookies": "కుకీలు",
        "Legal": "చట్టపరమైన",
        "Licenses": "లైసెన్సులు",
        "Copyright": "కాపీరైట్",
        "Trademark": "ట్రేడ్‌మార్క్",
        "Credits": "క్రెడిట్స్",
        "Version": "వెర్షన్",
        "Build": "బిల్డ్",
        "Release": "విడుదల",
        "Alpha": "ఆల్ఫా",
        "Beta": "బీటా",
        "Staging": "స్టేజింగ్",
        "Production": "ప్రొడక్షన్",
        "Development": "అభివృద్ధి",
        "Testing": "టెస్టింగ్",
        "Quality": "నాణ్యత",
        "Performance": "పనితీరు",
        "Security": "భద్రత",
        "Reliability": "విశ్వసనీయత",
        "Availability": "అందుబాటు",
        "Scalability": "స్కేలబిలిటీ",
        "Usability": "వినియోగ సాధ్యత",
        "Accessibility": "యాక్సెసిబిలిటీ",
        "Internationalization": "అంతర్జాతీయీకరణ",
        "Localization": "స్థానికీకరణ",
        "Customization": "అనుకూలీకరణ",
        "Personalization": "వ్యక్తిగతీకరణ",
        "Integration": "ఏకీకరణ",
        "Automation": "ఆటోమేషన్",
        "Optimization": "ఆప్టిమైజేషన్",
        "Transformation": "రూపాంతరం",
        "Modernization": "ఆధునీకరణ",
        "Migration": "వలస",
        "Upgrade": "అప్‌గ్రేడ్",
        "Update": "అప్‌డేట్",
        "Fix": "పరిష్కారం",
        "Patch": "ప్యాచ్",
        "Hotfix": "హాట్‌ఫిక్స్",
        "Emergency": "అత్యవసర",
        "Priority": "ప్రాధాన్యత",
        "High": "అధిక",
        "Medium": "మధ్యమ",
        "Low": "తక్కువ",
        "Lowest": "అతి తక్కువ",
        "Highest": "అత్యున్నత",
        "Normal": "సాధారణ",
        "Urgent": "అత్యవసరం",
        "Immediate": "తక్షణ",
        "ASAP": "సాధ్యమైనంత త్వరగా",
        "Today": "నేడు",
        "Tomorrow": "రేపు",
        "Yesterday": "నిన్న",
        "Now": "ఇప్పుడు",
        "Next": "తదుపరి",
        "Previous": "మునుపటి",
        "Current": "ప్రస్తుత",
        "Past": "గత",
        "Future": "భవిష్యత్",
        "Upcoming": "రాబోయే",
        "Ongoing": "కొనసాగుతున్న",
        "Completed": "పూర్తయింది",
        "Pending": "పెండింగ్",
        "Cancelled": "రద్దు చేయబడింది",
        "Failed": "విఫలమైంది",
        "Succeeded": "విజయవంతమైంది",
        "In Progress": "ప్రగతిలో ఉంది",
        "To Do": "చేయవలసినవి",
        "Doing": "చేస్తున్నాను",
        "Done": "పూర్తయింది",
        "Backlog": "బ్యాక్‌లాగ్",
        "Ready": "సిద్ధంగా ఉంది",
        "Review": "సమీక్ష",
        "Draft": "డ్రాఫ్ట్",
        "Archived": "ఆర్కైవ్ చేయబడింది",
        "Deleted": "తొలగించబడింది",
        "Restored": "పునరుద్ధరించబడింది"
        ,
        refreshAll: "మొత్తం డేటాను రిఫ్రెష్ చేయండి",
        socialMediaLinks: "సోషల్ మీడియా లింకులు",
        twitter: "ట్విట్టర్",
        youtube: "యూట్యూబ్",
        instagram: "ఇన్‌స్టాగ్రామ్",
        completed: "పూర్తయింది",
        time: "సమయం",
        allOrdersCleared: "అన్ని ఆర్డర్లు క్లియర్ చేయబడ్డాయి!",
        greatJob: "పేస్‌ను కొనసాగించడం చాలా బాగుంది.",
        startPreparation: "తయారీ ప్రారంభించండి",
        markReady: "సిద్ధంగా ఉందని గుర్తించండి",
        complete: "పూర్తి చేయండి",
        analyticsDashboard: "విశ్లేషణ డాష్‌బోర్డ్",
        analyticsDescription: "మీ రెస్టారెంట్ పనితీరుపై నిజ-సమయ అంతర్దృష్టులు",
        totalOrders: "మొత్తం ఆర్డర్లు",
        totalRevenue: "మొత్తం రాబడి",
        tablesServed: "అందించిన టేబుల్స్",
        avgOrderValue: "సగటు ఆర్డర్ విలువ",
        businessInsights: "వ్యాపార అంతర్దృష్టులు",
        topPerformer: "టాప్ పెర్ఫార్మర్",
        peakHour: "పీక్ అవర్",
        paymentPreference: "చెల్లింపు ప్రాధాన్యత",
        categoryOpportunity: "వర్గం అవకాశం",
        newCustomers: "కొత్త కస్టమర్లు",
        returningCustomers: "తిరిగి వచ్చే కస్టమర్లు",
        downloadExcel: "Excel డౌన్‌లోడ్",
        downloadPDF: "PDF డౌన్‌లోడ్",
        daily: "రోజువారీ",
        weekly: "వారపు",
        monthly: "నెలవారీ",
        yearly: "వార్షిక",
        today: "ఈరోజు",
        overview: "అవలోకనం",
        revenueByBranch: "శాఖల వారీగా ఆదాయం",
        ordersByBranch: "శాఖల వారీగా ఆర్డర్లు",
        topItemsByBranch: "శాఖల వారీగా టాప్ ఐటమ్స్",
        paymentReconciliation: "ఆర్డర్లు మరియు చెల్లింపుల పోలిక",
        last7: "గత 7 రోజులు",
        operations: "ఆపరేషన్స్",
        customers: "కస్టమర్లు",
        ingredientsManagement: "పదార్థాల నిర్వహణ",
        manageInventory: "మీ ఇన్వెంటరీ మరియు స్టాక్ స్థాయిలను నిర్వహించండి",
        backToAdmin: "అడ్మిన్ కు తిరిగి వెళ్ళు",
        addIngredient: "పదార్థం జోడించు",
        totalIngredients: "మొత్తం పదార్థాలు",
        lowStockItems: "తక్కువ స్టాక్ అంశాలు",
        searchIngredients: "పదార్థాలను శోధించండి...",
        quantity: "పరిమాణం",
        unit: "యూనిట్",
        threshold: "థ్రెషోల్డ్",
        lowStock: "తక్కువ స్టాక్",
        inStock: "స్టాక్ లో ఉంది",
        editIngredient: "పదార్థం సవరించు",
        deleteIngredientConfirm: "మీరు ఖచ్చితంగా ఈ పదార్థాన్ని తొలగించాలనుకుంటున్నారా?",
        saveIngredient: "పదార్థం సేవ్ చేయి",
        lowStockThreshold: "తక్కువ స్టాక్ థ్రెషోల్డ్",
        staffPortal: "సిబ్బంది పోర్టల్",
        enterPin: "పిన్ ఎంటర్ చేయండి",
        verifying: "ధృవీకరిస్తోంది...",
        staffDashboard: "సిబ్బంది డాష్‌బోర్డ్",
        attendance: "హాజరు",
        leaves: "సెలవులు",
        punchIn: "పంచ్ ఇన్",
        punchOut: "పంచ్ అవుట్",
        attendanceHistory: "హాజరు చరిత్ర",
        allStaffAttendance: "అందరి హాజరు",
        last7Days: "గత 7 రోజులు",
        thisMonth: "ఈ నెల",
        customRange: "కస్టమ్ రేంజ్",
        staffName: "సిబ్బంది పేరు",
        checkIn: "ఇన్",
        checkOut: "అవుట్",
        hours: "గంటలు",
        newLeaveRequest: "కొత్త సెలవు అభ్యర్థన",
        fromDate: "నుండి",
        toDate: "వరకు",
        reason: "కారణం",
        submitRequest: "అభ్యర్థన సమర్పించు",
        allLeaveRequests: "అన్ని సెలవు అభ్యర్థనలు",
        myLeaveHistory: "నా సెలవు చరిత్ర",
        noAttendanceRecords: "హాజరు రికార్డులు కనుగొనబడలేదు",
        noLeaveRequests: "సెలవు అభ్యర్థనలు కనుగొనబడలేదు",
        approve: "ఆమోదించు",
        reject: "తిరస్కరించు",
        firstIn: "మొదటి ఎంట్రీ",
        lastOut: "చివరి అవుట్",
        dailyRate: "రోజువారీ రేటు",
        download: "డౌన్‌లోడ్",
        refresh: "రిఫ్రెష్",
        manageAttendanceTitle: "హాజరు మరియు సెలవులను నిర్వహించండి",
        attendanceManagement: "హాజరు నిర్వహణ",
        manageAttendanceDesc: "స్టాఫ్ మేనేజ్‌మెంట్ డ్యాష్‌బోర్డ్‌లో వివరణాత్మక హాజరు లాగ్‌లు, పని గంటలు మరియు సెలవు అభ్యర్థనలను చూడండి.",
        openAttendanceManager: "అటెండెన్స్ మేనేజర్ తెరవండి",
        selectBranch: "శాఖను ఎంచుకోండి",
        viewingDataFor: "కోసం డేటాను చూస్తున్నారు",
        filteredByBranchInfo: "అన్ని మెనూ ఐటమ్స్, ఆర్డర్లు, పదార్థాలు మరియు అనలిటిక్స్ ఈ శాఖకు ఫిల్టర్ చేయబడ్డాయి.",
        revenueTrend: "ఆదాయ పోకడలు",
        ordersOverview: "ఆర్డర్ల అవలోకనం",
        revenueBreakdown: "ఆదాయ విభజన",
        revenueByCategory: "వర్గం వారీగా ఆదాయం",
        itemPerformance: "ఐటమ్ పనితీరు",
        topSellingItems: "టాప్ సెల్లింగ్ ఐటమ్స్",
        categoryPerformance: "వర్గం పనితీరు",
        topItemsDetails: "టాప్ ఐటమ్స్ వివరాలు",
        customerAnalysis: "కస్టమర్ విశ్లేషణ",
        customerRetention: "కస్టమర్ నిలుపుదల",
        customerMetrics: "కస్టమర్ మెట్రిక్స్",
        retentionRate: "నిలుపుదల రేటు",
        operationsAnalysis: "ఆపరేషన్స్ విశ్లేషణ",
        hourlyOrderDistribution: "గంటల వారీగా ఆర్డర్ల పంపిణీ",
        tablePerformance: "టేబుల్ పనితీరు",
        tablePerformanceDetails: "టేబుల్ పనితీరు వివరం",
        from: "నుండి",
        to: "వరకు",
        tryAdjustingFilters: "తేదీ ఫిల్టర్‌లను సర్దుబాటు చేయడానికి ప్రయత్నించండి",
        noAttendanceToExport: "ఎగుమతి చేయడానికి ఎటువంటి హాజరు రికార్డులు లేవు.",
        totalWorkingHours: "మొత్తం పని గంటలు",
        workingHours: "పని గంటలు",
        workSessionDetails: "పని సెషన్ వివరాలు",
        duration: "వ్యవధి",
        closeDetails: "వివరాలను మూసివేయి",
        allBranchesCombined: "అన్ని శాఖలు",
        branchMgmt: "బ్రాంచ్ మేనేజ్‌మెంట్",
        branchMgmtSub: "మీ స్థానాలు మరియు శాఖలను నిర్వహించండి",
        addNewBranch: "కొత్త బ్రాంచ్‌ని జోడించండి",
        loadingBranches: "బ్రాంచ్‌లు లోడ్ అవుతున్నాయి...",
        noBranchesYet: "ఇంకా బ్రాంచ్‌లు లేవు",
        noBranchesDesc: "మీ మొదటి బ్రాంచ్ లేదా స్థానాన్ని జోడించడం ద్వారా మీ వ్యాపారాన్ని విస్తరించండి.",
        createFirstBranch: "మొదటి బ్రాంచ్‌ని సృష్టించండి",
        branchActiveDesc: "బ్రాంచ్ యాక్టివ్‌గా ఉంది మరియు ఆర్డర్‌లను అంగీకరిస్తోంది",
        failedLoadBranches: "బ్రాంచ్‌లను లోడ్ చేయడం విఫలమైంది",
        branchSavedSuccess: "బ్రాంచ్ విజయవంతంగా సేవ్ చేయబడింది",
        failedSaveBranch: "బ్రాంచ్‌ను సేవ్ చేయడం విఫలమైంది",
        errorSavingBranch: "బ్రాంచ్‌ను సేవ్ చేయడంలో లోపం",
        deleteBranchConfirm: "మీరు ఖచ్చితంగా ఈ బ్రాంచ్‌ను తొలగించాలనుకుంటున్నారా? ఇది అనుబంధిత డేటాపై కూడా ప్రభావం చూపుతుంది.",
        branchDeletedSuccess: "బ్రాంచ్ విజయవంతంగా తొలగించబడింది",
        failedDeleteBranch: "బ్రాంచ్‌ను తొలగించడం విఫలమైంది",
        errorDeletingBranch: "బ్రాంచ్‌ను తొలగించడంలో లోపం",
        createBranch: "బ్రాంచ్‌ని సృష్టించండి",
        updateBranch: "బ్రాంచ్‌ని అప్‌డేట్ చేయండి",
        confirmDeletion: "తొలగింపును ధృవీకరించండి",
        branchName: "బ్రాంచ్ పేరు",
        branchNamePlaceholder: "ఉదా: డౌన్‌టౌన్ బ్రాంచ్, ఎయిర్‌పోర్ట్ లొకేషన్",
        managerName: "మేనేజర్ పేరు",
        managerNamePlaceholder: "బ్రాంచ్ మేనేజర్ పేరు",
        addressPlaceholder: "బ్రాంచ్ ఉన్న ప్రదేశం యొక్క పూర్తి చిరునామా",
        unavailable: "అందుబాటులో లేదు",
        leaveEmptyForNoBranch: "బ్రాంచ్ లేదు కోసం ఖాళీగా వదిలేయండి",
        allowedBranches: "అనుమతించబడిన బ్రాంచ్‌లు",
        all: "అన్నీ",
        branch: "బ్రాంచ్",
        nonACDefault: "నాన్ ఏసీ (డిఫాల్ట్)",
        unlock: "అన్‌లాక్",
        lock: "లాక్",
        refreshing: "మొత్తం డేటాను రిఫ్రెష్ చేస్తోంది...",
        refreshed: "మొత్తం డేటా రిఫ్రెష్ చేయబడింది!",
        refreshFailed: "రిఫ్రెష్ విఫలమైంది",
        statusUpdated: "స్టేటస్ అప్‌డేట్ చేయబడింది!",
        updateFailed: "అప్‌డేట్ విఫలమైంది",
        failedLoadMenu: "మెనూ లోడ్ చేయడంలో విఫలమైంది",
        categoryAdded: "కేటగిరీ జోడించబడింది!",
        updatedSuccessfully: "విజయవంతంగా అప్‌డేట్ చేయబడింది",
        enabledForAll: "అందరికీ ప్రారంభించబడింది",
        disabledForAll: "అందరికీ నిలిపివేయబడింది",
        bulkUpdateFailed: "బల్క్ అప్‌డేట్ విఫలమైంది",
        enterGroupName: "గ్రూప్ పేరును నమోదు చేయండి",
        deleted: "తొలగించబడింది",
        error: "లోపం",
        printedPopupBlocked: "ప్రింట్ చేయబడింది (పాపప్ బ్లాక్ చేయబడింది)",
        qrSentToPrinter: "QR ప్రింటర్‌కు పంపబడింది",
        printFailed: "ప్రింట్ విఫలమైంది",
        uploadingLogo: "లోగో అప్‌లోడ్ అవుతోంది...",
        logoUploaded: "లోగో అప్‌లోడ్ చేయబడింది!",
        uploadingBanner: "బ్యానర్ అప్‌లోడ్ అవుతోంది...",
        bannerUploaded: "బ్యానర్ అప్‌లోడ్ చేయబడింది!",
        generationFailed: "జనరేషన్ విఫలమైంది",
        uploadingQR: "QR అప్‌లోడ్ అవుతోంది...",
        qrAdded: "QR కోడ్ జోడించబడింది!",
        deleteStaffConfirm: "మీరు నిజంగా ఈ సిబ్బంది సభ్యుడిని తొలగించాలనుకుంటున్నారా?",
        deleteGroupConfirm: "గ్రూప్‌ను తొలగించాలా? ఈ గ్రూప్‌లోని టేబుల్స్ Non AC కి సెట్ చేయబడతాయి.",
        deleteRoleConfirm: "మీరు నిజంగా ఈ పాత్రను తొలగించాలనుకుంటున్నారా?",
        deleteUserConfirm: "మీరు నిజంగా ఈ వినియోగదారుని తొలగించాలనుకుంటున్నారా?",
        deleteCategoryConfirm: "\"{name}\" కేటగిరీని తొలగించాలా? అందులోని ఐటమ్స్ తమ కేటగిరీని కోల్పోతాయి.",
        active: "యాక్టివ్",
        inactive: "ఇన్యాక్టివ్",
        upiIdDesc: "UPI ID ని నమోదు చేయడం వల్ల బిల్లుపై GPay, PhonePe మరియు Paytm బటన్లు ప్రారంభించబడతాయి.",
        optionalDiscountDesc: "ఐచ్ఛికం: డిస్కౌంట్ శాతాన్ని జోడించండి (ఉదా. 10% డిస్కౌంట్ కోసం 10)",
        paymentSettings: "చెల్లింపు సెట్టింగ్‌లు",
        festivalOfferDesc: "ఈ డిస్కౌంట్ మొత్తం బిల్లుకు వర్తించబడుతుంది",
        yes: "అవును",
        no: "కాదు",
        confirmAction: "చర్యను ధృవీకరించండి",
        failedResetEmail: "రీసెట్ ఇమెయిల్ పంపడంలో విఫలమైంది",
        saving: "సేవ్ చేస్తోంది...",
        qr: "QR",
    },

};


export const LanguageProvider = ({ children }) => {
    // Load initial language from localStorage or default to 'en'
    const [language, setLanguage] = useState(() => {
        return localStorage.getItem('dineflow_language') || 'en';
    });

    // Listen for storage changes to sync across tabs
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'dineflow_language' && e.newValue) {
                setLanguage(e.newValue);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const t = (key, params = {}) => {
        if (!key) return '';

        // Exact match
        let translation = translations[language]?.[key] || translations['en']?.[key];

        // Case-insensitive fallback
        if (!translation && typeof key === 'string') {
            const normalizedKey = key.trim().toLowerCase();
            const langKeys = Object.keys(translations[language] || {});
            const foundKey = langKeys.find(k => k.toLowerCase() === normalizedKey);

            if (foundKey) {
                translation = translations[language][foundKey];
            } else {
                const enKeys = Object.keys(translations['en'] || {});
                const foundEnKey = enKeys.find(k => k.toLowerCase() === normalizedKey);
                if (foundEnKey) {
                    translation = translations['en'][foundEnKey];
                }
            }
        }

        // Final fallback to key
        if (!translation) translation = key;

        if (typeof translation === 'string' && params) {
            Object.keys(params).forEach(p => {
                translation = translation.replace(`{${p}}`, params[p]);
            });
        }
        return translation;
    };

    const changeLanguage = (lang) => {
        setLanguage(lang);
        localStorage.setItem('dineflow_language', lang);
    };

    return (
        <LanguageContext.Provider value={{ language, changeLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageProvider;
